import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Component, Input, ViewChild, inject, EventEmitter, Output, NgModule, ViewChildren } from '@angular/core';
import * as i2$1 from '@engineering11/ui-lib/e11-datatable';
import { DataType, E11DataTableModule } from '@engineering11/ui-lib/e11-datatable';
import * as i2 from '@engineering11/web-api-error';
import { toE11Error, e11LoggerRef, e11Log, E11ErrorHandlerService, E11Logger, e11Error } from '@engineering11/web-api-error';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { BehaviorSubject, startWith, switchMap, of, filter, map, catchError, Subject, tap, finalize, combineLatest, takeUntil } from 'rxjs';
import { isNotNil, pick, uuid } from '@engineering11/utility';
import { RestApiClient } from '@engineering11/web-api-rest';
import { toSelectOptions } from '@engineering11/ui-lib/e11-select';
import * as i3 from '@engineering11/ui-lib/e11-notifications';
import { E11NotificationsService } from '@engineering11/ui-lib/e11-notifications';
import * as i4 from 'ngx-stripe';
import { StripeCardNumberComponent, StripeService, injectStripe, NgxStripeModule } from 'ngx-stripe';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i5 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as i3$1 from '@engineering11/ui-lib/e11-confirmation';
import { E11ConfirmationModule } from '@engineering11/ui-lib/e11-confirmation';
import * as i5$1 from '@engineering11/ui-lib/e11-overflow';
import { E11OverflowModule } from '@engineering11/ui-lib/e11-overflow';
import * as i1$1 from '@ngrx/component';
import { LetDirective, PushPipe } from '@ngrx/component';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import * as i2$2 from '@engineering11/ui-lib/e11-ui-utils';
import * as i3$2 from '@engineering11/ui-lib/e11-modal';
import { E11ModalModule } from '@engineering11/ui-lib/e11-modal';
import * as i2$3 from '@engineering11/ui-lib/e11-button';
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button';
import * as i6 from '@engineering11/web-utilities';
import { WebUtilityModule } from '@engineering11/web-utilities';
import { SubcollectionRepository, FirestoreQueryBuilder, CollectionRepository } from '@engineering11/web-api-firebase';
import * as i4$1 from '@engineering11/ui-lib/e11-tooltip';
import { E11TooltipModule } from '@engineering11/ui-lib/e11-tooltip';
import * as i4$2 from '@engineering11/ui-lib/e11-form-builder';
import { e11FormFor, e11FormLayoutFullWidth, e11FieldInputSelect, e11FormLayoutInlineGrid, e11FieldInputCheckBox, e11FieldInputText, e11FieldInputNumber, e11FieldInputAutocomplete, E11DynamicFormModule } from '@engineering11/ui-lib/e11-form-builder';
import * as i2$4 from '@engineering11/ui-lib/e11-badge';
import { E11BadgeModule } from '@engineering11/ui-lib/e11-badge';
import * as i4$3 from '@engineering11/ui-lib/e11-empty-state';
import { E11EmptyStateModule } from '@engineering11/ui-lib/e11-empty-state';
import { E11InputErrorsModule } from '@engineering11/ui-lib/e11-input-errors';
import { isEmpty, xor } from 'lodash';
import * as i3$3 from '@engineering11/ui-lib/e11-input-checkbox';
import { E11InputCheckboxModule } from '@engineering11/ui-lib/e11-input-checkbox';
import { map as map$1, tap as tap$1 } from 'rxjs/operators';
import { E11DividerModule } from '@engineering11/ui-lib/e11-divider';

const billingTableOptions = {
    keyField: 'id',
    selectable: false,
    enableDynamicFields: false,
    enableFilter: false,
    enableSorting: false,
    paginationSettings: {
        enabled: true,
        serverSide: false,
        pageLinks: 5,
        rowCount: 10,
        rowsPerPageOptions: [25, 50, 100],
    },
    columns: [
        {
            key: 'date',
            title: 'Date',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
        {
            key: 'description',
            title: 'Description',
            type: DataType.CharField,
            visible: true,
            optional: false,
        },
        {
            key: 'amount',
            title: 'Amount',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
        {
            key: 'invoiceStatus',
            title: 'Status',
            type: DataType.CharField,
            visible: true,
            optional: false,
        },
        {
            key: 'invoiceUrl',
            title: 'Invoice',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
    ],
};

const SDK_NAME = 'product';
const COLLECTIONS = Object.freeze({
    PRODUCT: `${SDK_NAME}_product/`,
    PRICE: `/${SDK_NAME}_product_price/`,
    SUBSCRIPTION: `/${SDK_NAME}_subscription/`,
});
const COMMON_CURRENCY_CODES = [
    'USD', // United States Dollar
    'EUR', // Euro
    'JPY', // Japanese Yen
    'GBP', // British Pound Sterling
    'AUD', // Australian Dollar
    'CAD', // Canadian Dollar
    'CHF', // Swiss Franc
    'CNY', // Chinese Yuan
    'INR', // Indian Rupee
    'SGD', // Singapore Dollar
    'MXN', // Mexican Peso
    'BRL', // Brazilian Real
    'ZAR', // South African Rand
    'AED', // United Arab Emirates Dirham
    'HKD', // Hong Kong Dollar
    // Add more currencies as needed
];
const DEFAULT_CURRENCY_CODE = COMMON_CURRENCY_CODES[0];
const PAYMENT_ERRORS = Object.freeze({
    CANT_CONFIRM_PAYMENT: 'payment/cant-confirm-payment',
    CANT_CREATE_TOKEN: 'payment/cant-create-payment-token',
    UNKNOWN: 'payment/unknown',
});
/**
 * Stipe requires the amount to be in the lowest available currency unit.
 * Thats why we will multiply the amount by 100. For example, if the amount is 10.00 USD, we will store it as 1000.
 * And when we will display it, we will divide it by 100.
 * reference: https://stripe.com/docs/currencies#zero-decimal
 */
const AMOUNT_MULTIPLIER = 100;
const DEFAULT_DIVIDE_BY = 100;
var PaymentPermissions;
(function (PaymentPermissions) {
    PaymentPermissions["ViewBillingDetails"] = "product/view-billing-details";
    PaymentPermissions["EditBillingDetails"] = "product/edit-billing-details";
})(PaymentPermissions || (PaymentPermissions = {}));
const SKU_REGEX = /[A-Z0-9][A-Z0-9_-]{4,}[A-Z0-9]/;

const transformPaymentIntentResponseToBillingHistory = (paymentIntentResponse) => {
    return {
        id: paymentIntentResponse.id,
        amount: paymentIntentResponse.amount / DEFAULT_DIVIDE_BY,
        currency: paymentIntentResponse.currency.toUpperCase(),
        date: new Date(paymentIntentResponse.created * 1000),
        billingStatus: paymentIntentResponse.status,
        invoiceUrl: paymentIntentResponse.invoice?.invoice_pdf,
        invoiceStatus: paymentIntentResponse.invoice?.status,
        description: paymentIntentResponse.description,
    };
};
const transformStripeAddressToBillingAddress = (stripeAddress) => ({
    city: stripeAddress.address_city,
    country: stripeAddress.address_country,
    line1: stripeAddress.address_line1,
    line2: stripeAddress.address_line2,
    postal_code: stripeAddress.address_zip,
    state: stripeAddress.address_state,
});

var QuantityTransformRound;
(function (QuantityTransformRound) {
    QuantityTransformRound["Up"] = "up";
    QuantityTransformRound["Down"] = "down";
})(QuantityTransformRound || (QuantityTransformRound = {}));
var RecurringPriceInterval;
(function (RecurringPriceInterval) {
    RecurringPriceInterval["Day"] = "day";
    RecurringPriceInterval["Week"] = "week";
    RecurringPriceInterval["Month"] = "month";
    RecurringPriceInterval["Year"] = "year";
})(RecurringPriceInterval || (RecurringPriceInterval = {}));
const selectableRecurringPriceIntervals = toSelectOptions(Object.values(RecurringPriceInterval));
var PaymentIntegration;
(function (PaymentIntegration) {
    PaymentIntegration["CustomInvoicing"] = "CustomInvoicing";
    PaymentIntegration["Stripe"] = "Stripe";
})(PaymentIntegration || (PaymentIntegration = {}));
const selectablePaymentIntegration = toSelectOptions(Object.values(PaymentIntegration));

var ProductSubscriptionStatus;
(function (ProductSubscriptionStatus) {
    ProductSubscriptionStatus["Active"] = "Active";
    ProductSubscriptionStatus["Cancelled"] = "Cancelled";
    ProductSubscriptionStatus["Pending"] = "Pending";
})(ProductSubscriptionStatus || (ProductSubscriptionStatus = {}));
const productSubscriptionStatusOptions = Object.keys(ProductSubscriptionStatus).map((status) => ({
    name: status,
    value: ProductSubscriptionStatus[status],
}));

/**
 * Injection token for the stripe publishable key
 * If undefined, then we are using the payment sdk with out stripe enabled
 */
const stripePublishableKey = new InjectionToken('stripePublishableKey');
class BasePaymentConfigurationProvider {
    constructor() {
        this._customerKeySubject$ = new BehaviorSubject(undefined);
    }
    updateClientKey(clientKey) {
        this._customerKeySubject$.next(clientKey);
    }
    get customerKey$() {
        return this._customerKeySubject$.pipe(startWith(undefined), switchMap((key) => (key !== undefined ? of(key) : this.config.customerKey$)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BasePaymentConfigurationProvider, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BasePaymentConfigurationProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BasePaymentConfigurationProvider, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const PAYMENT_CUSTOMERS_PATH = 'product/payment-customers';
class PaymentService {
    constructor(libConfig, logger) {
        this.libConfig = libConfig;
        this.logger = logger;
        this.api = new RestApiClient({ token: libConfig.config.token$.pipe(filter(isNotNil)), baseUrl: `${libConfig.config.paymentsBaseURL}` });
    }
    createPaymentIntent(paymentIntent) {
        return this.api
            .post('product/payment-intents', paymentIntent)
            .pipe(map((res) => res.data));
    }
    getPaymentCustomer() {
        return this.api.get(PAYMENT_CUSTOMERS_PATH).pipe(map((res) => res.data));
    }
    getPaymentCustomerBillingHistory() {
        return this.api
            .get(`${PAYMENT_CUSTOMERS_PATH}/billing-history`)
            .pipe(map((res) => res.data.data.map((billingHistory) => transformPaymentIntentResponseToBillingHistory(billingHistory))));
    }
    updateCustomerAddress(customerDetail) {
        return this.api.put(`${PAYMENT_CUSTOMERS_PATH}/payment-methods`, customerDetail);
    }
    createCustomerPaymentMethods(token) {
        return this.api.post(`${PAYMENT_CUSTOMERS_PATH}/payment-methods`, { card: { token } });
    }
    removePaymentMethod(paymentMethodId) {
        return this.api.delete(`${PAYMENT_CUSTOMERS_PATH}/payment-methods/${paymentMethodId}`);
    }
    setDefaultPaymentMethod(paymentMethodId) {
        return this.api.post(`${PAYMENT_CUSTOMERS_PATH}/payment-methods/${paymentMethodId}/set-default`, {});
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PaymentService, deps: [{ token: BasePaymentConfigurationProvider }, { token: i2.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PaymentService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PaymentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: BasePaymentConfigurationProvider }, { type: i2.E11Logger }] });

// ! SDK-internal - do not export from public-api - this exposes stripe interfaces
class E11StripeService {
    constructor(stripeService) {
        this.stripeService = stripeService;
    }
    // IMPORTANT WARNING: The Stripe service must be made public. We require a single instance of the Stripe service and need to pass it into ngxStripeCardGroup.
    stripeServiceInstance() {
        return this.stripeService;
    }
    confirmPayment(options) {
        return this.stripeService.confirmPayment(options).pipe(map((res) => {
            if (res.error) {
                throw toE11Error(res.error, {
                    type: `payments/${res.error.type}`,
                    title: 'We can not confirm your payment',
                    message: res.error.message,
                });
            }
            return res;
        }), catchError((err, caught) => {
            throw toE11Error(err, {
                type: PAYMENT_ERRORS.CANT_CONFIRM_PAYMENT,
                title: 'We cannot confirm your payment, please try again later',
                message: 'we cant confirm your payment, please try again later',
                alertUser: true,
            });
        }));
    }
    confirmCardPayment(clientSecret, data) {
        return this.stripeService.confirmCardPayment(clientSecret, data).pipe(map((res) => {
            if (res.error) {
                throw toE11Error(res.error, {
                    type: `payments/${res.error.type}`,
                    title: 'We can not confirm your payment',
                    message: res.error.message,
                });
            }
            return res;
        }), catchError((err, caught) => {
            throw toE11Error(err, {
                type: PAYMENT_ERRORS.CANT_CONFIRM_PAYMENT,
                title: 'We cannot confirm your payment, please try again later',
                message: 'we cant confirm your payment, please try again later',
                alertUser: true,
            });
        }));
    }
    // createToken(tokenType: StripeCardElement | StripeCardNumberElement, data?: CreateTokenCardData): Observable<TokenResult>
    createToken(tokenType, data) {
        return this.stripeService.createToken(tokenType, data).pipe(map((res) => {
            // Stripe handles showing error to the user - so we don't show it
            if (res.error)
                throw toE11Error(res.error, { message: 'Payment token not found', title: '', type: PAYMENT_ERRORS.CANT_CREATE_TOKEN });
            return res.token;
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: E11StripeService, deps: [{ token: i4.StripeService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: E11StripeService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: E11StripeService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i4.StripeService }] });

const initialState$2 = {
    billingHistories: [],
    billingHistoryLoaded: false,
    paymentMethods: [],
    customerLoaded: false,
    paymentMethodsLoaded: false,
    paymentMethodsLoading: false,
    paymentIntentLoading: false,
    billingHistoryLoading: false,
    verifyCardAndAddPaymentMethodLoading: false,
};
class PaymentStore extends ComponentStore {
    constructor(paymentService, errorHandler, notificationService, e11StripeService, libConfig) {
        super(initialState$2);
        this.paymentService = paymentService;
        this.errorHandler = errorHandler;
        this.notificationService = notificationService;
        this.e11StripeService = e11StripeService;
        this.libConfig = libConfig;
        this.logger = e11LoggerRef();
        this.customerBillingEmail$ = this.libConfig.config.customerBillingEmail$.pipe(filter(isNotNil));
        this.confirmPaymentFinalStatus$ = new Subject();
        this.addPaymentMethodStatus$ = new Subject();
        /**
         * Selectors
         */
        this.getState = this.select((s) => s);
        this.getBillingHistories$ = this.select((s) => s.billingHistories);
        this.getBillingHistoryLoaded$ = this.select((s) => s.billingHistoryLoaded);
        this.getBillingHistoryLoading$ = this.select((s) => s.billingHistoryLoading);
        this.getVerifyCardAndAddPaymentMethodLoading$ = this.select((s) => s.verifyCardAndAddPaymentMethodLoading);
        this.getPaymentMethods$ = this.select((s) => s.paymentMethods);
        this.getPaymentMethodsLoaded$ = this.select((s) => s.paymentMethodsLoaded);
        this.getPaymentMethodsLoading$ = this.select((s) => s.paymentMethodsLoading);
        this.getPaymentIntentLoading$ = this.select((s) => s.paymentIntentLoading);
        this.getPaymentCustomer$ = this.select((s) => s.paymentCustomer);
        this.getCustomerLoaded$ = this.select((s) => s.customerLoaded);
        this.getCustomerDefaultPaymentMethod$ = this.select((s) => s.paymentMethods.find((paymentMethod) => paymentMethod.isDefault));
        /**
         * Reducers
         */
        this.onBillingHistories = this.updater((state, billingHistories) => ({
            ...state,
            billingHistories,
            billingHistoryLoaded: true,
        }));
        this.onPaymentCustomer = this.updater((state, { customer, paymentMethods }) => ({
            ...state,
            customer,
            paymentMethods,
            paymentMethodsLoaded: true,
            customerLoaded: true,
        }));
        this.onPaymentIntentLoading = this.updater((state, paymentIntentLoading) => ({ ...state, paymentIntentLoading }));
        this.onPaymentMethodsLoading = this.updater((state, paymentMethodsLoading) => ({ ...state, paymentMethodsLoading }));
        this.onBillingHistoryLoading = this.updater((state, billingHistoryLoading) => ({ ...state, billingHistoryLoading }));
        this.onVerifyCardAndAddPaymentMethodLoading = this.updater((state, verifyCardAndAddPaymentMethodLoading) => ({
            ...state,
            verifyCardAndAddPaymentMethodLoading,
        }));
        this.onPaymentMethodRemoved = this.updater((state, paymentMethodId) => ({
            ...state,
            paymentMethods: state.paymentMethods.filter((paymentMethod) => paymentMethod.id !== paymentMethodId),
        }));
        this.onPaymentMethodDefault = this.updater((state, paymentMethodId) => ({
            ...state,
            paymentMethods: state.paymentMethods.map((paymentMethod) => ({
                ...paymentMethod,
                isDefault: paymentMethod.id === paymentMethodId,
            })),
        }));
        /**
         * Effects
         */
        this.getBillingHistory = this.effect((_$) => {
            return _$.pipe(tap(() => this.onBillingHistoryLoading(true)), switchMap(() => {
                return this.paymentService.getPaymentCustomerBillingHistory().pipe(e11Log({ key: 'getBillingHistory' }), tapResponse((billingHistoriesResponse) => this.onBillingHistories(billingHistoriesResponse), (e) => this.errorHandler.handleError(e)), finalize(() => this.onBillingHistoryLoading(false)));
            }));
        });
        this.getPaymentCustomer = this.effect((_$) => {
            return _$.pipe(tap(() => this.onPaymentMethodsLoading(true)), switchMap(() => {
                return this.paymentService.getPaymentCustomer().pipe(tapResponse((paymentCustomerResponse) => this.onPaymentCustomer(paymentCustomerResponse), (e) => this.errorHandler.handleError(e)), finalize(() => this.onPaymentMethodsLoading(false)));
            }));
        });
        this.removePaymentMethod = this.effect((paymentMethodId$) => {
            return paymentMethodId$.pipe(switchMap((paymentMethodId) => {
                return this.paymentService.removePaymentMethod(paymentMethodId).pipe(tapResponse(() => {
                    this.notificationService.popNotificationMessage(NOTIFICATION_MESSAGES$1.paymentMethodRemoved());
                    return this.onPaymentMethodDefault(paymentMethodId);
                }, (e) => this.errorHandler.handleError(e)));
            }));
        });
        this.setDefaultPaymentMethod = this.effect((paymentMethodId$) => {
            return paymentMethodId$.pipe(switchMap((paymentMethodId) => {
                return this.paymentService.setDefaultPaymentMethod(paymentMethodId).pipe(tapResponse(() => {
                    this.notificationService.popNotificationMessage(NOTIFICATION_MESSAGES$1.setDefaultPaymentMethod());
                    return this.onPaymentMethodDefault(paymentMethodId);
                }, (e) => this.errorHandler.handleError(e)));
            }));
        });
        this.createPaymentIntent = this.effect((intentObject$) => {
            return combineLatest([intentObject$, this.customerBillingEmail$]).pipe(tap(() => this.onPaymentIntentLoading(true)), switchMap(([{ intentObject, confirmPaymentData }, receiptEmail]) => {
                return this.paymentService.createPaymentIntent({ ...intentObject, receiptEmail }).pipe(tapResponse((response) => (confirmPaymentData ? this.confirmCardPayment({ ...response, confirmPaymentData }) : this.confirmPayment(response)), (e) => {
                    this.onPaymentIntentLoading(false);
                    return this.errorHandler.handleError(e);
                }));
            }));
        });
        this.confirmPayment = this.effect((paymentIntent$) => {
            return paymentIntent$.pipe(switchMap(({ clientSecret }) => {
                return this.e11StripeService.confirmPayment({ clientSecret, redirect: 'if_required' }).pipe(tapResponse((res) => {
                    this.onPaymentIntentLoading(false);
                    this.confirmPaymentFinalStatus$.next(true);
                }, (e) => {
                    this.onPaymentIntentLoading(false);
                    return this.errorHandler.handleError(e);
                }));
            }));
        });
        this.confirmCardPayment = this.effect((paymentIntent$) => {
            return paymentIntent$.pipe(switchMap(({ clientSecret, confirmPaymentData }) => {
                return this.e11StripeService.confirmCardPayment(clientSecret, confirmPaymentData).pipe(tapResponse((res) => {
                    this.onPaymentIntentLoading(false);
                    this.confirmPaymentFinalStatus$.next(true);
                }, (e) => {
                    this.onPaymentIntentLoading(false);
                    return this.errorHandler.handleError(e);
                }));
            }));
        });
        this.verifyCardAndAddPaymentMethod = this.effect((cardForm$) => {
            return cardForm$.pipe(switchMap(({ tokenType, data, paymentIntent }) => {
                this.onVerifyCardAndAddPaymentMethodLoading(true);
                return this.e11StripeService.createToken(tokenType, data).pipe(tapResponse((token) => {
                    return this.addPaymentMethod(token.id);
                }, (e) => {
                    this.addPaymentMethodStatus$.next(true);
                    return this.errorHandler.handleError(e);
                }));
            }));
        });
        this.addPaymentMethod = this.effect((paymentMethodToken$) => {
            return paymentMethodToken$.pipe(switchMap((paymentMethodToken) => {
                return this.paymentService.createCustomerPaymentMethods(paymentMethodToken).pipe(tapResponse(() => {
                    this.notificationService.popNotificationMessage(NOTIFICATION_MESSAGES$1.paymentMethodAdded());
                    this.addPaymentMethodStatus$.next(true);
                    return this.getPaymentCustomer();
                }, (e) => this.errorHandler.handleError(e), () => this.onVerifyCardAndAddPaymentMethodLoading(false)));
            }));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PaymentStore, deps: [{ token: PaymentService }, { token: i2.E11ErrorHandlerService }, { token: i3.E11NotificationsService }, { token: E11StripeService }, { token: BasePaymentConfigurationProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PaymentStore, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PaymentStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: PaymentService }, { type: i2.E11ErrorHandlerService }, { type: i3.E11NotificationsService }, { type: E11StripeService }, { type: BasePaymentConfigurationProvider }] });
const NOTIFICATION_MESSAGES$1 = {
    setDefaultPaymentMethod: () => ({
        title: 'Default Payment Method',
        message: 'Payment method has been set as default successfully',
        type: 'success',
    }),
    paymentMethodAdded: () => ({
        title: 'Payment Method Added',
        message: 'Payment method has been added successfully',
        type: 'success',
    }),
    paymentMethodRemoved: () => ({
        title: 'Payment Method Removed',
        message: 'Payment method has been removed successfully',
        type: 'success',
    }),
};

class SdkBillingHistoryComponent {
    constructor(paymentStore) {
        this.paymentStore = paymentStore;
        this.initialGridOptions = billingTableOptions;
        this.billingHistories$ = this.paymentStore.getBillingHistories$;
        this.billingHistoryLoading$ = this.paymentStore.getBillingHistoryLoading$;
        this.billingHistoryLoaded$ = this.paymentStore.getBillingHistoryLoaded$;
        this.paymentStore.getBillingHistory();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkBillingHistoryComponent, deps: [{ token: PaymentStore }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkBillingHistoryComponent, selector: "sdk-payment-billing-history", ngImport: i0, template: `
    <e11-datatable
      [initialGridOptions]="initialGridOptions"
      [loading]="(billingHistoryLoading$ | async) || false"
      [records]="(billingHistories$ | async) || []"
    >
      <ng-template e11DatatableTemplate="date" let-row>
        {{ row.date | date: 'MMMM dd, yyyy' }}
      </ng-template>
      <ng-template e11DatatableTemplate="amount" let-row>
        {{ row.amount | currency: row.currency }}
      </ng-template>
      <ng-template e11DatatableTemplate="invoiceUrl" let-row>
        <a [href]="row.invoiceUrl" class=" e11-text-skin-primary-accent">{{ 'Download PDF' | translate }} </a>
      </ng-template>
    </e11-datatable>
  `, isInline: true, dependencies: [{ kind: "component", type: i2$1.E11DataTableComponent, selector: "e11-datatable", inputs: ["initialGridOptions", "records", "selectedRecords", "isAllSelected", "loading", "striped"], outputs: ["selectedRecordsChange", "isAllSelectedChange", "bulkOperationsChecked"] }, { kind: "directive", type: i2$1.E11DatatableTemplate, selector: "[e11DatatableTemplate]", inputs: ["type", "e11DatatableTemplate"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1.CurrencyPipe, name: "currency" }, { kind: "pipe", type: i1.DatePipe, name: "date" }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkBillingHistoryComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-payment-billing-history',
                    template: `
    <e11-datatable
      [initialGridOptions]="initialGridOptions"
      [loading]="(billingHistoryLoading$ | async) || false"
      [records]="(billingHistories$ | async) || []"
    >
      <ng-template e11DatatableTemplate="date" let-row>
        {{ row.date | date: 'MMMM dd, yyyy' }}
      </ng-template>
      <ng-template e11DatatableTemplate="amount" let-row>
        {{ row.amount | currency: row.currency }}
      </ng-template>
      <ng-template e11DatatableTemplate="invoiceUrl" let-row>
        <a [href]="row.invoiceUrl" class=" e11-text-skin-primary-accent">{{ 'Download PDF' | translate }} </a>
      </ng-template>
    </e11-datatable>
  `,
                }]
        }], ctorParameters: () => [{ type: PaymentStore }] });

class AmexSVGComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AmexSVGComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: AmexSVGComponent, selector: "amex-svg", ngImport: i0, template: `
    <svg enable-background="new 0 0 780 500" height="100%" viewBox="0 0 780 500" width="100%" xmlns="http://www.w3.org/2000/svg">
      <path
        d="m575.61 145.11-15.092 35.039h30.266zm-174.15 21.713c2.845-1.422 4.52-4.515 4.52-8.356 0-3.764-1.76-6.49-4.604-7.771-2.591-1.42-6.577-1.584-10.399-1.584h-27v19.523h26.638c4.266.001 7.831-.059 10.845-1.812zm-345.97-21.713-14.921 35.039h29.932zm694.7 224.47h-42.344v-18.852h42.173c4.181 0 7.109-.525 8.872-2.178 1.667-1.473 2.609-3.555 2.592-5.732 0-2.562-1.062-4.596-2.68-5.813-1.588-1.342-3.907-1.953-7.726-1.953-20.588-.67-46.273.609-46.273-27.211 0-12.75 8.451-26.172 31.461-26.172h43.677v-17.492h-40.58c-12.246 0-21.144 2.81-27.443 7.181v-7.181h-60.022c-9.597 0-20.863 2.279-26.191 7.181v-7.181h-107.19v7.181c-8.529-5.897-22.925-7.181-29.565-7.181h-70.702v7.181c-6.747-6.262-21.758-7.181-30.902-7.181h-79.127l-18.104 18.775-16.959-18.775h-118.2v122.68h115.97l18.655-19.076 17.575 19.076 71.484.06v-28.859h7.03c9.484.146 20.67-.223 30.542-4.311v33.106h58.962v-31.976h2.844c3.628 0 3.988.146 3.988 3.621v28.348h179.12c11.372 0 23.26-2.786 29.841-7.853v7.853h56.817c11.822 0 23.369-1.588 32.154-5.653v-22.853c-5.324 7.462-15.707 11.245-29.751 11.245zm-363.58-28.967h-27.36v29.488h-42.618l-27-29.102-28.058 29.102h-86.854v-87.914h88.19l26.976 28.818 27.89-28.818h70.064c17.401 0 36.952 4.617 36.952 28.963 0 24.422-19.016 29.463-38.182 29.463zm131.56-3.986c3.097 4.291 3.544 8.297 3.634 16.047v17.428h-22.016v-10.998c0-5.289.533-13.121-3.544-17.209-3.2-3.148-8.086-3.9-16.088-3.9h-23.432v32.107h-22.031v-87.914h50.62c11.105 0 19.188.473 26.384 4.148 6.92 4.006 11.275 9.494 11.275 19.523-.002 14.031-9.769 21.189-15.541 23.389 4.878 1.725 8.866 4.818 10.739 7.379zm90.575-36.258h-51.346v15.982h50.091v17.938h-50.091v17.492l51.346.078v18.242h-73.182v-87.914h73.182zm56.344 69.731h-42.705v-18.852h42.535c4.16 0 7.109-.527 8.957-2.178 1.507-1.359 2.591-3.336 2.591-5.73 0-2.564-1.174-4.598-2.676-5.818-1.678-1.34-3.993-1.947-7.809-1.947-20.506-.674-46.186.605-46.186-27.213 0-12.752 8.363-26.174 31.35-26.174h43.96v18.709h-40.225c-3.987 0-6.579.146-8.783 1.592-2.405 1.424-3.295 3.535-3.295 6.322 0 3.316 2.04 5.574 4.797 6.549 2.314.771 4.797.996 8.533.996l11.805.309c11.899.273 20.073 2.25 25.04 7.068 4.266 4.232 6.559 9.578 6.559 18.625-.002 18.913-12.335 27.742-34.448 27.742zm-170.06-68.313c-2.649-1.508-6.559-1.588-10.461-1.588h-27.001v19.744h26.64c4.265 0 7.892-.145 10.822-1.812 2.842-1.646 4.543-4.678 4.543-8.438s-1.701-6.482-4.543-7.906zm244.99-1.59c-3.988 0-6.641.145-8.873 1.588-2.314 1.426-3.202 3.537-3.202 6.326 0 3.314 1.953 5.572 4.794 6.549 2.315.771 4.796.996 8.448.996l11.887.303c11.99.285 19.998 2.262 24.879 7.08.889.668 1.423 1.42 2.034 2.174v-25.014h-39.965zm-352.65 0h-28.59v22.391h28.336c8.424 0 13.663-4.006 13.667-11.611-.004-7.688-5.497-10.78-13.413-10.78zm-190.81 0v15.984h48.136v17.938h-48.136v17.49h53.909l25.047-25.791-23.983-25.621zm140.77 61.479v-70.482l-33.664 34.674zm-138.93-141.15v15.148h183.19l-.085-32.046h3.545c2.483.083 3.205.302 3.205 4.229v27.818h94.748v-7.461c7.642 3.924 19.527 7.461 35.168 7.461h39.86l8.531-19.522h18.913l8.342 19.522h76.811v-18.544l11.629 18.543h61.555v-122.58h-60.915v14.477l-8.53-14.477h-62.507v14.477l-7.833-14.477h-84.434c-14.135 0-26.555 1.89-36.591 7.158v-7.158h-58.268v7.158c-6.387-5.43-15.089-7.158-24.762-7.158h-212.87l-14.282 31.662-14.668-31.662h-67.047v14.477l-7.367-14.477h-57.18l-26.553 58.284v46.621l39.264-87.894h32.579l37.29 83.217v-83.217h35.789l28.695 59.625 26.362-59.625h36.507v87.894h-22.475l-.082-68.837-31.796 68.837h-19.252l-31.877-68.898v68.898h-44.6l-8.425-19.605h-45.654l-8.512 19.605h-23.814v17.682h37.466l8.447-19.523h18.914l8.425 19.523h73.713v-14.927l6.579 14.989h38.266l6.58-15.214zm288.67-80.176c7.085-7.015 18.188-10.25 33.298-10.25h21.227v18.833h-20.782c-7.998 0-12.521 1.14-16.871 5.208-3.74 3.7-6.304 10.696-6.304 19.908 0 9.417 1.955 16.206 6.028 20.641 3.376 3.478 9.513 4.533 15.283 4.533h9.851l30.902-69.12h32.853l37.124 83.134v-83.133h33.386l38.543 61.213v-61.213h22.46v87.891h-31.072l-41.562-65.968v65.968h-44.656l-8.532-19.605h-45.55l-8.278 19.605h-25.66c-10.657 0-24.151-2.258-31.793-9.722-7.707-7.462-11.713-17.571-11.713-33.553-.004-13.037 2.389-24.953 11.818-34.37zm-45.101-10.249h22.372v87.894h-22.372zm-100.87 0h50.432c11.203 0 19.464.285 26.553 4.21 6.936 3.926 11.095 9.658 11.095 19.46 0 14.015-9.763 21.254-15.448 23.429 4.796 1.75 8.896 4.841 10.849 7.401 3.096 4.372 3.629 8.277 3.629 16.126v17.267h-22.115l-.083-11.084c0-5.29.528-12.896-3.461-17.122-3.203-3.09-8.088-3.763-15.983-3.763h-23.538v31.97h-21.927zm-88.393 0h73.249v18.303h-51.32v15.843h50.088v18.017h-50.088v17.553h51.32v18.177h-73.249z"
        fill="#2557d6"
      />
    </svg>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AmexSVGComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'amex-svg',
                    template: `
    <svg enable-background="new 0 0 780 500" height="100%" viewBox="0 0 780 500" width="100%" xmlns="http://www.w3.org/2000/svg">
      <path
        d="m575.61 145.11-15.092 35.039h30.266zm-174.15 21.713c2.845-1.422 4.52-4.515 4.52-8.356 0-3.764-1.76-6.49-4.604-7.771-2.591-1.42-6.577-1.584-10.399-1.584h-27v19.523h26.638c4.266.001 7.831-.059 10.845-1.812zm-345.97-21.713-14.921 35.039h29.932zm694.7 224.47h-42.344v-18.852h42.173c4.181 0 7.109-.525 8.872-2.178 1.667-1.473 2.609-3.555 2.592-5.732 0-2.562-1.062-4.596-2.68-5.813-1.588-1.342-3.907-1.953-7.726-1.953-20.588-.67-46.273.609-46.273-27.211 0-12.75 8.451-26.172 31.461-26.172h43.677v-17.492h-40.58c-12.246 0-21.144 2.81-27.443 7.181v-7.181h-60.022c-9.597 0-20.863 2.279-26.191 7.181v-7.181h-107.19v7.181c-8.529-5.897-22.925-7.181-29.565-7.181h-70.702v7.181c-6.747-6.262-21.758-7.181-30.902-7.181h-79.127l-18.104 18.775-16.959-18.775h-118.2v122.68h115.97l18.655-19.076 17.575 19.076 71.484.06v-28.859h7.03c9.484.146 20.67-.223 30.542-4.311v33.106h58.962v-31.976h2.844c3.628 0 3.988.146 3.988 3.621v28.348h179.12c11.372 0 23.26-2.786 29.841-7.853v7.853h56.817c11.822 0 23.369-1.588 32.154-5.653v-22.853c-5.324 7.462-15.707 11.245-29.751 11.245zm-363.58-28.967h-27.36v29.488h-42.618l-27-29.102-28.058 29.102h-86.854v-87.914h88.19l26.976 28.818 27.89-28.818h70.064c17.401 0 36.952 4.617 36.952 28.963 0 24.422-19.016 29.463-38.182 29.463zm131.56-3.986c3.097 4.291 3.544 8.297 3.634 16.047v17.428h-22.016v-10.998c0-5.289.533-13.121-3.544-17.209-3.2-3.148-8.086-3.9-16.088-3.9h-23.432v32.107h-22.031v-87.914h50.62c11.105 0 19.188.473 26.384 4.148 6.92 4.006 11.275 9.494 11.275 19.523-.002 14.031-9.769 21.189-15.541 23.389 4.878 1.725 8.866 4.818 10.739 7.379zm90.575-36.258h-51.346v15.982h50.091v17.938h-50.091v17.492l51.346.078v18.242h-73.182v-87.914h73.182zm56.344 69.731h-42.705v-18.852h42.535c4.16 0 7.109-.527 8.957-2.178 1.507-1.359 2.591-3.336 2.591-5.73 0-2.564-1.174-4.598-2.676-5.818-1.678-1.34-3.993-1.947-7.809-1.947-20.506-.674-46.186.605-46.186-27.213 0-12.752 8.363-26.174 31.35-26.174h43.96v18.709h-40.225c-3.987 0-6.579.146-8.783 1.592-2.405 1.424-3.295 3.535-3.295 6.322 0 3.316 2.04 5.574 4.797 6.549 2.314.771 4.797.996 8.533.996l11.805.309c11.899.273 20.073 2.25 25.04 7.068 4.266 4.232 6.559 9.578 6.559 18.625-.002 18.913-12.335 27.742-34.448 27.742zm-170.06-68.313c-2.649-1.508-6.559-1.588-10.461-1.588h-27.001v19.744h26.64c4.265 0 7.892-.145 10.822-1.812 2.842-1.646 4.543-4.678 4.543-8.438s-1.701-6.482-4.543-7.906zm244.99-1.59c-3.988 0-6.641.145-8.873 1.588-2.314 1.426-3.202 3.537-3.202 6.326 0 3.314 1.953 5.572 4.794 6.549 2.315.771 4.796.996 8.448.996l11.887.303c11.99.285 19.998 2.262 24.879 7.08.889.668 1.423 1.42 2.034 2.174v-25.014h-39.965zm-352.65 0h-28.59v22.391h28.336c8.424 0 13.663-4.006 13.667-11.611-.004-7.688-5.497-10.78-13.413-10.78zm-190.81 0v15.984h48.136v17.938h-48.136v17.49h53.909l25.047-25.791-23.983-25.621zm140.77 61.479v-70.482l-33.664 34.674zm-138.93-141.15v15.148h183.19l-.085-32.046h3.545c2.483.083 3.205.302 3.205 4.229v27.818h94.748v-7.461c7.642 3.924 19.527 7.461 35.168 7.461h39.86l8.531-19.522h18.913l8.342 19.522h76.811v-18.544l11.629 18.543h61.555v-122.58h-60.915v14.477l-8.53-14.477h-62.507v14.477l-7.833-14.477h-84.434c-14.135 0-26.555 1.89-36.591 7.158v-7.158h-58.268v7.158c-6.387-5.43-15.089-7.158-24.762-7.158h-212.87l-14.282 31.662-14.668-31.662h-67.047v14.477l-7.367-14.477h-57.18l-26.553 58.284v46.621l39.264-87.894h32.579l37.29 83.217v-83.217h35.789l28.695 59.625 26.362-59.625h36.507v87.894h-22.475l-.082-68.837-31.796 68.837h-19.252l-31.877-68.898v68.898h-44.6l-8.425-19.605h-45.654l-8.512 19.605h-23.814v17.682h37.466l8.447-19.523h18.914l8.425 19.523h73.713v-14.927l6.579 14.989h38.266l6.58-15.214zm288.67-80.176c7.085-7.015 18.188-10.25 33.298-10.25h21.227v18.833h-20.782c-7.998 0-12.521 1.14-16.871 5.208-3.74 3.7-6.304 10.696-6.304 19.908 0 9.417 1.955 16.206 6.028 20.641 3.376 3.478 9.513 4.533 15.283 4.533h9.851l30.902-69.12h32.853l37.124 83.134v-83.133h33.386l38.543 61.213v-61.213h22.46v87.891h-31.072l-41.562-65.968v65.968h-44.656l-8.532-19.605h-45.55l-8.278 19.605h-25.66c-10.657 0-24.151-2.258-31.793-9.722-7.707-7.462-11.713-17.571-11.713-33.553-.004-13.037 2.389-24.953 11.818-34.37zm-45.101-10.249h22.372v87.894h-22.372zm-100.87 0h50.432c11.203 0 19.464.285 26.553 4.21 6.936 3.926 11.095 9.658 11.095 19.46 0 14.015-9.763 21.254-15.448 23.429 4.796 1.75 8.896 4.841 10.849 7.401 3.096 4.372 3.629 8.277 3.629 16.126v17.267h-22.115l-.083-11.084c0-5.29.528-12.896-3.461-17.122-3.203-3.09-8.088-3.763-15.983-3.763h-23.538v31.97h-21.927zm-88.393 0h73.249v18.303h-51.32v15.843h50.088v18.017h-50.088v17.553h51.32v18.177h-73.249z"
        fill="#2557d6"
      />
    </svg>
  `,
                }]
        }], ctorParameters: () => [] });

class DiscoverSVGComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: DiscoverSVGComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: DiscoverSVGComponent, selector: "discover-svg", ngImport: i0, template: `
    <svg enable-background="new 0 0 780 500" height="100%" viewBox="0 0 780 500" width="100%" xmlns="http://www.w3.org/2000/svg">
      <path
        d="m409.41 197.26c30.938 0 56.02 23.58 56.02 52.709v.033c0 29.129-25.082 52.742-56.02 52.742-30.941 0-56.022-23.613-56.022-52.742v-.033c0-29.129 25.081-52.709 56.022-52.709z"
        fill="#f47216"
      />
      <path
        d="m321.43 197.94c8.836 0 16.247 1.785 25.27 6.09v22.753c-8.544-7.863-15.955-11.154-25.757-11.154-19.265 0-34.413 15.015-34.413 34.051 0 20.074 14.681 34.195 35.368 34.195 9.312 0 16.586-3.12 24.802-10.856v22.764c-9.343 4.142-16.912 5.775-25.757 5.775-31.277 0-55.581-22.597-55.581-51.736-.002-28.83 24.949-51.882 56.068-51.882zm-97.113.626c11.546 0 22.109 3.721 30.942 10.994l-10.748 13.248c-5.351-5.646-10.411-8.027-16.563-8.027-8.854 0-15.301 4.744-15.301 10.988 0 5.354 3.618 8.188 15.944 12.481 23.364 8.043 30.289 15.176 30.289 30.926 0 19.193-14.976 32.554-36.319 32.554-15.631 0-26.993-5.795-36.457-18.871l13.268-12.03c4.73 8.608 12.622 13.223 22.42 13.223 9.163 0 15.947-5.95 15.947-13.983 0-4.164-2.056-7.733-6.158-10.258-2.066-1.195-6.158-2.978-14.199-5.646-19.292-6.538-25.91-13.527-25.91-27.186-.001-16.227 14.213-28.413 32.845-28.413zm234.72 1.729h22.436l28.084 66.592 28.447-66.592h22.267l-45.493 101.69h-11.054zm-301.21.152h20.541v99.143h-20.541zm411.73 0h58.253v16.799h-37.726v22.006h36.336v16.791h-36.336v26.762h37.726v16.785h-58.253zm115.59 57.377c15.471-2.965 23.983-12.926 23.983-28.105 0-18.562-13.575-29.271-37.266-29.271h-30.454v99.144h20.516v-39.83h2.681l28.43 39.828h25.26zm-17.218-11.736h-6.002v-30.025h6.326c12.791 0 19.744 5.049 19.744 14.697.002 9.967-6.951 15.328-20.068 15.328zm-576.09-45.641h-30.149v99.143h29.992c15.946 0 27.465-3.543 37.573-11.445 12.014-9.359 19.117-23.467 19.117-38.057.001-29.259-23.221-49.641-56.533-49.641zm23.997 74.479c-6.454 5.484-14.837 7.879-28.108 7.879h-5.514v-65.559h5.513c13.271 0 21.323 2.238 28.108 8.018 7.104 5.956 11.377 15.184 11.377 24.682.001 9.513-4.273 19.024-11.376 24.98z"
      />
    </svg>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: DiscoverSVGComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'discover-svg',
                    template: `
    <svg enable-background="new 0 0 780 500" height="100%" viewBox="0 0 780 500" width="100%" xmlns="http://www.w3.org/2000/svg">
      <path
        d="m409.41 197.26c30.938 0 56.02 23.58 56.02 52.709v.033c0 29.129-25.082 52.742-56.02 52.742-30.941 0-56.022-23.613-56.022-52.742v-.033c0-29.129 25.081-52.709 56.022-52.709z"
        fill="#f47216"
      />
      <path
        d="m321.43 197.94c8.836 0 16.247 1.785 25.27 6.09v22.753c-8.544-7.863-15.955-11.154-25.757-11.154-19.265 0-34.413 15.015-34.413 34.051 0 20.074 14.681 34.195 35.368 34.195 9.312 0 16.586-3.12 24.802-10.856v22.764c-9.343 4.142-16.912 5.775-25.757 5.775-31.277 0-55.581-22.597-55.581-51.736-.002-28.83 24.949-51.882 56.068-51.882zm-97.113.626c11.546 0 22.109 3.721 30.942 10.994l-10.748 13.248c-5.351-5.646-10.411-8.027-16.563-8.027-8.854 0-15.301 4.744-15.301 10.988 0 5.354 3.618 8.188 15.944 12.481 23.364 8.043 30.289 15.176 30.289 30.926 0 19.193-14.976 32.554-36.319 32.554-15.631 0-26.993-5.795-36.457-18.871l13.268-12.03c4.73 8.608 12.622 13.223 22.42 13.223 9.163 0 15.947-5.95 15.947-13.983 0-4.164-2.056-7.733-6.158-10.258-2.066-1.195-6.158-2.978-14.199-5.646-19.292-6.538-25.91-13.527-25.91-27.186-.001-16.227 14.213-28.413 32.845-28.413zm234.72 1.729h22.436l28.084 66.592 28.447-66.592h22.267l-45.493 101.69h-11.054zm-301.21.152h20.541v99.143h-20.541zm411.73 0h58.253v16.799h-37.726v22.006h36.336v16.791h-36.336v26.762h37.726v16.785h-58.253zm115.59 57.377c15.471-2.965 23.983-12.926 23.983-28.105 0-18.562-13.575-29.271-37.266-29.271h-30.454v99.144h20.516v-39.83h2.681l28.43 39.828h25.26zm-17.218-11.736h-6.002v-30.025h6.326c12.791 0 19.744 5.049 19.744 14.697.002 9.967-6.951 15.328-20.068 15.328zm-576.09-45.641h-30.149v99.143h29.992c15.946 0 27.465-3.543 37.573-11.445 12.014-9.359 19.117-23.467 19.117-38.057.001-29.259-23.221-49.641-56.533-49.641zm23.997 74.479c-6.454 5.484-14.837 7.879-28.108 7.879h-5.514v-65.559h5.513c13.271 0 21.323 2.238 28.108 8.018 7.104 5.956 11.377 15.184 11.377 24.682.001 9.513-4.273 19.024-11.376 24.98z"
      />
    </svg>
  `,
                }]
        }], ctorParameters: () => [] });

class JcbSVGComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: JcbSVGComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: JcbSVGComponent, selector: "jcb-svg", ngImport: i0, template: `
    <svg
      enable-background="new 0 0 780 500"
      height="100%"
      viewBox="0 0 780 500"
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
    >
      <linearGradient
        id="a"
        gradientTransform="matrix(132.87 0 0 323.02 -120270 -100930)"
        gradientUnits="userSpaceOnUse"
        x1="908.72"
        x2="909.72"
        y1="313.21"
        y2="313.21"
      >
        <stop offset="0" stop-color="#007b40" />
        <stop offset="1" stop-color="#55b330" />
      </linearGradient>
      <linearGradient
        id="b"
        gradientTransform="matrix(133.43 0 0 323.02 -121080 -100920)"
        gradientUnits="userSpaceOnUse"
        x1="908.73"
        x2="909.73"
        y1="313.21"
        y2="313.21"
      >
        <stop offset="0" stop-color="#1d2970" />
        <stop offset="1" stop-color="#006dba" />
      </linearGradient>
      <linearGradient
        id="c"
        gradientTransform="matrix(132.96 0 0 323.03 -120500 -100930)"
        gradientUnits="userSpaceOnUse"
        x1="908.72"
        x2="909.72"
        y1="313.21"
        y2="313.21"
      >
        <stop offset="0" stop-color="#6e2b2f" />
        <stop offset="1" stop-color="#e30138" />
      </linearGradient>
      <path
        d="m632.24 361.27c0 41.615-33.729 75.36-75.357 75.36h-409.13v-297.88c0-41.626 33.73-75.371 75.364-75.371h409.12l-.001 297.89z"
        fill="#fff"
      />
      <path
        d="m498.86 256.54c11.686.254 23.438-.516 35.077.4 11.787 2.199 14.628 20.043 4.156 25.887-7.145 3.85-15.633 1.434-23.379 2.113h-15.854zm41.834-32.145c2.596 9.164-6.238 17.392-15.064 16.13h-26.77c.188-8.642-.367-18.022.272-26.209 10.724.302 21.547-.616 32.209.48 4.581 1.151 8.415 4.917 9.353 9.599zm64.425-135.9c.498 17.501.072 35.927.215 53.783-.033 72.596.07 145.19-.057 217.79-.47 27.207-24.582 50.848-51.601 51.391-27.045.11-54.094.017-81.143.047v-109.75c29.471-.152 58.957.309 88.416-.23 13.666-.858 28.635-9.875 29.271-24.914 1.609-15.104-12.631-25.551-26.151-27.201-5.197-.135-5.045-1.515 0-2.117 12.895-2.787 23.021-16.133 19.227-29.499-3.233-14.058-18.771-19.499-31.695-19.472-26.352-.179-52.709-.025-79.062-.077.17-20.489-.355-41 .283-61.474 2.088-26.716 26.807-48.748 53.446-48.27 26.287-.004 52.57-.004 78.851-.005z"
        fill="url(#a)"
      />
      <path
        d="m174.74 139.54c.673-27.164 24.888-50.611 51.872-51.008 26.945-.083 53.894-.012 80.839-.036-.074 90.885.146 181.78-.111 272.66-1.038 26.834-24.989 49.834-51.679 50.309-26.996.098-53.995.014-80.992.041v-113.45c26.223 6.195 53.722 8.832 80.474 4.723 15.991-2.573 33.487-10.426 38.901-27.016 3.984-14.191 1.741-29.126 2.334-43.691v-33.825h-46.297c-.208 22.371.426 44.781-.335 67.125-1.248 13.734-14.849 22.46-27.802 21.994-16.064.17-47.897-11.642-47.897-11.642-.08-41.914.466-94.405.693-136.18z"
        fill="url(#b)"
      />
      <path
        d="m324.72 211.89c-2.437.517-.49-8.301-1.113-11.646.166-21.15-.347-42.323.283-63.458 2.082-26.829 26.991-48.916 53.738-48.288h78.768c-.074 90.885.145 181.78-.111 272.66-1.039 26.834-24.992 49.833-51.683 50.309-26.997.102-53.997.016-80.996.042v-124.3c18.439 15.129 43.5 17.484 66.472 17.525 17.318-.006 34.535-2.676 51.353-6.67v-22.772c-18.953 9.446-41.233 15.446-62.243 10.019-14.656-3.648-25.295-17.812-25.058-32.937-1.698-15.729 7.522-32.335 22.979-37.011 19.191-6.008 40.107-1.413 58.096 6.398 3.854 2.018 7.766 4.521 6.225-1.921v-17.899c-30.086-7.158-62.104-9.792-92.33-2.005-8.749 2.468-17.273 6.211-24.38 11.956z"
        fill="url(#c)"
      />
    </svg>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: JcbSVGComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'jcb-svg',
                    template: `
    <svg
      enable-background="new 0 0 780 500"
      height="100%"
      viewBox="0 0 780 500"
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
    >
      <linearGradient
        id="a"
        gradientTransform="matrix(132.87 0 0 323.02 -120270 -100930)"
        gradientUnits="userSpaceOnUse"
        x1="908.72"
        x2="909.72"
        y1="313.21"
        y2="313.21"
      >
        <stop offset="0" stop-color="#007b40" />
        <stop offset="1" stop-color="#55b330" />
      </linearGradient>
      <linearGradient
        id="b"
        gradientTransform="matrix(133.43 0 0 323.02 -121080 -100920)"
        gradientUnits="userSpaceOnUse"
        x1="908.73"
        x2="909.73"
        y1="313.21"
        y2="313.21"
      >
        <stop offset="0" stop-color="#1d2970" />
        <stop offset="1" stop-color="#006dba" />
      </linearGradient>
      <linearGradient
        id="c"
        gradientTransform="matrix(132.96 0 0 323.03 -120500 -100930)"
        gradientUnits="userSpaceOnUse"
        x1="908.72"
        x2="909.72"
        y1="313.21"
        y2="313.21"
      >
        <stop offset="0" stop-color="#6e2b2f" />
        <stop offset="1" stop-color="#e30138" />
      </linearGradient>
      <path
        d="m632.24 361.27c0 41.615-33.729 75.36-75.357 75.36h-409.13v-297.88c0-41.626 33.73-75.371 75.364-75.371h409.12l-.001 297.89z"
        fill="#fff"
      />
      <path
        d="m498.86 256.54c11.686.254 23.438-.516 35.077.4 11.787 2.199 14.628 20.043 4.156 25.887-7.145 3.85-15.633 1.434-23.379 2.113h-15.854zm41.834-32.145c2.596 9.164-6.238 17.392-15.064 16.13h-26.77c.188-8.642-.367-18.022.272-26.209 10.724.302 21.547-.616 32.209.48 4.581 1.151 8.415 4.917 9.353 9.599zm64.425-135.9c.498 17.501.072 35.927.215 53.783-.033 72.596.07 145.19-.057 217.79-.47 27.207-24.582 50.848-51.601 51.391-27.045.11-54.094.017-81.143.047v-109.75c29.471-.152 58.957.309 88.416-.23 13.666-.858 28.635-9.875 29.271-24.914 1.609-15.104-12.631-25.551-26.151-27.201-5.197-.135-5.045-1.515 0-2.117 12.895-2.787 23.021-16.133 19.227-29.499-3.233-14.058-18.771-19.499-31.695-19.472-26.352-.179-52.709-.025-79.062-.077.17-20.489-.355-41 .283-61.474 2.088-26.716 26.807-48.748 53.446-48.27 26.287-.004 52.57-.004 78.851-.005z"
        fill="url(#a)"
      />
      <path
        d="m174.74 139.54c.673-27.164 24.888-50.611 51.872-51.008 26.945-.083 53.894-.012 80.839-.036-.074 90.885.146 181.78-.111 272.66-1.038 26.834-24.989 49.834-51.679 50.309-26.996.098-53.995.014-80.992.041v-113.45c26.223 6.195 53.722 8.832 80.474 4.723 15.991-2.573 33.487-10.426 38.901-27.016 3.984-14.191 1.741-29.126 2.334-43.691v-33.825h-46.297c-.208 22.371.426 44.781-.335 67.125-1.248 13.734-14.849 22.46-27.802 21.994-16.064.17-47.897-11.642-47.897-11.642-.08-41.914.466-94.405.693-136.18z"
        fill="url(#b)"
      />
      <path
        d="m324.72 211.89c-2.437.517-.49-8.301-1.113-11.646.166-21.15-.347-42.323.283-63.458 2.082-26.829 26.991-48.916 53.738-48.288h78.768c-.074 90.885.145 181.78-.111 272.66-1.039 26.834-24.992 49.833-51.683 50.309-26.997.102-53.997.016-80.996.042v-124.3c18.439 15.129 43.5 17.484 66.472 17.525 17.318-.006 34.535-2.676 51.353-6.67v-22.772c-18.953 9.446-41.233 15.446-62.243 10.019-14.656-3.648-25.295-17.812-25.058-32.937-1.698-15.729 7.522-32.335 22.979-37.011 19.191-6.008 40.107-1.413 58.096 6.398 3.854 2.018 7.766 4.521 6.225-1.921v-17.899c-30.086-7.158-62.104-9.792-92.33-2.005-8.749 2.468-17.273 6.211-24.38 11.956z"
        fill="url(#c)"
      />
    </svg>
  `,
                }]
        }], ctorParameters: () => [] });

class MasterCardSVGComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MasterCardSVGComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: MasterCardSVGComponent, selector: "master-card-svg", ngImport: i0, template: `<svg enable-background="new 0 0 780 500" height="100%" viewBox="0 0 780 500" width="100%" xmlns="http://www.w3.org/2000/svg">
    <path
      d="m449.01 250c0 99.143-80.371 179.5-179.51 179.5s-179.5-80.361-179.5-179.5c0-99.133 80.362-179.5 179.5-179.5 99.137 0 179.51 80.371 179.51 179.5"
      fill="#d9222a"
    />
    <path
      d="m510.49 70.496c-46.379 0-88.643 17.596-120.5 46.467-6.49 5.889-12.548 12.237-18.125 18.996h36.267c4.965 6.037 9.536 12.387 13.685 19.012h-63.635c-3.827 6.122-7.281 12.469-10.342 19.008h84.313c2.894 6.185 5.431 12.53 7.601 19.004h-99.513c-2.09 6.234-3.832 12.58-5.217 19.008h109.94c2.689 12.49 4.045 25.231 4.042 38.008 0 19.935-3.254 39.112-9.254 57.021h-99.513c2.164 6.477 4.7 12.824 7.596 19.008h84.316c-3.063 6.541-6.519 12.889-10.347 19.013h-63.625c4.147 6.62 8.719 12.966 13.685 18.996h36.259c-5.57 6.772-11.63 13.127-18.13 19.013 31.857 28.866 74.117 46.454 120.5 46.454 99.139 0 179.51-80.361 179.51-179.5 0-99.129-80.371-179.5-179.51-179.5"
      fill="#ee9f2d"
    />
    <path
      d="m666.07 350.06c0-3.199 2.592-5.801 5.796-5.801s5.796 2.602 5.796 5.801-2.592 5.801-5.796 5.801-5.796-2.602-5.796-5.801zm5.796 4.408c2.434-.001 4.407-1.974 4.408-4.408 0-2.432-1.971-4.402-4.402-4.404h-.006c-2.429-.003-4.4 1.963-4.404 4.391v.014c-.002 2.433 1.968 4.406 4.4 4.408.001-.001.003-.001.004-.001zm-.783-1.86h-1.187v-5.096h2.149c.45 0 .908 0 1.305.254.413.279.646.771.646 1.279 0 .571-.338 1.104-.884 1.312l.938 2.25h-1.315l-.779-2.017h-.871zm0-2.89h.658c.246 0 .505.021.726-.1.195-.125.296-.359.296-.584-.005-.209-.112-.402-.288-.518-.207-.129-.536-.101-.758-.101h-.634zm-443.5-80.063c-2.046-.238-2.945-.301-4.35-.301-11.046 0-16.638 3.787-16.638 11.268 0 4.611 2.729 7.545 6.987 7.545 7.939 0 13.659-7.559 14.001-18.512zm14.171 32.996h-16.146l.371-7.676c-4.926 6.065-11.496 8.949-20.426 8.949-10.563 0-17.804-8.25-17.804-20.229 0-18.024 12.596-28.541 34.217-28.541 2.208 0 5.042.199 7.941.57.604-2.441.763-3.488.763-4.801 0-4.908-3.396-6.737-12.5-6.737-9.533-.108-17.396 2.271-20.625 3.333.204-1.229 2.7-16.659 2.7-16.659 9.712-2.846 16.116-3.917 23.325-3.917 16.732 0 25.596 7.513 25.579 21.712.033 3.805-.597 8.5-1.579 14.671-1.691 10.734-5.32 33.721-5.816 39.325zm-62.158 0h-19.487l11.162-69.997-24.925 69.997h-13.279l-1.642-69.597-11.733 69.597h-18.242l15.237-91.056h28.021l1.7 50.968 17.092-50.968h31.167zm354.97-32.996c-2.037-.238-2.941-.301-4.342-.301-11.041 0-16.634 3.787-16.634 11.268 0 4.611 2.726 7.545 6.983 7.545 7.94 0 13.664-7.559 13.993-18.512zm14.184 32.996h-16.146l.366-7.676c-4.926 6.065-11.5 8.949-20.422 8.949-10.565 0-17.8-8.25-17.8-20.229 0-18.024 12.588-28.541 34.213-28.541 2.208 0 5.037.199 7.934.57.604-2.441.763-3.488.763-4.801 0-4.908-3.392-6.737-12.496-6.737-9.533-.108-17.387 2.271-20.629 3.333.204-1.229 2.709-16.659 2.709-16.659 9.712-2.846 16.112-3.917 23.313-3.917 16.74 0 25.604 7.513 25.587 21.712.032 3.805-.597 8.5-1.579 14.671-1.684 10.734-5.321 33.721-5.813 39.325zm-220.39-1.125c-5.333 1.679-9.491 2.398-14 2.398-9.962 0-15.399-5.725-15.399-16.267-.142-3.271 1.433-11.88 2.671-19.737 1.125-6.917 8.449-50.529 8.449-50.529h19.371l-2.263 11.208h11.699l-2.642 17.796h-11.742c-2.25 14.083-5.454 31.625-5.491 33.95 0 3.816 2.037 5.483 6.671 5.483 2.221 0 3.94-.227 5.254-.7zm59.392-.6c-6.654 2.034-13.075 3.017-19.879 3-21.684-.021-32.987-11.346-32.987-33.032 0-25.313 14.38-43.947 33.899-43.947 15.971 0 26.171 10.433 26.171 26.796 0 5.429-.7 10.729-2.388 18.212h-38.574c-1.305 10.741 5.57 15.217 16.837 15.217 6.935 0 13.188-1.429 20.142-4.663zm-10.888-43.9c.107-1.543 2.055-13.217-9.013-13.217-6.171 0-10.583 4.704-12.38 13.217zm-123.42-5.017c0 9.367 4.542 15.826 14.842 20.676 7.892 3.709 9.112 4.81 9.112 8.17 0 4.617-3.479 6.701-11.191 6.701-5.813 0-11.221-.908-17.458-2.922 0 0-2.563 16.321-2.68 17.102 4.43.967 8.38 1.861 20.279 2.19 20.563 0 30.059-7.829 30.059-24.75 0-10.175-3.976-16.146-13.737-20.634-8.171-3.75-9.108-4.587-9.108-8.045 0-4.004 3.237-6.046 9.537-6.046 3.825 0 9.05.408 14 1.112l2.775-17.175c-5.046-.8-12.696-1.442-17.15-1.442-21.801.001-29.347 11.388-29.28 25.063m229.09-23.116c5.412 0 10.458 1.421 17.412 4.921l3.188-19.763c-2.854-1.121-12.904-7.7-21.417-7.7-13.041 0-24.065 6.471-31.82 17.15-11.309-3.746-15.958 3.825-21.657 11.367l-5.063 1.179c.383-2.483.729-4.95.612-7.446h-17.896c-2.445 22.917-6.778 46.128-10.171 69.075l-.884 4.976h19.496c3.254-21.143 5.037-34.68 6.121-43.842l7.341-4.084c1.097-4.078 4.529-5.458 11.417-5.291-.926 5.008-1.389 10.091-1.383 15.184 0 24.225 13.07 39.308 34.05 39.308 5.404 0 10.041-.712 17.221-2.658l3.43-20.759c-6.458 3.181-11.759 4.677-16.559 4.677-11.329 0-18.184-8.363-18.184-22.185 0-20.051 10.196-34.109 24.746-34.109"
    />
    <path
      d="m185.21 297.24h-19.491l11.171-69.988-24.926 69.988h-13.283l-1.642-69.588-11.733 69.588h-18.241l15.237-91.042h28.021l.788 56.362 18.904-56.362h30.267z"
      fill="#fff"
    />
    <path
      d="m647.52 211.6-4.321 26.309c-5.329-7.013-11.054-12.088-18.612-12.088-9.833 0-18.783 7.455-24.642 18.425-8.158-1.692-16.597-4.563-16.597-4.563l-.004.067c.658-6.134.921-9.875.862-11.146h-17.9c-2.438 22.917-6.771 46.128-10.157 69.075l-.893 4.976h19.492c2.633-17.096 4.648-31.291 6.133-42.551 6.658-6.016 9.992-11.266 16.721-10.916-2.979 7.205-4.725 15.503-4.725 24.017 0 18.513 9.366 30.725 23.533 30.725 7.142 0 12.621-2.462 17.967-8.171l-.913 6.884h18.435l14.842-91.042zm-24.371 73.941c-6.634 0-9.983-4.908-9.983-14.596 0-14.555 6.271-24.875 15.112-24.875 6.695 0 10.32 5.104 10.32 14.509.001 14.679-6.37 24.962-15.449 24.962z"
    />
    <path
      d="m233.19 264.26c-2.042-.236-2.946-.299-4.346-.299-11.046 0-16.634 3.787-16.634 11.266 0 4.604 2.729 7.547 6.979 7.547 7.947-.001 13.668-7.559 14.001-18.514zm14.178 32.984h-16.146l.367-7.663c-4.921 6.054-11.5 8.95-20.421 8.95-10.567 0-17.805-8.25-17.805-20.229 0-18.032 12.592-28.542 34.217-28.542 2.208 0 5.042.2 7.938.571.604-2.441.763-3.487.763-4.808 0-4.909-3.392-6.729-12.496-6.729-9.537-.108-17.396 2.271-20.629 3.321.204-1.225 2.7-16.637 2.7-16.637 9.708-2.858 16.12-3.929 23.32-3.929 16.737 0 25.604 7.517 25.588 21.704.029 3.821-.604 8.513-1.584 14.675-1.687 10.724-5.319 33.724-5.812 39.316zm261.38-88.592-3.191 19.767c-6.95-3.496-12-4.92-17.407-4.92-14.551 0-24.75 14.058-24.75 34.106 0 13.821 6.857 22.181 18.184 22.181 4.8 0 10.096-1.492 16.554-4.675l-3.421 20.75c-7.184 1.957-11.816 2.67-17.225 2.67-20.977 0-34.051-15.084-34.051-39.309 0-32.55 18.059-55.3 43.888-55.3 8.507.001 18.561 3.609 21.419 4.73m31.443 55.608c-2.041-.236-2.941-.299-4.347-.299-11.041 0-16.633 3.787-16.633 11.266 0 4.604 2.729 7.547 6.983 7.547 7.938-.001 13.663-7.559 13.997-18.514zm14.178 32.984h-16.15l.371-7.663c-4.925 6.054-11.5 8.95-20.421 8.95-10.563 0-17.804-8.25-17.804-20.229 0-18.032 12.596-28.542 34.212-28.542 2.213 0 5.042.2 7.941.571.601-2.441.763-3.487.763-4.808 0-4.909-3.393-6.729-12.495-6.729-9.533-.108-17.396 2.271-20.63 3.321.204-1.225 2.704-16.637 2.704-16.637 9.709-2.858 16.116-3.929 23.316-3.929 16.741 0 25.604 7.517 25.583 21.704.033 3.821-.596 8.513-1.579 14.675-1.682 10.724-5.323 33.724-5.811 39.316zm-220.39-1.121c-5.338 1.679-9.496 2.408-14 2.408-9.962 0-15.399-5.726-15.399-16.268-.138-3.279 1.438-11.88 2.675-19.736 1.12-6.926 8.445-50.534 8.445-50.534h19.368l-2.26 11.212h9.941l-2.646 17.788h-9.975c-2.25 14.092-5.463 31.62-5.496 33.95 0 3.83 2.041 5.482 6.671 5.482 2.221 0 3.938-.216 5.254-.691zm59.391-.592c-6.65 2.033-13.079 3.012-19.879 3-21.685-.021-32.987-11.346-32.987-33.033 0-25.321 14.379-43.95 33.899-43.95 15.971 0 26.171 10.429 26.171 26.8 0 5.434-.7 10.733-2.384 18.212h-38.574c-1.306 10.741 5.569 15.222 16.837 15.222 6.93 0 13.188-1.435 20.138-4.677zm-10.891-43.912c.116-1.538 2.06-13.217-9.013-13.217-6.167 0-10.579 4.717-12.375 13.217zm-123.42-5.005c0 9.367 4.542 15.818 14.842 20.675 7.892 3.709 9.112 4.812 9.112 8.172 0 4.616-3.483 6.699-11.188 6.699-5.816 0-11.225-.908-17.467-2.921 0 0-2.554 16.321-2.671 17.101 4.421.967 8.375 1.85 20.275 2.191 20.566 0 30.059-7.829 30.059-24.746 0-10.18-3.971-16.15-13.737-20.637-8.167-3.759-9.113-4.584-9.113-8.046 0-4 3.246-6.059 9.542-6.059 3.821 0 9.046.421 14.004 1.125l2.771-17.179c-5.042-.8-12.692-1.441-17.146-1.441-21.804 0-29.346 11.379-29.283 25.066m398.45 50.63h-18.438l.917-6.893c-5.347 5.717-10.825 8.18-17.968 8.18-14.166 0-23.528-12.213-23.528-30.726 0-24.63 14.521-45.392 31.708-45.392 7.559 0 13.279 3.087 18.604 10.096l4.325-26.308h19.221zm-28.746-17.109c9.075 0 15.45-10.283 15.45-24.953 0-9.405-3.629-14.509-10.325-14.509-8.837 0-15.115 10.315-15.115 24.875-.001 9.686 3.357 14.587 9.99 14.587zm-56.842-56.929c-2.441 22.917-6.773 46.13-10.162 69.063l-.892 4.976h19.491c6.972-45.275 8.658-54.117 19.588-53.009 1.742-9.267 4.982-17.383 7.399-21.479-8.163-1.7-12.721 2.913-18.688 11.675.471-3.788 1.333-7.467 1.162-11.225zm-160.42 0c-2.446 22.917-6.779 46.13-10.167 69.063l-.888 4.976h19.5c6.963-45.275 8.646-54.117 19.57-53.009 1.75-9.267 4.991-17.383 7.399-21.479-8.154-1.7-12.717 2.913-18.679 11.675.471-3.788 1.324-7.467 1.162-11.225zm254.57 68.241c-.004-3.199 2.586-5.795 5.784-5.799h.012c3.197-.004 5.793 2.586 5.796 5.783v.016c-.001 3.201-2.595 5.795-5.796 5.797-3.201-.002-5.795-2.596-5.796-5.797zm5.796 4.405c2.431.002 4.402-1.969 4.403-4.399v-.004c.003-2.433-1.968-4.406-4.399-4.408h-.004c-2.435.001-4.407 1.974-4.408 4.408.002 2.432 1.975 4.403 4.408 4.403zm-.784-1.871h-1.188v-5.082h2.153c.446 0 .909.009 1.296.254.417.283.654.767.654 1.274 0 .575-.337 1.112-.888 1.317l.941 2.236h-1.32l-.779-2.009h-.87zm0-2.879h.653c.246 0 .513.019.729-.1.196-.125.296-.361.296-.588-.009-.21-.114-.404-.287-.523-.204-.117-.542-.084-.763-.084h-.629z"
      fill="#fff"
    />
  </svg> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: MasterCardSVGComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'master-card-svg',
                    template: `<svg enable-background="new 0 0 780 500" height="100%" viewBox="0 0 780 500" width="100%" xmlns="http://www.w3.org/2000/svg">
    <path
      d="m449.01 250c0 99.143-80.371 179.5-179.51 179.5s-179.5-80.361-179.5-179.5c0-99.133 80.362-179.5 179.5-179.5 99.137 0 179.51 80.371 179.51 179.5"
      fill="#d9222a"
    />
    <path
      d="m510.49 70.496c-46.379 0-88.643 17.596-120.5 46.467-6.49 5.889-12.548 12.237-18.125 18.996h36.267c4.965 6.037 9.536 12.387 13.685 19.012h-63.635c-3.827 6.122-7.281 12.469-10.342 19.008h84.313c2.894 6.185 5.431 12.53 7.601 19.004h-99.513c-2.09 6.234-3.832 12.58-5.217 19.008h109.94c2.689 12.49 4.045 25.231 4.042 38.008 0 19.935-3.254 39.112-9.254 57.021h-99.513c2.164 6.477 4.7 12.824 7.596 19.008h84.316c-3.063 6.541-6.519 12.889-10.347 19.013h-63.625c4.147 6.62 8.719 12.966 13.685 18.996h36.259c-5.57 6.772-11.63 13.127-18.13 19.013 31.857 28.866 74.117 46.454 120.5 46.454 99.139 0 179.51-80.361 179.51-179.5 0-99.129-80.371-179.5-179.51-179.5"
      fill="#ee9f2d"
    />
    <path
      d="m666.07 350.06c0-3.199 2.592-5.801 5.796-5.801s5.796 2.602 5.796 5.801-2.592 5.801-5.796 5.801-5.796-2.602-5.796-5.801zm5.796 4.408c2.434-.001 4.407-1.974 4.408-4.408 0-2.432-1.971-4.402-4.402-4.404h-.006c-2.429-.003-4.4 1.963-4.404 4.391v.014c-.002 2.433 1.968 4.406 4.4 4.408.001-.001.003-.001.004-.001zm-.783-1.86h-1.187v-5.096h2.149c.45 0 .908 0 1.305.254.413.279.646.771.646 1.279 0 .571-.338 1.104-.884 1.312l.938 2.25h-1.315l-.779-2.017h-.871zm0-2.89h.658c.246 0 .505.021.726-.1.195-.125.296-.359.296-.584-.005-.209-.112-.402-.288-.518-.207-.129-.536-.101-.758-.101h-.634zm-443.5-80.063c-2.046-.238-2.945-.301-4.35-.301-11.046 0-16.638 3.787-16.638 11.268 0 4.611 2.729 7.545 6.987 7.545 7.939 0 13.659-7.559 14.001-18.512zm14.171 32.996h-16.146l.371-7.676c-4.926 6.065-11.496 8.949-20.426 8.949-10.563 0-17.804-8.25-17.804-20.229 0-18.024 12.596-28.541 34.217-28.541 2.208 0 5.042.199 7.941.57.604-2.441.763-3.488.763-4.801 0-4.908-3.396-6.737-12.5-6.737-9.533-.108-17.396 2.271-20.625 3.333.204-1.229 2.7-16.659 2.7-16.659 9.712-2.846 16.116-3.917 23.325-3.917 16.732 0 25.596 7.513 25.579 21.712.033 3.805-.597 8.5-1.579 14.671-1.691 10.734-5.32 33.721-5.816 39.325zm-62.158 0h-19.487l11.162-69.997-24.925 69.997h-13.279l-1.642-69.597-11.733 69.597h-18.242l15.237-91.056h28.021l1.7 50.968 17.092-50.968h31.167zm354.97-32.996c-2.037-.238-2.941-.301-4.342-.301-11.041 0-16.634 3.787-16.634 11.268 0 4.611 2.726 7.545 6.983 7.545 7.94 0 13.664-7.559 13.993-18.512zm14.184 32.996h-16.146l.366-7.676c-4.926 6.065-11.5 8.949-20.422 8.949-10.565 0-17.8-8.25-17.8-20.229 0-18.024 12.588-28.541 34.213-28.541 2.208 0 5.037.199 7.934.57.604-2.441.763-3.488.763-4.801 0-4.908-3.392-6.737-12.496-6.737-9.533-.108-17.387 2.271-20.629 3.333.204-1.229 2.709-16.659 2.709-16.659 9.712-2.846 16.112-3.917 23.313-3.917 16.74 0 25.604 7.513 25.587 21.712.032 3.805-.597 8.5-1.579 14.671-1.684 10.734-5.321 33.721-5.813 39.325zm-220.39-1.125c-5.333 1.679-9.491 2.398-14 2.398-9.962 0-15.399-5.725-15.399-16.267-.142-3.271 1.433-11.88 2.671-19.737 1.125-6.917 8.449-50.529 8.449-50.529h19.371l-2.263 11.208h11.699l-2.642 17.796h-11.742c-2.25 14.083-5.454 31.625-5.491 33.95 0 3.816 2.037 5.483 6.671 5.483 2.221 0 3.94-.227 5.254-.7zm59.392-.6c-6.654 2.034-13.075 3.017-19.879 3-21.684-.021-32.987-11.346-32.987-33.032 0-25.313 14.38-43.947 33.899-43.947 15.971 0 26.171 10.433 26.171 26.796 0 5.429-.7 10.729-2.388 18.212h-38.574c-1.305 10.741 5.57 15.217 16.837 15.217 6.935 0 13.188-1.429 20.142-4.663zm-10.888-43.9c.107-1.543 2.055-13.217-9.013-13.217-6.171 0-10.583 4.704-12.38 13.217zm-123.42-5.017c0 9.367 4.542 15.826 14.842 20.676 7.892 3.709 9.112 4.81 9.112 8.17 0 4.617-3.479 6.701-11.191 6.701-5.813 0-11.221-.908-17.458-2.922 0 0-2.563 16.321-2.68 17.102 4.43.967 8.38 1.861 20.279 2.19 20.563 0 30.059-7.829 30.059-24.75 0-10.175-3.976-16.146-13.737-20.634-8.171-3.75-9.108-4.587-9.108-8.045 0-4.004 3.237-6.046 9.537-6.046 3.825 0 9.05.408 14 1.112l2.775-17.175c-5.046-.8-12.696-1.442-17.15-1.442-21.801.001-29.347 11.388-29.28 25.063m229.09-23.116c5.412 0 10.458 1.421 17.412 4.921l3.188-19.763c-2.854-1.121-12.904-7.7-21.417-7.7-13.041 0-24.065 6.471-31.82 17.15-11.309-3.746-15.958 3.825-21.657 11.367l-5.063 1.179c.383-2.483.729-4.95.612-7.446h-17.896c-2.445 22.917-6.778 46.128-10.171 69.075l-.884 4.976h19.496c3.254-21.143 5.037-34.68 6.121-43.842l7.341-4.084c1.097-4.078 4.529-5.458 11.417-5.291-.926 5.008-1.389 10.091-1.383 15.184 0 24.225 13.07 39.308 34.05 39.308 5.404 0 10.041-.712 17.221-2.658l3.43-20.759c-6.458 3.181-11.759 4.677-16.559 4.677-11.329 0-18.184-8.363-18.184-22.185 0-20.051 10.196-34.109 24.746-34.109"
    />
    <path
      d="m185.21 297.24h-19.491l11.171-69.988-24.926 69.988h-13.283l-1.642-69.588-11.733 69.588h-18.241l15.237-91.042h28.021l.788 56.362 18.904-56.362h30.267z"
      fill="#fff"
    />
    <path
      d="m647.52 211.6-4.321 26.309c-5.329-7.013-11.054-12.088-18.612-12.088-9.833 0-18.783 7.455-24.642 18.425-8.158-1.692-16.597-4.563-16.597-4.563l-.004.067c.658-6.134.921-9.875.862-11.146h-17.9c-2.438 22.917-6.771 46.128-10.157 69.075l-.893 4.976h19.492c2.633-17.096 4.648-31.291 6.133-42.551 6.658-6.016 9.992-11.266 16.721-10.916-2.979 7.205-4.725 15.503-4.725 24.017 0 18.513 9.366 30.725 23.533 30.725 7.142 0 12.621-2.462 17.967-8.171l-.913 6.884h18.435l14.842-91.042zm-24.371 73.941c-6.634 0-9.983-4.908-9.983-14.596 0-14.555 6.271-24.875 15.112-24.875 6.695 0 10.32 5.104 10.32 14.509.001 14.679-6.37 24.962-15.449 24.962z"
    />
    <path
      d="m233.19 264.26c-2.042-.236-2.946-.299-4.346-.299-11.046 0-16.634 3.787-16.634 11.266 0 4.604 2.729 7.547 6.979 7.547 7.947-.001 13.668-7.559 14.001-18.514zm14.178 32.984h-16.146l.367-7.663c-4.921 6.054-11.5 8.95-20.421 8.95-10.567 0-17.805-8.25-17.805-20.229 0-18.032 12.592-28.542 34.217-28.542 2.208 0 5.042.2 7.938.571.604-2.441.763-3.487.763-4.808 0-4.909-3.392-6.729-12.496-6.729-9.537-.108-17.396 2.271-20.629 3.321.204-1.225 2.7-16.637 2.7-16.637 9.708-2.858 16.12-3.929 23.32-3.929 16.737 0 25.604 7.517 25.588 21.704.029 3.821-.604 8.513-1.584 14.675-1.687 10.724-5.319 33.724-5.812 39.316zm261.38-88.592-3.191 19.767c-6.95-3.496-12-4.92-17.407-4.92-14.551 0-24.75 14.058-24.75 34.106 0 13.821 6.857 22.181 18.184 22.181 4.8 0 10.096-1.492 16.554-4.675l-3.421 20.75c-7.184 1.957-11.816 2.67-17.225 2.67-20.977 0-34.051-15.084-34.051-39.309 0-32.55 18.059-55.3 43.888-55.3 8.507.001 18.561 3.609 21.419 4.73m31.443 55.608c-2.041-.236-2.941-.299-4.347-.299-11.041 0-16.633 3.787-16.633 11.266 0 4.604 2.729 7.547 6.983 7.547 7.938-.001 13.663-7.559 13.997-18.514zm14.178 32.984h-16.15l.371-7.663c-4.925 6.054-11.5 8.95-20.421 8.95-10.563 0-17.804-8.25-17.804-20.229 0-18.032 12.596-28.542 34.212-28.542 2.213 0 5.042.2 7.941.571.601-2.441.763-3.487.763-4.808 0-4.909-3.393-6.729-12.495-6.729-9.533-.108-17.396 2.271-20.63 3.321.204-1.225 2.704-16.637 2.704-16.637 9.709-2.858 16.116-3.929 23.316-3.929 16.741 0 25.604 7.517 25.583 21.704.033 3.821-.596 8.513-1.579 14.675-1.682 10.724-5.323 33.724-5.811 39.316zm-220.39-1.121c-5.338 1.679-9.496 2.408-14 2.408-9.962 0-15.399-5.726-15.399-16.268-.138-3.279 1.438-11.88 2.675-19.736 1.12-6.926 8.445-50.534 8.445-50.534h19.368l-2.26 11.212h9.941l-2.646 17.788h-9.975c-2.25 14.092-5.463 31.62-5.496 33.95 0 3.83 2.041 5.482 6.671 5.482 2.221 0 3.938-.216 5.254-.691zm59.391-.592c-6.65 2.033-13.079 3.012-19.879 3-21.685-.021-32.987-11.346-32.987-33.033 0-25.321 14.379-43.95 33.899-43.95 15.971 0 26.171 10.429 26.171 26.8 0 5.434-.7 10.733-2.384 18.212h-38.574c-1.306 10.741 5.569 15.222 16.837 15.222 6.93 0 13.188-1.435 20.138-4.677zm-10.891-43.912c.116-1.538 2.06-13.217-9.013-13.217-6.167 0-10.579 4.717-12.375 13.217zm-123.42-5.005c0 9.367 4.542 15.818 14.842 20.675 7.892 3.709 9.112 4.812 9.112 8.172 0 4.616-3.483 6.699-11.188 6.699-5.816 0-11.225-.908-17.467-2.921 0 0-2.554 16.321-2.671 17.101 4.421.967 8.375 1.85 20.275 2.191 20.566 0 30.059-7.829 30.059-24.746 0-10.18-3.971-16.15-13.737-20.637-8.167-3.759-9.113-4.584-9.113-8.046 0-4 3.246-6.059 9.542-6.059 3.821 0 9.046.421 14.004 1.125l2.771-17.179c-5.042-.8-12.692-1.441-17.146-1.441-21.804 0-29.346 11.379-29.283 25.066m398.45 50.63h-18.438l.917-6.893c-5.347 5.717-10.825 8.18-17.968 8.18-14.166 0-23.528-12.213-23.528-30.726 0-24.63 14.521-45.392 31.708-45.392 7.559 0 13.279 3.087 18.604 10.096l4.325-26.308h19.221zm-28.746-17.109c9.075 0 15.45-10.283 15.45-24.953 0-9.405-3.629-14.509-10.325-14.509-8.837 0-15.115 10.315-15.115 24.875-.001 9.686 3.357 14.587 9.99 14.587zm-56.842-56.929c-2.441 22.917-6.773 46.13-10.162 69.063l-.892 4.976h19.491c6.972-45.275 8.658-54.117 19.588-53.009 1.742-9.267 4.982-17.383 7.399-21.479-8.163-1.7-12.721 2.913-18.688 11.675.471-3.788 1.333-7.467 1.162-11.225zm-160.42 0c-2.446 22.917-6.779 46.13-10.167 69.063l-.888 4.976h19.5c6.963-45.275 8.646-54.117 19.57-53.009 1.75-9.267 4.991-17.383 7.399-21.479-8.154-1.7-12.717 2.913-18.679 11.675.471-3.788 1.324-7.467 1.162-11.225zm254.57 68.241c-.004-3.199 2.586-5.795 5.784-5.799h.012c3.197-.004 5.793 2.586 5.796 5.783v.016c-.001 3.201-2.595 5.795-5.796 5.797-3.201-.002-5.795-2.596-5.796-5.797zm5.796 4.405c2.431.002 4.402-1.969 4.403-4.399v-.004c.003-2.433-1.968-4.406-4.399-4.408h-.004c-2.435.001-4.407 1.974-4.408 4.408.002 2.432 1.975 4.403 4.408 4.403zm-.784-1.871h-1.188v-5.082h2.153c.446 0 .909.009 1.296.254.417.283.654.767.654 1.274 0 .575-.337 1.112-.888 1.317l.941 2.236h-1.32l-.779-2.009h-.87zm0-2.879h.653c.246 0 .513.019.729-.1.196-.125.296-.361.296-.588-.009-.21-.114-.404-.287-.523-.204-.117-.542-.084-.763-.084h-.629z"
      fill="#fff"
    />
  </svg> `,
                }]
        }], ctorParameters: () => [] });

class UnionPaySVGComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UnionPaySVGComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: UnionPaySVGComponent, selector: "union-pay-svg", ngImport: i0, template: ` <svg enable-background="new 0 0 780 500" height="100%" viewBox="0 0 780 500" width="100%" xmlns="http://www.w3.org/2000/svg">
    <path
      d="m216.4 69.791h142.39c19.87 0 32.287 16.406 27.63 36.47l-66.333 287.48c-4.656 20.063-24.629 36.47-44.498 36.47h-142.39c-19.87 0-32.287-16.406-27.63-36.47l66.331-287.48c4.657-20.168 24.526-36.47 44.395-36.47h.104z"
      fill="#d10429"
    />
    <path
      d="m346.34 69.791h163.82c19.867 0 10.865 16.406 6.209 36.47l-66.334 287.48c-4.658 20.063-3.209 36.47-23.078 36.47h-163.81c-19.972 0-32.287-16.406-27.527-36.47l66.334-287.48c4.656-20.168 24.524-36.47 44.498-36.47h-.104z"
      fill="#022e64"
    />
    <path
      d="m504.41 69.791h142.39c19.869 0 32.287 16.406 27.631 36.47l-66.334 287.48c-4.656 20.063-24.629 36.47-44.498 36.47h-142.39c-19.973 0-32.288-16.406-27.631-36.47l66.334-287.48c4.656-20.168 24.525-36.47 44.393-36.47z"
      fill="#076f74"
    />
    <g fill="#fefefe">
      <path
        d="m480.5 340.81h13.453l3.828-13.063h-13.35zm10.762-35.95-4.658 15.467s5.072-2.613 7.865-3.449c2.795-.627 6.934-1.15 6.934-1.15l3.207-10.763h-13.451zm6.726-22.153-4.449 14.839s4.967-2.3 7.76-3.029c2.795-.732 6.934-.941 6.934-.941l3.207-10.764h-13.348zm29.7 0-17.385 57.997h4.656l-3.621 12.018h-4.658l-1.137 3.657h-16.559l1.139-3.657h-33.529l3.311-11.076h3.416l17.594-58.938 3.518-11.913h16.867l-1.76 5.956s4.449-3.239 8.797-4.39c4.244-1.148 28.666-1.566 28.666-1.566l-3.623 11.809h-5.795z"
      />
      <path
        d="m534.59 270.79h18.006l.207 6.792c-.102 1.149.828 1.672 3.002 1.672h3.621l-3.311 11.183h-9.729c-8.381.627-11.59-3.03-11.383-7.106l-.311-12.437zm2.217 53.2h-17.178l2.896-9.927h19.662l2.793-9.092h-19.35l3.311-11.182h53.812l-3.312 11.182h-18.109l-2.793 9.092h18.109l-3.002 9.927h-19.559l-3.518 4.18h7.969l1.965 12.54c.207 1.254.207 2.09.621 2.613.414.418 2.795.627 4.139.627h2.381l-3.725 12.227h-6.107c-.93 0-2.379-.104-4.346-.104-1.863-.21-3.104-1.255-4.346-1.882-1.139-.522-2.793-1.881-3.207-4.284l-1.863-12.54-8.9 12.331c-2.795 3.866-6.621 6.897-13.143 6.897h-12.417l3.311-10.869h4.762c1.346 0 2.588-.521 3.52-1.045.93-.418 1.758-.836 2.586-2.193zm-187.9-27.2h45.429l-3.312 10.973h-18.11l-2.793 9.299h18.627l-3.415 11.287h-18.524l-4.553 15.152c-.517 1.672 4.45 1.881 6.209 1.881l9.313-1.254-3.726 12.54h-20.904c-1.654 0-2.896-.209-4.76-.627-1.76-.418-2.587-1.254-3.311-2.403-.726-1.254-1.968-2.195-1.14-4.912l6.002-20.063h-10.347l3.415-11.495h10.348l2.794-9.3h-10.347l3.312-10.974zm31.387-19.835h18.626l-3.414 11.39h-25.458l-2.794 2.404c-1.242 1.15-1.552.732-3.105 1.568-1.447.73-4.449 2.193-8.382 2.193h-8.175l3.311-10.972h2.484c2.07 0 3.52-.21 4.243-.627.828-.522 1.76-1.672 2.69-3.554l4.656-8.568h18.526l-3.208 6.27zm35.106 18.81s5.07-4.701 13.764-6.164c1.967-.418 14.385-.211 14.385-.211l1.863-6.27h-26.182l-3.83 12.75zm24.629 4.807h-25.975l-1.551 5.329h22.559c2.691-.313 3.209.104 3.416-.104l1.654-5.225zm-33.734-29.678h15.832l-2.275 8.047s4.967-4.075 8.484-5.539c3.52-1.254 11.383-2.508 11.383-2.508l25.664-.104-8.795 29.469c-1.449 5.016-3.209 8.256-4.244 9.823-.93 1.463-2.07 2.821-4.346 4.075-2.172 1.15-4.141 1.881-6.002 1.986-1.656.104-4.346.209-7.865.209h-24.732l-6.934 23.303c-.619 2.299-.93 3.447-.516 4.074.309.523 1.24 1.15 2.379 1.15l10.865-1.045-3.725 12.749h-12.211c-3.932 0-6.727-.104-8.693-.21-1.862-.208-3.83 0-5.174-1.044-1.138-1.045-2.896-2.403-2.794-3.763.104-1.254.621-3.344 1.45-6.27z"
      />
      <path
        d="m452.43 317.79-1.449 7.105c-.619 2.194-1.137 3.867-2.793 5.33-1.76 1.463-3.725 3.03-8.484 3.03l-8.797.418-.104 7.942c-.104 2.193.518 1.984.828 2.402.414.418.723.523 1.137.732l2.795-.21 8.383-.417-3.52 11.704h-9.623c-6.727 0-11.797-.21-13.35-1.463-1.656-1.046-1.863-2.3-1.863-4.599l.621-31.141h15.42l-.207 6.374h3.725c1.242 0 2.174-.104 2.691-.418.516-.313.828-.836 1.035-1.567l1.551-5.016h12.109zm-219.37-156c-.517 2.508-10.451 48.592-10.451 48.592-2.174 9.3-3.726 15.989-8.9 20.273-3.001 2.508-6.52 3.657-10.555 3.657-6.52 0-10.245-3.239-10.866-9.404l-.104-2.09s1.966-12.436 1.966-12.54c0 0 10.349-42.009 12.212-47.548.103-.313.103-.522.103-.627-20.18.21-23.801 0-24.008-.313-.104.418-.621 3.03-.621 3.03l-10.556 47.34-.932 3.97-1.758 13.168c0 3.866.724 7.105 2.277 9.718 4.863 8.569 18.627 9.823 26.388 9.823 10.038 0 19.455-2.195 25.767-6.061 11.073-6.584 13.97-16.929 16.454-26.02l1.242-4.703s10.659-43.576 12.522-49.219c.103-.314.103-.523.207-.627-14.695.104-18.938 0-20.387-.314zm59.029 86.623c-7.141-.105-9.728-.105-18.11.313l-.311-.627c.724-3.24 1.552-6.374 2.173-9.614l1.035-4.389c1.552-6.792 3.001-14.839 3.208-17.242.207-1.463.62-5.12-3.519-5.12-1.759 0-3.518.835-5.38 1.671-1.036 3.658-3.002 13.899-4.037 18.497-2.07 9.823-2.173 10.972-3.104 15.78l-.621.626c-7.347-.104-9.934-.104-18.42.314l-.414-.732c1.449-5.852 2.794-11.704 4.14-17.556 3.518-15.78 4.45-21.84 5.38-29.887l.725-.418c8.279-1.149 10.245-1.463 19.248-3.239l.724.836-1.345 5.016c1.552-.94 3.001-1.881 4.553-2.613 4.243-2.09 8.9-2.717 11.487-2.717 3.932 0 8.279 1.15 10.038 5.748 1.656 4.075.62 9.091-1.656 19.019l-1.138 5.016c-2.277 11.077-2.69 13.062-3.933 20.586l-.827.627zm29.058.027c-4.346 0-7.14-.104-9.83 0-2.691 0-5.278.21-9.314.314l-.207-.314-.207-.418c1.138-4.18 1.656-5.643 2.277-7.106.517-1.463 1.034-2.926 2.07-7.21 1.241-5.539 2.069-9.405 2.586-12.854.621-3.24.932-6.06 1.346-9.3l.31-.209.31-.313c4.347-.627 7.038-1.045 9.832-1.463s5.691-.94 10.141-1.776l.207.418.103.418-2.482 10.345c-.828 3.449-1.656 6.897-2.38 10.346-1.554 7.315-2.277 10.032-2.587 12.017-.414 1.881-.519 2.822-1.14 6.584l-.414.313-.414.314zm45.941-25.675c-.31 1.881-1.966 8.883-4.139 11.809-1.553 2.194-3.312 3.553-5.382 3.553-.62 0-4.14 0-4.242-5.33 0-2.612.517-5.33 1.138-8.255 1.863-8.465 4.14-15.466 9.831-15.466 4.451 0 4.76 5.225 2.794 13.689zm18.731.836c2.483-11.077.518-16.302-1.862-19.437-3.726-4.807-10.348-6.374-17.178-6.374-4.141 0-13.867.418-21.525 7.524-5.484 5.12-8.071 12.122-9.52 18.81-1.554 6.792-3.312 19.019 7.864 23.617 3.414 1.463 8.382 1.88 11.59 1.88 8.176 0 16.558-2.298 22.87-8.986 4.863-5.434 7.036-13.585 7.864-17.034zm174.43 26.08c-8.693-.104-11.176-.104-19.145.314l-.518-.627c2.174-8.256 4.346-16.616 6.312-24.976 2.484-10.868 3.105-15.466 3.934-21.84l.619-.522c8.59-1.254 10.971-1.567 19.973-3.239l.207.731c-1.656 6.897-3.207 13.69-4.863 20.482-3.311 14.317-4.451 21.632-5.691 29.156l-.828.627z"
      />
      <path
        d="m547.75 224.16c-.414 1.776-2.07 8.882-4.242 11.808-1.449 2.09-4.967 3.449-6.934 3.449-.621 0-4.035 0-4.242-5.225 0-2.613.516-5.33 1.137-8.256 1.863-8.255 4.141-15.257 9.832-15.257 4.449 0 6.416 5.12 4.449 13.585zm17.076.836c2.482-11.077-7.658-.94-9.211-4.598-2.484-5.748-.932-17.243-10.865-21.109-3.83-1.568-12.832.418-20.49 7.524-5.381 5.016-8.072 12.017-9.52 18.705-1.555 6.688-3.312 19.02 7.76 23.304 3.52 1.567 6.727 1.985 9.934 1.776 11.178-.627 19.662-17.661 25.977-24.349 4.86-5.329 5.689 1.986 6.415-1.253zm-129.94 23.413c-7.141-.105-9.625-.105-18.006.313l-.311-.627c.725-3.24 1.553-6.374 2.275-9.614l.932-4.389c1.553-6.792 3.105-14.839 3.207-17.242.207-1.463.621-5.12-3.414-5.12-1.76 0-3.621.835-5.381 1.671-.932 3.658-3.002 13.899-4.037 18.497-1.965 9.823-2.172 10.972-3.104 15.78l-.621.626c-7.346-.104-9.934-.104-18.419.314l-.414-.732c1.449-5.852 2.794-11.704 4.14-17.556 3.518-15.78 4.346-21.84 5.379-29.887l.621-.418c8.281-1.149 10.35-1.463 19.248-3.239l.727.836-1.242 5.016c1.449-.94 3-1.881 4.449-2.613 4.244-2.09 8.9-2.717 11.486-2.717 3.934 0 8.176 1.15 10.037 5.748 1.656 4.075.52 9.091-1.758 19.019l-1.139 5.016c-2.379 11.077-2.689 13.062-3.934 20.586l-.826.627zm62-86.519-6.002.105c-15.523.209-21.732.104-24.215-.209-.207 1.15-.621 3.135-.621 3.135s-5.588 25.916-5.588 26.02c0 0-13.246 55.176-13.867 57.788 13.557-.209 19.041-.209 21.422.105.518-2.613 3.621-17.974 3.725-17.974 0 0 2.691-11.286 2.795-11.704 0 0 .826-1.15 1.654-1.672h1.242c11.695 0 24.836 0 35.186-7.628 7.037-5.225 11.797-13.063 13.971-22.468.516-2.299.93-5.016.93-7.837 0-3.658-.723-7.21-2.793-10.032-5.279-7.42-15.732-7.524-27.839-7.629zm7.762 27.066c-1.242 5.747-4.967 10.659-9.727 12.958-3.934 1.985-8.693 2.194-13.66 2.194h-3.209l.207-1.254s5.9-25.916 5.9-25.811l.205-1.359.104-1.045 2.381.21s12.211 1.044 12.418 1.044c4.759 1.881 6.83 6.688 5.381 13.063zm127.21 8.666-.725-.836c-8.795 1.776-10.451 2.09-18.523 3.24l-.621.626c0 .105-.104.21-.104.418v-.104c-6.002 14.107-5.898 11.077-10.762 22.154 0-.523 0-.836-.104-1.359l-1.242-24.035-.725-.836c-9.314 1.777-9.52 2.09-18.006 3.24l-.621.627c-.104.313-.104.627-.104.94l.104.105c1.035 5.538.828 4.284 1.863 12.958.518 4.284 1.139 8.569 1.654 12.749.828 7.106 1.348 10.554 2.381 21.318-5.795 9.613-7.141 13.271-12.729 21.734l.311.836c8.383-.312 10.244-.312 16.453-.312l1.348-1.568c4.654-10.135 40.254-71.79 40.254-71.79zm-302.72 6.922c4.76-3.344 5.38-7.942 1.345-10.345-4.036-2.404-11.176-1.672-15.937 1.672-4.76 3.24-5.277 7.837-1.241 10.345 3.932 2.3 11.073 1.672 15.833-1.672z"
      />
      <path
        d="m590.33 270.9-6.936 12.019c-2.172 4.075-6.311 7.21-12.727 7.21l-11.074-.209 3.209-10.868h2.172c1.139 0 1.967-.104 2.588-.418.621-.209.932-.627 1.449-1.254l4.139-6.583h17.281z"
      />
    </g>
  </svg>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UnionPaySVGComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'union-pay-svg',
                    template: ` <svg enable-background="new 0 0 780 500" height="100%" viewBox="0 0 780 500" width="100%" xmlns="http://www.w3.org/2000/svg">
    <path
      d="m216.4 69.791h142.39c19.87 0 32.287 16.406 27.63 36.47l-66.333 287.48c-4.656 20.063-24.629 36.47-44.498 36.47h-142.39c-19.87 0-32.287-16.406-27.63-36.47l66.331-287.48c4.657-20.168 24.526-36.47 44.395-36.47h.104z"
      fill="#d10429"
    />
    <path
      d="m346.34 69.791h163.82c19.867 0 10.865 16.406 6.209 36.47l-66.334 287.48c-4.658 20.063-3.209 36.47-23.078 36.47h-163.81c-19.972 0-32.287-16.406-27.527-36.47l66.334-287.48c4.656-20.168 24.524-36.47 44.498-36.47h-.104z"
      fill="#022e64"
    />
    <path
      d="m504.41 69.791h142.39c19.869 0 32.287 16.406 27.631 36.47l-66.334 287.48c-4.656 20.063-24.629 36.47-44.498 36.47h-142.39c-19.973 0-32.288-16.406-27.631-36.47l66.334-287.48c4.656-20.168 24.525-36.47 44.393-36.47z"
      fill="#076f74"
    />
    <g fill="#fefefe">
      <path
        d="m480.5 340.81h13.453l3.828-13.063h-13.35zm10.762-35.95-4.658 15.467s5.072-2.613 7.865-3.449c2.795-.627 6.934-1.15 6.934-1.15l3.207-10.763h-13.451zm6.726-22.153-4.449 14.839s4.967-2.3 7.76-3.029c2.795-.732 6.934-.941 6.934-.941l3.207-10.764h-13.348zm29.7 0-17.385 57.997h4.656l-3.621 12.018h-4.658l-1.137 3.657h-16.559l1.139-3.657h-33.529l3.311-11.076h3.416l17.594-58.938 3.518-11.913h16.867l-1.76 5.956s4.449-3.239 8.797-4.39c4.244-1.148 28.666-1.566 28.666-1.566l-3.623 11.809h-5.795z"
      />
      <path
        d="m534.59 270.79h18.006l.207 6.792c-.102 1.149.828 1.672 3.002 1.672h3.621l-3.311 11.183h-9.729c-8.381.627-11.59-3.03-11.383-7.106l-.311-12.437zm2.217 53.2h-17.178l2.896-9.927h19.662l2.793-9.092h-19.35l3.311-11.182h53.812l-3.312 11.182h-18.109l-2.793 9.092h18.109l-3.002 9.927h-19.559l-3.518 4.18h7.969l1.965 12.54c.207 1.254.207 2.09.621 2.613.414.418 2.795.627 4.139.627h2.381l-3.725 12.227h-6.107c-.93 0-2.379-.104-4.346-.104-1.863-.21-3.104-1.255-4.346-1.882-1.139-.522-2.793-1.881-3.207-4.284l-1.863-12.54-8.9 12.331c-2.795 3.866-6.621 6.897-13.143 6.897h-12.417l3.311-10.869h4.762c1.346 0 2.588-.521 3.52-1.045.93-.418 1.758-.836 2.586-2.193zm-187.9-27.2h45.429l-3.312 10.973h-18.11l-2.793 9.299h18.627l-3.415 11.287h-18.524l-4.553 15.152c-.517 1.672 4.45 1.881 6.209 1.881l9.313-1.254-3.726 12.54h-20.904c-1.654 0-2.896-.209-4.76-.627-1.76-.418-2.587-1.254-3.311-2.403-.726-1.254-1.968-2.195-1.14-4.912l6.002-20.063h-10.347l3.415-11.495h10.348l2.794-9.3h-10.347l3.312-10.974zm31.387-19.835h18.626l-3.414 11.39h-25.458l-2.794 2.404c-1.242 1.15-1.552.732-3.105 1.568-1.447.73-4.449 2.193-8.382 2.193h-8.175l3.311-10.972h2.484c2.07 0 3.52-.21 4.243-.627.828-.522 1.76-1.672 2.69-3.554l4.656-8.568h18.526l-3.208 6.27zm35.106 18.81s5.07-4.701 13.764-6.164c1.967-.418 14.385-.211 14.385-.211l1.863-6.27h-26.182l-3.83 12.75zm24.629 4.807h-25.975l-1.551 5.329h22.559c2.691-.313 3.209.104 3.416-.104l1.654-5.225zm-33.734-29.678h15.832l-2.275 8.047s4.967-4.075 8.484-5.539c3.52-1.254 11.383-2.508 11.383-2.508l25.664-.104-8.795 29.469c-1.449 5.016-3.209 8.256-4.244 9.823-.93 1.463-2.07 2.821-4.346 4.075-2.172 1.15-4.141 1.881-6.002 1.986-1.656.104-4.346.209-7.865.209h-24.732l-6.934 23.303c-.619 2.299-.93 3.447-.516 4.074.309.523 1.24 1.15 2.379 1.15l10.865-1.045-3.725 12.749h-12.211c-3.932 0-6.727-.104-8.693-.21-1.862-.208-3.83 0-5.174-1.044-1.138-1.045-2.896-2.403-2.794-3.763.104-1.254.621-3.344 1.45-6.27z"
      />
      <path
        d="m452.43 317.79-1.449 7.105c-.619 2.194-1.137 3.867-2.793 5.33-1.76 1.463-3.725 3.03-8.484 3.03l-8.797.418-.104 7.942c-.104 2.193.518 1.984.828 2.402.414.418.723.523 1.137.732l2.795-.21 8.383-.417-3.52 11.704h-9.623c-6.727 0-11.797-.21-13.35-1.463-1.656-1.046-1.863-2.3-1.863-4.599l.621-31.141h15.42l-.207 6.374h3.725c1.242 0 2.174-.104 2.691-.418.516-.313.828-.836 1.035-1.567l1.551-5.016h12.109zm-219.37-156c-.517 2.508-10.451 48.592-10.451 48.592-2.174 9.3-3.726 15.989-8.9 20.273-3.001 2.508-6.52 3.657-10.555 3.657-6.52 0-10.245-3.239-10.866-9.404l-.104-2.09s1.966-12.436 1.966-12.54c0 0 10.349-42.009 12.212-47.548.103-.313.103-.522.103-.627-20.18.21-23.801 0-24.008-.313-.104.418-.621 3.03-.621 3.03l-10.556 47.34-.932 3.97-1.758 13.168c0 3.866.724 7.105 2.277 9.718 4.863 8.569 18.627 9.823 26.388 9.823 10.038 0 19.455-2.195 25.767-6.061 11.073-6.584 13.97-16.929 16.454-26.02l1.242-4.703s10.659-43.576 12.522-49.219c.103-.314.103-.523.207-.627-14.695.104-18.938 0-20.387-.314zm59.029 86.623c-7.141-.105-9.728-.105-18.11.313l-.311-.627c.724-3.24 1.552-6.374 2.173-9.614l1.035-4.389c1.552-6.792 3.001-14.839 3.208-17.242.207-1.463.62-5.12-3.519-5.12-1.759 0-3.518.835-5.38 1.671-1.036 3.658-3.002 13.899-4.037 18.497-2.07 9.823-2.173 10.972-3.104 15.78l-.621.626c-7.347-.104-9.934-.104-18.42.314l-.414-.732c1.449-5.852 2.794-11.704 4.14-17.556 3.518-15.78 4.45-21.84 5.38-29.887l.725-.418c8.279-1.149 10.245-1.463 19.248-3.239l.724.836-1.345 5.016c1.552-.94 3.001-1.881 4.553-2.613 4.243-2.09 8.9-2.717 11.487-2.717 3.932 0 8.279 1.15 10.038 5.748 1.656 4.075.62 9.091-1.656 19.019l-1.138 5.016c-2.277 11.077-2.69 13.062-3.933 20.586l-.827.627zm29.058.027c-4.346 0-7.14-.104-9.83 0-2.691 0-5.278.21-9.314.314l-.207-.314-.207-.418c1.138-4.18 1.656-5.643 2.277-7.106.517-1.463 1.034-2.926 2.07-7.21 1.241-5.539 2.069-9.405 2.586-12.854.621-3.24.932-6.06 1.346-9.3l.31-.209.31-.313c4.347-.627 7.038-1.045 9.832-1.463s5.691-.94 10.141-1.776l.207.418.103.418-2.482 10.345c-.828 3.449-1.656 6.897-2.38 10.346-1.554 7.315-2.277 10.032-2.587 12.017-.414 1.881-.519 2.822-1.14 6.584l-.414.313-.414.314zm45.941-25.675c-.31 1.881-1.966 8.883-4.139 11.809-1.553 2.194-3.312 3.553-5.382 3.553-.62 0-4.14 0-4.242-5.33 0-2.612.517-5.33 1.138-8.255 1.863-8.465 4.14-15.466 9.831-15.466 4.451 0 4.76 5.225 2.794 13.689zm18.731.836c2.483-11.077.518-16.302-1.862-19.437-3.726-4.807-10.348-6.374-17.178-6.374-4.141 0-13.867.418-21.525 7.524-5.484 5.12-8.071 12.122-9.52 18.81-1.554 6.792-3.312 19.019 7.864 23.617 3.414 1.463 8.382 1.88 11.59 1.88 8.176 0 16.558-2.298 22.87-8.986 4.863-5.434 7.036-13.585 7.864-17.034zm174.43 26.08c-8.693-.104-11.176-.104-19.145.314l-.518-.627c2.174-8.256 4.346-16.616 6.312-24.976 2.484-10.868 3.105-15.466 3.934-21.84l.619-.522c8.59-1.254 10.971-1.567 19.973-3.239l.207.731c-1.656 6.897-3.207 13.69-4.863 20.482-3.311 14.317-4.451 21.632-5.691 29.156l-.828.627z"
      />
      <path
        d="m547.75 224.16c-.414 1.776-2.07 8.882-4.242 11.808-1.449 2.09-4.967 3.449-6.934 3.449-.621 0-4.035 0-4.242-5.225 0-2.613.516-5.33 1.137-8.256 1.863-8.255 4.141-15.257 9.832-15.257 4.449 0 6.416 5.12 4.449 13.585zm17.076.836c2.482-11.077-7.658-.94-9.211-4.598-2.484-5.748-.932-17.243-10.865-21.109-3.83-1.568-12.832.418-20.49 7.524-5.381 5.016-8.072 12.017-9.52 18.705-1.555 6.688-3.312 19.02 7.76 23.304 3.52 1.567 6.727 1.985 9.934 1.776 11.178-.627 19.662-17.661 25.977-24.349 4.86-5.329 5.689 1.986 6.415-1.253zm-129.94 23.413c-7.141-.105-9.625-.105-18.006.313l-.311-.627c.725-3.24 1.553-6.374 2.275-9.614l.932-4.389c1.553-6.792 3.105-14.839 3.207-17.242.207-1.463.621-5.12-3.414-5.12-1.76 0-3.621.835-5.381 1.671-.932 3.658-3.002 13.899-4.037 18.497-1.965 9.823-2.172 10.972-3.104 15.78l-.621.626c-7.346-.104-9.934-.104-18.419.314l-.414-.732c1.449-5.852 2.794-11.704 4.14-17.556 3.518-15.78 4.346-21.84 5.379-29.887l.621-.418c8.281-1.149 10.35-1.463 19.248-3.239l.727.836-1.242 5.016c1.449-.94 3-1.881 4.449-2.613 4.244-2.09 8.9-2.717 11.486-2.717 3.934 0 8.176 1.15 10.037 5.748 1.656 4.075.52 9.091-1.758 19.019l-1.139 5.016c-2.379 11.077-2.689 13.062-3.934 20.586l-.826.627zm62-86.519-6.002.105c-15.523.209-21.732.104-24.215-.209-.207 1.15-.621 3.135-.621 3.135s-5.588 25.916-5.588 26.02c0 0-13.246 55.176-13.867 57.788 13.557-.209 19.041-.209 21.422.105.518-2.613 3.621-17.974 3.725-17.974 0 0 2.691-11.286 2.795-11.704 0 0 .826-1.15 1.654-1.672h1.242c11.695 0 24.836 0 35.186-7.628 7.037-5.225 11.797-13.063 13.971-22.468.516-2.299.93-5.016.93-7.837 0-3.658-.723-7.21-2.793-10.032-5.279-7.42-15.732-7.524-27.839-7.629zm7.762 27.066c-1.242 5.747-4.967 10.659-9.727 12.958-3.934 1.985-8.693 2.194-13.66 2.194h-3.209l.207-1.254s5.9-25.916 5.9-25.811l.205-1.359.104-1.045 2.381.21s12.211 1.044 12.418 1.044c4.759 1.881 6.83 6.688 5.381 13.063zm127.21 8.666-.725-.836c-8.795 1.776-10.451 2.09-18.523 3.24l-.621.626c0 .105-.104.21-.104.418v-.104c-6.002 14.107-5.898 11.077-10.762 22.154 0-.523 0-.836-.104-1.359l-1.242-24.035-.725-.836c-9.314 1.777-9.52 2.09-18.006 3.24l-.621.627c-.104.313-.104.627-.104.94l.104.105c1.035 5.538.828 4.284 1.863 12.958.518 4.284 1.139 8.569 1.654 12.749.828 7.106 1.348 10.554 2.381 21.318-5.795 9.613-7.141 13.271-12.729 21.734l.311.836c8.383-.312 10.244-.312 16.453-.312l1.348-1.568c4.654-10.135 40.254-71.79 40.254-71.79zm-302.72 6.922c4.76-3.344 5.38-7.942 1.345-10.345-4.036-2.404-11.176-1.672-15.937 1.672-4.76 3.24-5.277 7.837-1.241 10.345 3.932 2.3 11.073 1.672 15.833-1.672z"
      />
      <path
        d="m590.33 270.9-6.936 12.019c-2.172 4.075-6.311 7.21-12.727 7.21l-11.074-.209 3.209-10.868h2.172c1.139 0 1.967-.104 2.588-.418.621-.209.932-.627 1.449-1.254l4.139-6.583h17.281z"
      />
    </g>
  </svg>`,
                }]
        }], ctorParameters: () => [] });

class VisaSVGComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: VisaSVGComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: VisaSVGComponent, selector: "visa-svg", ngImport: i0, template: ` <svg enable-background="new 0 0 780 500" height="90%" viewBox="0 0 780 500" width="90%" xmlns="http://www.w3.org/2000/svg">
    <path
      d="m40 0h700c22.092 0 40 17.909 40 40v420c0 22.092-17.908 40-40 40h-700c-22.091 0-40-17.908-40-40v-420c0-22.091 17.909-40 40-40z"
      fill="#0e4595"
    />
    <path
      d="m293.2 348.73 33.361-195.76h53.36l-33.385 195.76zm246.11-191.54c-10.57-3.966-27.137-8.222-47.822-8.222-52.725 0-89.865 26.55-90.18 64.603-.299 28.13 26.514 43.822 46.752 53.186 20.771 9.595 27.752 15.714 27.654 24.283-.131 13.121-16.586 19.116-31.922 19.116-21.357 0-32.703-2.967-50.227-10.276l-6.876-3.11-7.489 43.823c12.463 5.464 35.51 10.198 59.438 10.443 56.09 0 92.5-26.246 92.916-66.882.199-22.269-14.016-39.216-44.801-53.188-18.65-9.055-30.072-15.099-29.951-24.268 0-8.137 9.668-16.839 30.557-16.839 17.449-.27 30.09 3.535 39.938 7.5l4.781 2.26zm137.31-4.223h-41.232c-12.773 0-22.332 3.487-27.941 16.234l-79.244 179.4h56.031s9.16-24.123 11.232-29.418c6.125 0 60.555.084 68.338.084 1.596 6.853 6.49 29.334 6.49 29.334h49.514l-43.188-195.64zm-65.418 126.41c4.412-11.279 21.26-54.723 21.26-54.723-.316.522 4.379-11.334 7.074-18.684l3.605 16.879s10.219 46.729 12.354 56.528zm-363.3-126.41-52.24 133.5-5.567-27.13c-9.725-31.273-40.025-65.155-73.898-82.118l47.766 171.2 56.456-.064 84.004-195.39h-56.521"
      fill="#fff"
    />
    <path
      d="m146.92 152.96h-86.041l-.681 4.073c66.938 16.204 111.23 55.363 129.62 102.41l-18.71-89.96c-3.23-12.395-12.597-16.094-24.186-16.527"
      fill="#f2ae14"
    />
  </svg>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: VisaSVGComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'visa-svg',
                    template: ` <svg enable-background="new 0 0 780 500" height="90%" viewBox="0 0 780 500" width="90%" xmlns="http://www.w3.org/2000/svg">
    <path
      d="m40 0h700c22.092 0 40 17.909 40 40v420c0 22.092-17.908 40-40 40h-700c-22.091 0-40-17.908-40-40v-420c0-22.091 17.909-40 40-40z"
      fill="#0e4595"
    />
    <path
      d="m293.2 348.73 33.361-195.76h53.36l-33.385 195.76zm246.11-191.54c-10.57-3.966-27.137-8.222-47.822-8.222-52.725 0-89.865 26.55-90.18 64.603-.299 28.13 26.514 43.822 46.752 53.186 20.771 9.595 27.752 15.714 27.654 24.283-.131 13.121-16.586 19.116-31.922 19.116-21.357 0-32.703-2.967-50.227-10.276l-6.876-3.11-7.489 43.823c12.463 5.464 35.51 10.198 59.438 10.443 56.09 0 92.5-26.246 92.916-66.882.199-22.269-14.016-39.216-44.801-53.188-18.65-9.055-30.072-15.099-29.951-24.268 0-8.137 9.668-16.839 30.557-16.839 17.449-.27 30.09 3.535 39.938 7.5l4.781 2.26zm137.31-4.223h-41.232c-12.773 0-22.332 3.487-27.941 16.234l-79.244 179.4h56.031s9.16-24.123 11.232-29.418c6.125 0 60.555.084 68.338.084 1.596 6.853 6.49 29.334 6.49 29.334h49.514l-43.188-195.64zm-65.418 126.41c4.412-11.279 21.26-54.723 21.26-54.723-.316.522 4.379-11.334 7.074-18.684l3.605 16.879s10.219 46.729 12.354 56.528zm-363.3-126.41-52.24 133.5-5.567-27.13c-9.725-31.273-40.025-65.155-73.898-82.118l47.766 171.2 56.456-.064 84.004-195.39h-56.521"
      fill="#fff"
    />
    <path
      d="m146.92 152.96h-86.041l-.681 4.073c66.938 16.204 111.23 55.363 129.62 102.41l-18.71-89.96c-3.23-12.395-12.597-16.094-24.186-16.527"
      fill="#f2ae14"
    />
  </svg>`,
                }]
        }], ctorParameters: () => [] });

class CreditCardIconComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: CreditCardIconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: CreditCardIconComponent, selector: "credit-card-icon", inputs: { cardType: "cardType" }, ngImport: i0, template: `
    <div [ngSwitch]="cardType">
      <amex-svg *ngSwitchCase="'amex'"></amex-svg>
      <discover-svg *ngSwitchCase="'discover'"></discover-svg>
      <jcb-svg *ngSwitchCase="'jcb'"></jcb-svg>
      <master-card-svg *ngSwitchCase="'mastercard'"></master-card-svg>
      <union-pay-svg *ngSwitchCase="'unionpay'"></union-pay-svg>
      <visa-svg *ngSwitchCase="'visa'"></visa-svg>
      <div *ngSwitchDefault></div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: i1.NgSwitchDefault, selector: "[ngSwitchDefault]" }, { kind: "component", type: AmexSVGComponent, selector: "amex-svg" }, { kind: "component", type: DiscoverSVGComponent, selector: "discover-svg" }, { kind: "component", type: JcbSVGComponent, selector: "jcb-svg" }, { kind: "component", type: MasterCardSVGComponent, selector: "master-card-svg" }, { kind: "component", type: UnionPaySVGComponent, selector: "union-pay-svg" }, { kind: "component", type: VisaSVGComponent, selector: "visa-svg" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: CreditCardIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'credit-card-icon',
                    template: `
    <div [ngSwitch]="cardType">
      <amex-svg *ngSwitchCase="'amex'"></amex-svg>
      <discover-svg *ngSwitchCase="'discover'"></discover-svg>
      <jcb-svg *ngSwitchCase="'jcb'"></jcb-svg>
      <master-card-svg *ngSwitchCase="'mastercard'"></master-card-svg>
      <union-pay-svg *ngSwitchCase="'unionpay'"></union-pay-svg>
      <visa-svg *ngSwitchCase="'visa'"></visa-svg>
      <div *ngSwitchDefault></div>
    </div>
  `,
                }]
        }], ctorParameters: () => [], propDecorators: { cardType: [{
                type: Input
            }] } });

class SdkPaymentCardComponent {
    constructor(paymentStore) {
        this.paymentStore = paymentStore;
        this.isLoading = true;
    }
    onDelete() {
        this.isLoading = true;
        this.paymentStore.removePaymentMethod(this.paymentCard.id);
    }
    onSetDefault() {
        this.isLoading = true;
        this.paymentStore.setDefaultPaymentMethod(this.paymentCard.id);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkPaymentCardComponent, deps: [{ token: PaymentStore }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkPaymentCardComponent, selector: "sdk-payment-card", inputs: { paymentCard: "paymentCard", isLoading: "isLoading" }, ngImport: i0, template: `<div
    class="e11-overflow-hidden e11-group e11-w-64 sm:e11-w-72 md:e11-w-96 e11-h-44 md:e11-h-56 e11-m-auto e11-bg-red-100 e11-rounded-xl e11-relative e11-text-white e11-shadow-2xl e11-transition-transform e11-transform hover:e11-scale-110"
  >
    <div
      class="e11-relative e11-object-cover e11-w-full e11-h-full e11-rounded-xl e11-bg-gradient-to-r e11-from-blue-600 e11-to-blue-500"
      *ngIf="!isLoading"
    ></div>
    <div class="e11-relative e11-rounded-xl e11-skeleton-empty !e11-h-full !e11-w-full" *ngIf="isLoading"></div>
    <div class="ring e11-absolute"></div>
    <div class="e11-w-full e11-px-8 e11-absolute e11-top-8">
      <div class="e11-flex e11-justify-between">
        <div>
          <p class="e11-font-light e11-mb-0" [ngClass]="{ 'e11-text-skin-grey ': isLoading }">{{ 'Name' | translate }}</p>
          <p class="e11-font-semibold e11-mb-0 e11-tracking-widest" *ngIf="!isLoading">{{ paymentCard.billing_details.name }}</p>
        </div>
        <credit-card-icon class="e11-w-14 e11-h-14" [cardType]="paymentCard.card?.brand" *ngIf="!isLoading"></credit-card-icon>
      </div>
      <div class="e11-pt-1">
        <p class="e11-font-light e11-mb-0" [ngClass]="{ 'e11-text-skin-grey ': isLoading }">{{ 'Card Number' | translate }}</p>
        <p class="e11-font-semibold e11-mb-0 e11-tracking-more-wider" *ngIf="!isLoading">**** **** **** {{ paymentCard.card?.last4 || '----' }}</p>
      </div>
      <div class="e11-hidden md:e11-block e11-pt-6 e11-pr-6">
        <div class="e11-flex e11-justify-between">
          <div>
            <p class="e11-font-light e11-mb-0 e11-text-xs" [ngClass]="{ 'e11-text-skin-grey ': isLoading }">{{ 'Expiry' | translate }}</p>
            <p class="e11-font-semibold e11-mb-0 e11-tracking-wider e11-text-sm" *ngIf="!isLoading">
              {{ paymentCard.card?.exp_month }}/{{ paymentCard.card?.exp_year }}
            </p>
          </div>
          <div></div>
          <div>
            <p class="e11-font-light e11-mb-0 e11-text-xs" [ngClass]="{ 'e11-text-skin-grey ': isLoading }">{{ 'CVV' | translate }}</p>
            <p class="e11-font-semibold e11-mb-0 e11-tracking-more-wider e11-text-sm" *ngIf="!isLoading">···</p>
          </div>
        </div>
      </div>
    </div>
    <div class="e11-absolute e11-right-0 e11-top-0">
      <e11-overflow
        class="e11-p-4 e11-hidden group-hover:e11-block"
        iconClass="!e11-text-skin-white"
        *ngIf="!isLoading"
        [closeOnMouseOut]="true"
        [menuMaxWidth]="160"
        [menuPosition]="'bottom left'"
      >
        <li
          class="e11-py-1 e11-px-4 e11-text-sm hover:e11-bg-skin-grey/20 e11-text-skin-black e11-cursor-pointer e11-font-primary-medium"
          custom-option
          (click)="onSetDefault()"
        >
          {{ 'Set as default' | translate }}
        </li>

        <li
          class="e11-py-1 e11-px-4 e11-text-sm hover:e11-bg-skin-grey/20 e11-text-skin-black e11-cursor-pointer e11-font-primary-medium"
          custom-option
          e11ConfirmationBox
          [confirmationBoxContent]="confirmationBoxText"
          [componentInput]=" {
          confirmText: 'Delete' | translate,
          cancelText: 'Cancel' | translate,
          confirmValueWithInputBox: true,
          valueToConfirm: 'DELETE',
        }"
          (confirmed)="onDelete()"
        >
          {{ 'Remove' | translate }}
        </li>
      </e11-overflow>
      <ng-template #confirmationBoxText>
        <p>{{ 'You are about to remove this card. To confirm this operation please enter ' | translate }}<span class="e11-font-bold">DELETE</span></p>
      </ng-template>
    </div>
  </div> `, isInline: true, styles: [".ring{height:500px;width:500px;border-radius:50%;background:transparent;border:50px solid rgba(255,255,255,.1);bottom:-300px;right:-300px;box-sizing:border-box;pointer-events:none}.ring:after{content:\"\";position:absolute;height:600px;width:600px;border-radius:50%;background:transparent;border:30px solid rgba(255,255,255,.1);bottom:-80px;right:-110px;box-sizing:border-box;pointer-events:none}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$1.E11ConfirmationBoxDirective, selector: "[e11ConfirmationBox]", inputs: ["confirmationBoxContent", "componentInput"], outputs: ["confirmed", "canceled"] }, { kind: "component", type: CreditCardIconComponent, selector: "credit-card-icon", inputs: ["cardType"] }, { kind: "component", type: i5$1.E11OverflowComponent, selector: "e11-overflow", inputs: ["idMenuControl", "idOptionsMenu", "optionMenuClass", "iconClass", "containerClass", "showInContent", "icon", "iconColor", "options", "menuWidth", "menuMinWidth", "menuMaxWidth", "menuPosition", "closeOnSelect", "showSelectedIcon", "openOnClick", "closeOnMouseOut", "closeOnEscape", "closeOnClickOutside"], outputs: ["clickOptionEvent", "onClickOptionEvent"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkPaymentCardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-payment-card', template: `<div
    class="e11-overflow-hidden e11-group e11-w-64 sm:e11-w-72 md:e11-w-96 e11-h-44 md:e11-h-56 e11-m-auto e11-bg-red-100 e11-rounded-xl e11-relative e11-text-white e11-shadow-2xl e11-transition-transform e11-transform hover:e11-scale-110"
  >
    <div
      class="e11-relative e11-object-cover e11-w-full e11-h-full e11-rounded-xl e11-bg-gradient-to-r e11-from-blue-600 e11-to-blue-500"
      *ngIf="!isLoading"
    ></div>
    <div class="e11-relative e11-rounded-xl e11-skeleton-empty !e11-h-full !e11-w-full" *ngIf="isLoading"></div>
    <div class="ring e11-absolute"></div>
    <div class="e11-w-full e11-px-8 e11-absolute e11-top-8">
      <div class="e11-flex e11-justify-between">
        <div>
          <p class="e11-font-light e11-mb-0" [ngClass]="{ 'e11-text-skin-grey ': isLoading }">{{ 'Name' | translate }}</p>
          <p class="e11-font-semibold e11-mb-0 e11-tracking-widest" *ngIf="!isLoading">{{ paymentCard.billing_details.name }}</p>
        </div>
        <credit-card-icon class="e11-w-14 e11-h-14" [cardType]="paymentCard.card?.brand" *ngIf="!isLoading"></credit-card-icon>
      </div>
      <div class="e11-pt-1">
        <p class="e11-font-light e11-mb-0" [ngClass]="{ 'e11-text-skin-grey ': isLoading }">{{ 'Card Number' | translate }}</p>
        <p class="e11-font-semibold e11-mb-0 e11-tracking-more-wider" *ngIf="!isLoading">**** **** **** {{ paymentCard.card?.last4 || '----' }}</p>
      </div>
      <div class="e11-hidden md:e11-block e11-pt-6 e11-pr-6">
        <div class="e11-flex e11-justify-between">
          <div>
            <p class="e11-font-light e11-mb-0 e11-text-xs" [ngClass]="{ 'e11-text-skin-grey ': isLoading }">{{ 'Expiry' | translate }}</p>
            <p class="e11-font-semibold e11-mb-0 e11-tracking-wider e11-text-sm" *ngIf="!isLoading">
              {{ paymentCard.card?.exp_month }}/{{ paymentCard.card?.exp_year }}
            </p>
          </div>
          <div></div>
          <div>
            <p class="e11-font-light e11-mb-0 e11-text-xs" [ngClass]="{ 'e11-text-skin-grey ': isLoading }">{{ 'CVV' | translate }}</p>
            <p class="e11-font-semibold e11-mb-0 e11-tracking-more-wider e11-text-sm" *ngIf="!isLoading">···</p>
          </div>
        </div>
      </div>
    </div>
    <div class="e11-absolute e11-right-0 e11-top-0">
      <e11-overflow
        class="e11-p-4 e11-hidden group-hover:e11-block"
        iconClass="!e11-text-skin-white"
        *ngIf="!isLoading"
        [closeOnMouseOut]="true"
        [menuMaxWidth]="160"
        [menuPosition]="'bottom left'"
      >
        <li
          class="e11-py-1 e11-px-4 e11-text-sm hover:e11-bg-skin-grey/20 e11-text-skin-black e11-cursor-pointer e11-font-primary-medium"
          custom-option
          (click)="onSetDefault()"
        >
          {{ 'Set as default' | translate }}
        </li>

        <li
          class="e11-py-1 e11-px-4 e11-text-sm hover:e11-bg-skin-grey/20 e11-text-skin-black e11-cursor-pointer e11-font-primary-medium"
          custom-option
          e11ConfirmationBox
          [confirmationBoxContent]="confirmationBoxText"
          [componentInput]=" {
          confirmText: 'Delete' | translate,
          cancelText: 'Cancel' | translate,
          confirmValueWithInputBox: true,
          valueToConfirm: 'DELETE',
        }"
          (confirmed)="onDelete()"
        >
          {{ 'Remove' | translate }}
        </li>
      </e11-overflow>
      <ng-template #confirmationBoxText>
        <p>{{ 'You are about to remove this card. To confirm this operation please enter ' | translate }}<span class="e11-font-bold">DELETE</span></p>
      </ng-template>
    </div>
  </div> `, styles: [".ring{height:500px;width:500px;border-radius:50%;background:transparent;border:50px solid rgba(255,255,255,.1);bottom:-300px;right:-300px;box-sizing:border-box;pointer-events:none}.ring:after{content:\"\";position:absolute;height:600px;width:600px;border-radius:50%;background:transparent;border:30px solid rgba(255,255,255,.1);bottom:-80px;right:-110px;box-sizing:border-box;pointer-events:none}\n"] }]
        }], ctorParameters: () => [{ type: PaymentStore }], propDecorators: { paymentCard: [{
                type: Input
            }], isLoading: [{
                type: Input
            }] } });

const formattedAddressFields = {
    name: 'Name',
    address_city: 'City',
    address_line1: 'Address 1',
    address_country: 'Country',
    address_state: 'State',
    address_zip: 'Zip',
};
class SdkStripePaymentElementComponent {
    constructor(libConfig, e11StripeService) {
        this.libConfig = libConfig;
        this.e11StripeService = e11StripeService;
        this.validationFieldNames = formattedAddressFields;
        this.elementsOptions = {
            locale: 'en',
            mode: 'setup',
            currency: 'usd',
            appearance: {
                theme: 'stripe',
            },
        };
        this.cardNumberOptions = {
            showIcon: true,
            iconStyle: 'solid',
        };
        this.addressOptions = {
            mode: 'billing',
            autocomplete: {
                mode: 'google_maps_api',
                apiKey: this.libConfig.config.mapsApiKey,
            },
            validation: {},
        };
        this.stripeAddressForm = new FormGroup({
            name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(3)] }),
            address_city: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
            address_line1: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
            address_line2: new FormControl('', { nonNullable: true, validators: [] }),
            address_country: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
            address_state: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
            address_zip: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
        });
        this.cardErrorGroupByElementType = {
            cardNumber: undefined,
            cardExpiry: undefined,
            cardCvc: undefined,
        };
        this.stripeServiceInstance = this.e11StripeService.stripeServiceInstance();
    }
    ngOnInit() { }
    elementChange($event) {
        this.cardErrorGroupByElementType[$event.elementType] = $event;
    }
    get f() {
        return this.stripeAddressForm.controls;
    }
    isInvalidAndTouched(field) {
        const control = this.f[field];
        return control && control.invalid && (control.dirty || control.touched);
    }
    validateCardField(field, errorMessage) {
        if (!this.cardErrorGroupByElementType[field]) {
            this.cardErrorGroupByElementType[field] = {
                brand: 'unknown',
                elementType: field,
                empty: true,
                complete: true,
                error: {
                    type: 'validation_error',
                    code: 'card_error',
                    message: errorMessage,
                },
            };
            return false;
        }
        if (this.cardErrorGroupByElementType[field]?.error)
            return false;
        if (this.cardErrorGroupByElementType[field]?.empty) {
            this.cardErrorGroupByElementType[field].error = {
                type: 'validation_error',
                code: 'card_error',
                message: errorMessage,
            };
            return false;
        }
        return true;
    }
    cardCvcValidation() {
        return this.validateCardField('cardCvc', 'Card cvc is required');
    }
    cardDateValidation() {
        return this.validateCardField('cardExpiry', 'Card expiry is required');
    }
    cardNumberValidation() {
        return this.validateCardField('cardNumber', 'Card number is required');
    }
    validateAndGateAll() {
        this.stripeAddressForm.markAllAsTouched();
        const validations = [this.cardNumberValidation(), this.cardDateValidation(), this.cardCvcValidation(), this.stripeAddressForm.valid];
        if (validations.every((validation) => validation === true)) {
            return { ...this.stripeAddressForm.getRawValue(), address_line2: this.stripeAddressForm.getRawValue().address_line2 || '' };
        }
        return null;
    }
    getErrorMessage(field, errorType, value) {
        const control = this.f[field];
        if (control.hasError(errorType)) {
            switch (errorType) {
                case 'required':
                    return `${formattedAddressFields[field]} field is required`;
                case 'minlength':
                    return `${formattedAddressFields[field]} field minimum length is ${value} characters`;
                // Add other cases as needed
            }
        }
        return null;
    }
    onChange(event) {
        this.stripeAddressForm.patchValue({
            name: event.value.name,
            address_city: event.value.address.city,
            address_line1: event.value.address.line1,
            address_line2: event.value.address.line2 || '',
            address_country: event.value.address.country,
            address_state: event.value.address.state,
            address_zip: event.value.address.postal_code,
        });
        this.stripeAddressForm.updateValueAndValidity();
    }
    onReady(event) { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkStripePaymentElementComponent, deps: [{ token: BasePaymentConfigurationProvider }, { token: E11StripeService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkStripePaymentElementComponent, selector: "sdk-stripe-payment-element", viewQueries: [{ propertyName: "card", first: true, predicate: StripeCardNumberComponent, descendants: true }], ngImport: i0, template: "<div ngxStripeCardGroup [elementsOptions]=\"elementsOptions\" [stripe]=\"this.stripeServiceInstance\" (change)=\"elementChange($event)\">\n  <div>\n    <ngx-stripe-address\n      [stripe]=\"this.stripeServiceInstance\"\n      [elementsOptions]=\"elementsOptions\"\n      [options]=\"addressOptions\"\n      (change)=\"onChange($event)\"\n      (ready)=\"onReady($event)\"\n    ></ngx-stripe-address>\n  </div>\n  <ng-container *ngFor=\"let field of validationFieldNames | keyvalue\">\n    <p *ngIf=\"isInvalidAndTouched(field.key)\" class=\"e11-mb-0 e11-flex e11-items-center e11-h-6 e11-text-skin-error\">\n      {{ getErrorMessage(field.key, \"minlength\", \"3\") }}\n      {{ getErrorMessage(field.key, \"required\") }}\n    </p>\n  </ng-container>\n  <div class=\"e11-flex e11-flex-col lg:e11-flex-row e11-justify-between e11-py-4 e11-gap-2\">\n    <div class=\"e11-w-full lg:e11-w-3/4\">\n      <label\n        [ngClass]=\"{\n          'e11-text-skin-danger': cardErrorGroupByElementType.cardNumber?.error\n        }\"\n      >\n        {{ \"Card Number\" | translate }}</label\n      >\n      <div\n        class=\"e11-border e11-rounded-md e11-p-3\"\n        [ngClass]=\"{\n          'e11-border-skin-danger': cardErrorGroupByElementType.cardNumber?.error\n        }\"\n      >\n        <ngx-stripe-card-number [options]=\"cardNumberOptions\"></ngx-stripe-card-number>\n      </div>\n      <p *ngIf=\"cardErrorGroupByElementType.cardNumber?.error\" class=\"e11-mb-0 e11-text-skin-error\">\n        {{ cardErrorGroupByElementType.cardNumber!.error!.message | translate }}\n      </p>\n    </div>\n    <div class=\"e11-flex e11-justify-between e11-w-full lg:e11-w-1/4 e11-gap-2\">\n      <div class=\"e11-w-full\">\n        <label\n          [ngClass]=\"{\n            'e11-text-skin-danger': cardErrorGroupByElementType.cardExpiry?.error\n          }\"\n        >\n          {{ \"MM/YY\" | translate }}</label\n        >\n        <div\n          class=\"e11-border e11-rounded-md e11-p-3\"\n          [ngClass]=\"{\n            'e11-border-skin-danger': cardErrorGroupByElementType.cardExpiry?.error\n          }\"\n        >\n          <ngx-stripe-card-expiry></ngx-stripe-card-expiry>\n        </div>\n        <p *ngIf=\"cardErrorGroupByElementType.cardExpiry?.error\" class=\"e11-mb-0 e11-text-skin-error\">\n          {{ cardErrorGroupByElementType.cardExpiry!.error!.message | translate }}\n        </p>\n      </div>\n      <div class=\"e11-w-full\">\n        <label\n          [ngClass]=\"{\n            'e11-text-skin-danger': cardErrorGroupByElementType.cardCvc?.error\n          }\"\n        >\n          {{ \"CVC\" | translate }}</label\n        >\n        <div\n          class=\"e11-border e11-rounded-md e11-p-3\"\n          [ngClass]=\"{\n            'e11-border-skin-danger': cardErrorGroupByElementType.cardCvc?.error\n          }\"\n        >\n          <ngx-stripe-card-cvc></ngx-stripe-card-cvc>\n        </div>\n        <p *ngIf=\"cardErrorGroupByElementType.cardCvc?.error\" class=\"e11-mb-0 e11-text-skin-error\">\n          {{ cardErrorGroupByElementType.cardCvc!.error!.message | translate }}\n        </p>\n      </div>\n    </div>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.StripeAddressComponent, selector: "ngx-stripe-address", inputs: ["containerClass", "options", "elementsOptions", "stripe"], outputs: ["load", "blur", "change", "focus", "ready", "escape", "loaderror", "loaderstart"] }, { kind: "component", type: i4.StripeCardNumberComponent, selector: "ngx-stripe-card-number", inputs: ["containerClass", "options"], outputs: ["load", "blur", "change", "focus", "ready", "escape"] }, { kind: "component", type: i4.StripeCardExpiryComponent, selector: "ngx-stripe-card-expiry", inputs: ["containerClass", "options"], outputs: ["load", "blur", "change", "focus", "ready", "escape"] }, { kind: "component", type: i4.StripeCardCvcComponent, selector: "ngx-stripe-card-cvc", inputs: ["containerClass", "options"], outputs: ["load", "blur", "change", "focus", "ready", "escape"] }, { kind: "directive", type: i4.StripeCardGroupDirective, selector: "ngx-stripe-card-group,[ngxStripeCardGroup]", inputs: ["elementsOptions", "stripe"], outputs: ["elements", "load", "blur", "change", "focus", "ready", "escape"] }, { kind: "pipe", type: i1.KeyValuePipe, name: "keyvalue" }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkStripePaymentElementComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-stripe-payment-element', template: "<div ngxStripeCardGroup [elementsOptions]=\"elementsOptions\" [stripe]=\"this.stripeServiceInstance\" (change)=\"elementChange($event)\">\n  <div>\n    <ngx-stripe-address\n      [stripe]=\"this.stripeServiceInstance\"\n      [elementsOptions]=\"elementsOptions\"\n      [options]=\"addressOptions\"\n      (change)=\"onChange($event)\"\n      (ready)=\"onReady($event)\"\n    ></ngx-stripe-address>\n  </div>\n  <ng-container *ngFor=\"let field of validationFieldNames | keyvalue\">\n    <p *ngIf=\"isInvalidAndTouched(field.key)\" class=\"e11-mb-0 e11-flex e11-items-center e11-h-6 e11-text-skin-error\">\n      {{ getErrorMessage(field.key, \"minlength\", \"3\") }}\n      {{ getErrorMessage(field.key, \"required\") }}\n    </p>\n  </ng-container>\n  <div class=\"e11-flex e11-flex-col lg:e11-flex-row e11-justify-between e11-py-4 e11-gap-2\">\n    <div class=\"e11-w-full lg:e11-w-3/4\">\n      <label\n        [ngClass]=\"{\n          'e11-text-skin-danger': cardErrorGroupByElementType.cardNumber?.error\n        }\"\n      >\n        {{ \"Card Number\" | translate }}</label\n      >\n      <div\n        class=\"e11-border e11-rounded-md e11-p-3\"\n        [ngClass]=\"{\n          'e11-border-skin-danger': cardErrorGroupByElementType.cardNumber?.error\n        }\"\n      >\n        <ngx-stripe-card-number [options]=\"cardNumberOptions\"></ngx-stripe-card-number>\n      </div>\n      <p *ngIf=\"cardErrorGroupByElementType.cardNumber?.error\" class=\"e11-mb-0 e11-text-skin-error\">\n        {{ cardErrorGroupByElementType.cardNumber!.error!.message | translate }}\n      </p>\n    </div>\n    <div class=\"e11-flex e11-justify-between e11-w-full lg:e11-w-1/4 e11-gap-2\">\n      <div class=\"e11-w-full\">\n        <label\n          [ngClass]=\"{\n            'e11-text-skin-danger': cardErrorGroupByElementType.cardExpiry?.error\n          }\"\n        >\n          {{ \"MM/YY\" | translate }}</label\n        >\n        <div\n          class=\"e11-border e11-rounded-md e11-p-3\"\n          [ngClass]=\"{\n            'e11-border-skin-danger': cardErrorGroupByElementType.cardExpiry?.error\n          }\"\n        >\n          <ngx-stripe-card-expiry></ngx-stripe-card-expiry>\n        </div>\n        <p *ngIf=\"cardErrorGroupByElementType.cardExpiry?.error\" class=\"e11-mb-0 e11-text-skin-error\">\n          {{ cardErrorGroupByElementType.cardExpiry!.error!.message | translate }}\n        </p>\n      </div>\n      <div class=\"e11-w-full\">\n        <label\n          [ngClass]=\"{\n            'e11-text-skin-danger': cardErrorGroupByElementType.cardCvc?.error\n          }\"\n        >\n          {{ \"CVC\" | translate }}</label\n        >\n        <div\n          class=\"e11-border e11-rounded-md e11-p-3\"\n          [ngClass]=\"{\n            'e11-border-skin-danger': cardErrorGroupByElementType.cardCvc?.error\n          }\"\n        >\n          <ngx-stripe-card-cvc></ngx-stripe-card-cvc>\n        </div>\n        <p *ngIf=\"cardErrorGroupByElementType.cardCvc?.error\" class=\"e11-mb-0 e11-text-skin-error\">\n          {{ cardErrorGroupByElementType.cardCvc!.error!.message | translate }}\n        </p>\n      </div>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: () => [{ type: BasePaymentConfigurationProvider }, { type: E11StripeService }], propDecorators: { card: [{
                type: ViewChild,
                args: [StripeCardNumberComponent]
            }] } });

class SdkNewPaymentCardComponent {
    constructor(paymentStore) {
        this.paymentStore = paymentStore;
        this.getVerifyCardAndAddPaymentMethodLoading$ = this.paymentStore.getVerifyCardAndAddPaymentMethodLoading$;
        this.addPaymentMethodStatus$ = this.paymentStore.addPaymentMethodStatus$.pipe(tap((result) => result && this.modalNewCard.close()));
    }
    ngOnInit() { }
    createToken() {
        const stripeFormAddressValue = this.stripePaymentElement.validateAndGateAll();
        if (!stripeFormAddressValue)
            return;
        this.paymentStore.verifyCardAndAddPaymentMethod({
            tokenType: this.stripePaymentElement.card.element,
            data: stripeFormAddressValue,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkNewPaymentCardComponent, deps: [{ token: PaymentStore }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkNewPaymentCardComponent, selector: "sdk-payment-new-payment-card", viewQueries: [{ propertyName: "stripePaymentElement", first: true, predicate: SdkStripePaymentElementComponent, descendants: true }, { propertyName: "modalNewCard", first: true, predicate: ["modalNewCard"], descendants: true }], ngImport: i0, template: `<div
      (click)="modalNewCard.open()"
      class="e11-w-64 sm:e11-w-72 md:e11-w-96 e11-h-44 md:e11-h-56 e11-flex e11-cursor-pointer e11-justify-center e11-items-center e11-border e11-border-dashed e11-border-skin-primary e11-rounded-lg e11-transition-transform e11-transform hover:e11-scale-105"
    >
      <div class="e11-text-skin-primary e11-text-center">
        <span class="material-icons-outlined e11-text-3xl">add_circle_outline</span>
        <div>{{ 'Add Payment Methods' | translate }}</div>
      </div>
    </div>

    <e11-modal #modalNewCard modalWidth="sm" [bodyHeight]="'auto'" title="{{ 'Add new card' | translate }}" [showHeader]="true" [showFooter]="true">
      <ng-template e11Template="Body" class="e11-mb-8">
        <sdk-stripe-payment-element #stripePaymentElement></sdk-stripe-payment-element>
      </ng-template>
      <ng-template e11Template="Footer">
        <div class="e11-flex e11-w-full e11-flex-row-reverse">
          <e11-button
            (click)="createToken()"
            [loading]="(getVerifyCardAndAddPaymentMethodLoading$ | async) || false"
            value="{{ 'Save payment method' | translate }}"
            [color]="'primary-accent'"
          >
          </e11-button>
        </div>
      </ng-template>
    </e11-modal> `, isInline: true, dependencies: [{ kind: "directive", type: i2$2.E11Template, selector: "[e11Template]", inputs: ["type", "e11Template"] }, { kind: "component", type: i3$2.E11ModalComponent, selector: "e11-modal", inputs: ["title", "width", "bodyHeight", "titleAlign", "hideCloseButton", "closer", "bodyClassOverrides", "bodyClass", "modalClassOverrides", "modalClass", "contentClassOverrides", "contentClass", "headerClassOverrides", "headerClass", "footerClassOverrides", "footerClass", "showFooter", "showHeader", "darkMode", "closeOnEscape", "closeOnClickOutside", "headerCTAs", "hasBackdrop", "backdropStyle"], outputs: ["modalOpened", "onModalOpened", "modalClosed", "onModalClosed", "headerCTAClicked", "onHeaderCTAClicked"] }, { kind: "component", type: i2$3.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: SdkStripePaymentElementComponent, selector: "sdk-stripe-payment-element" }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkNewPaymentCardComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-payment-new-payment-card',
                    template: `<div
      (click)="modalNewCard.open()"
      class="e11-w-64 sm:e11-w-72 md:e11-w-96 e11-h-44 md:e11-h-56 e11-flex e11-cursor-pointer e11-justify-center e11-items-center e11-border e11-border-dashed e11-border-skin-primary e11-rounded-lg e11-transition-transform e11-transform hover:e11-scale-105"
    >
      <div class="e11-text-skin-primary e11-text-center">
        <span class="material-icons-outlined e11-text-3xl">add_circle_outline</span>
        <div>{{ 'Add Payment Methods' | translate }}</div>
      </div>
    </div>

    <e11-modal #modalNewCard modalWidth="sm" [bodyHeight]="'auto'" title="{{ 'Add new card' | translate }}" [showHeader]="true" [showFooter]="true">
      <ng-template e11Template="Body" class="e11-mb-8">
        <sdk-stripe-payment-element #stripePaymentElement></sdk-stripe-payment-element>
      </ng-template>
      <ng-template e11Template="Footer">
        <div class="e11-flex e11-w-full e11-flex-row-reverse">
          <e11-button
            (click)="createToken()"
            [loading]="(getVerifyCardAndAddPaymentMethodLoading$ | async) || false"
            value="{{ 'Save payment method' | translate }}"
            [color]="'primary-accent'"
          >
          </e11-button>
        </div>
      </ng-template>
    </e11-modal> `,
                }]
        }], ctorParameters: () => [{ type: PaymentStore }], propDecorators: { stripePaymentElement: [{
                type: ViewChild,
                args: [SdkStripePaymentElementComponent]
            }], modalNewCard: [{
                type: ViewChild,
                args: ['modalNewCard']
            }] } });

class SdkPaymentMethodsComponent {
    constructor(paymentStore) {
        this.paymentStore = paymentStore;
        this.paymentMethods$ = this.paymentStore.getPaymentMethods$;
        this.paymentMethodsLoaded$ = this.paymentStore.getPaymentMethodsLoaded$;
        this.paymentMethodsLoading$ = this.paymentStore.getPaymentMethodsLoading$;
    }
    ngOnInit() {
        this.paymentStore.getPaymentCustomer();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkPaymentMethodsComponent, deps: [{ token: PaymentStore }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkPaymentMethodsComponent, selector: "sdk-payment-payment-methods", ngImport: i0, template: `
    <div class="e11-flex e11-flex-wrap e11-gap-6" *ngrxLet="paymentMethods$ as paymentMethods">
      <sdk-payment-card *ngFor="let paymentMethod of paymentMethods" [paymentCard]="paymentMethod" [isLoading]="false"></sdk-payment-card>
      <ng-container *ngIf="(paymentMethodsLoading$ | async) || false">
        <sdk-payment-card *ngFor="let i of 2 | range" [isLoading]="true"></sdk-payment-card>
      </ng-container>
      <sdk-payment-new-payment-card *ngIf="!(paymentMethodsLoading$ | async)"></sdk-payment-new-payment-card>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SdkPaymentCardComponent, selector: "sdk-payment-card", inputs: ["paymentCard", "isLoading"] }, { kind: "component", type: SdkNewPaymentCardComponent, selector: "sdk-payment-new-payment-card" }, { kind: "pipe", type: i6.RangePipe, name: "range" }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkPaymentMethodsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-payment-payment-methods',
                    template: `
    <div class="e11-flex e11-flex-wrap e11-gap-6" *ngrxLet="paymentMethods$ as paymentMethods">
      <sdk-payment-card *ngFor="let paymentMethod of paymentMethods" [paymentCard]="paymentMethod" [isLoading]="false"></sdk-payment-card>
      <ng-container *ngIf="(paymentMethodsLoading$ | async) || false">
        <sdk-payment-card *ngFor="let i of 2 | range" [isLoading]="true"></sdk-payment-card>
      </ng-container>
      <sdk-payment-new-payment-card *ngIf="!(paymentMethodsLoading$ | async)"></sdk-payment-new-payment-card>
    </div>
  `,
                }]
        }], ctorParameters: () => [{ type: PaymentStore }] });

class SdkBillingComponent {
    constructor() {
        this.isStripeEnabled = inject(stripePublishableKey);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkBillingComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkBillingComponent, selector: "sdk-payment-billing", ngImport: i0, template: `<div class="e11-flex e11-flex-col e11-gap-6">
    <div *ngIf="isStripeEnabled">
      <h3 class="e11-mb-2">{{ 'Payment Methods' | translate }}</h3>
      <sdk-payment-payment-methods></sdk-payment-payment-methods>
    </div>
    <hr />
    <div class="e11-overflow-auto">
      <h3 class="e11-mb-2">{{ 'Billing History' | translate }}</h3>
      <sdk-payment-billing-history></sdk-payment-billing-history>
    </div>
  </div> `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SdkBillingHistoryComponent, selector: "sdk-payment-billing-history" }, { kind: "component", type: SdkPaymentMethodsComponent, selector: "sdk-payment-payment-methods" }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkBillingComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-payment-billing',
                    template: `<div class="e11-flex e11-flex-col e11-gap-6">
    <div *ngIf="isStripeEnabled">
      <h3 class="e11-mb-2">{{ 'Payment Methods' | translate }}</h3>
      <sdk-payment-payment-methods></sdk-payment-payment-methods>
    </div>
    <hr />
    <div class="e11-overflow-auto">
      <h3 class="e11-mb-2">{{ 'Billing History' | translate }}</h3>
      <sdk-payment-billing-history></sdk-payment-billing-history>
    </div>
  </div> `,
                }]
        }] });

const ADMIN_PLATFORM_PAYMENT_CUSTOMERS_PATH = 'platform-admin/payment-customers';
const ADMIN_PLATFORM_PAYMENT_TENANT_PATH = 'platform-admin/tenant-product';
const TENANT_SUBSCRIPTION_PATH = 'tenant/subscriptions';
class PlatformAdminCustomerPaymentService {
    constructor(libConfig, logger) {
        this.libConfig = libConfig;
        this.logger = logger;
        this.api = new RestApiClient({ token: libConfig.config.token$.pipe(filter(isNotNil)), baseUrl: `${libConfig.config.paymentsBaseURL}` });
    }
    createPaymentIntent(customerKey, data) {
        return this.api.post(`platform-admin/payment-intents/${customerKey}`, data).pipe(map((res) => res.data));
    }
    getCustomerProducts(customerKey) { }
    getCustomerInvoices(customerKey) {
        return this.api
            .get(`${ADMIN_PLATFORM_PAYMENT_CUSTOMERS_PATH}/${customerKey}/customer-invoices`)
            .pipe(map((res) => res.data));
    }
    getCustomerSingleInvoice(customerKey, invoiceId) {
        return this.api.get(`${ADMIN_PLATFORM_PAYMENT_CUSTOMERS_PATH}/${customerKey}/customer-invoices/${invoiceId}`).pipe(map((res) => res.data));
    }
    updateCustomerInvoice(customerKey, invoiceId) {
        return this.api.put(`${ADMIN_PLATFORM_PAYMENT_CUSTOMERS_PATH}/${customerKey}/customer-invoices/${invoiceId}`, {}).pipe(map((res) => res.data));
    }
    updateInvoicePaymentStatus(customerKey, invoiceId) {
        return this.api
            .put(`${ADMIN_PLATFORM_PAYMENT_CUSTOMERS_PATH}/${customerKey}/customer-invoices/${invoiceId}/update-payment-status`, {})
            .pipe(map((res) => res.data));
    }
    getPlatformAdminTenantProducts(tenantKey) {
        return this.api.get(`${ADMIN_PLATFORM_PAYMENT_TENANT_PATH}/${tenantKey}/`).pipe(map((res) => res.data));
    }
    getTenantProducts() {
        return this.api.get(`tenant-product`).pipe(map((res) => res.data));
    }
    addProductToProduct(data) {
        return this.api.post(`${ADMIN_PLATFORM_PAYMENT_TENANT_PATH}/`, data).pipe(map((res) => res.data));
    }
    addTenantCustomerSubscription(customerKey, data) {
        return this.api.post(`${TENANT_SUBSCRIPTION_PATH}/${customerKey}`, data).pipe(map((res) => res.data));
    }
    cancelTenantCustomerSubscription(subscriptionId) {
        return this.api.put(`${TENANT_SUBSCRIPTION_PATH}/${subscriptionId}/cancel`, {}).pipe(map((res) => res.data));
    }
    getCustomerSubscriptions(customerKey) {
        return this.api.get(`${TENANT_SUBSCRIPTION_PATH}/${customerKey}`).pipe(map((res) => res.data));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminCustomerPaymentService, deps: [{ token: BasePaymentConfigurationProvider }, { token: i2.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminCustomerPaymentService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminCustomerPaymentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: BasePaymentConfigurationProvider }, { type: i2.E11Logger }] });

const ADMIN_PLATFORM_SUBSCRIPTIONS_PATH = 'platform-admin/subscriptions';
class PlatformAdminSubscriptionsService {
    constructor(libConfig, logger) {
        this.libConfig = libConfig;
        this.logger = logger;
        this.api = new RestApiClient({ token: libConfig.config.token$.pipe(filter(isNotNil)), baseUrl: `${libConfig.config.paymentsBaseURL}` });
    }
    getEnabledSubscriptions() {
        return this.api.get(`${ADMIN_PLATFORM_SUBSCRIPTIONS_PATH}`).pipe(map((res) => res.data));
    }
    getCustomerSubscriptions(customerKey) {
        return this.api.get(`${ADMIN_PLATFORM_SUBSCRIPTIONS_PATH}/${customerKey}`).pipe(map((res) => res.data));
    }
    addCustomerSubscription(customerKey, data) {
        return this.api.post(`${ADMIN_PLATFORM_SUBSCRIPTIONS_PATH}/${customerKey}`, data).pipe(map((res) => res.data));
    }
    cancelSubscription(subscriptionId) {
        return this.api.put(`${ADMIN_PLATFORM_SUBSCRIPTIONS_PATH}/${subscriptionId}/cancel`, {}).pipe(map((res) => res.data));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminSubscriptionsService, deps: [{ token: BasePaymentConfigurationProvider }, { token: i2.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminSubscriptionsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminSubscriptionsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: BasePaymentConfigurationProvider }, { type: i2.E11Logger }] });

const initialState$1 = {
    customerInvoices: [],
    tenantProducts: [],
    loadingTenantProducts: false,
    loadingCustomerInvoices: false,
    customerInvoicesLoaded: false,
    selectedCustomerInvoice: null,
    loadingCustomerInvoice: false,
    customerSubscriptions: [],
    loadingCustomerSubscriptions: false,
    enabledSubscriptions: [],
    loadingEnabledSubscriptions: false,
};
class PlatformAdminCustomerPaymentsStore extends ComponentStore {
    #platformAdminCustomerPaymentService;
    #platformAdminSubscriptionsService;
    #logger;
    #errorHandler;
    constructor() {
        super(initialState$1);
        this.#platformAdminCustomerPaymentService = inject(PlatformAdminCustomerPaymentService);
        this.#platformAdminSubscriptionsService = inject(PlatformAdminSubscriptionsService);
        this.#logger = e11LoggerRef();
        this.#errorHandler = inject(E11ErrorHandlerService);
        this.customerInvoices$ = this.select((state) => state.customerInvoices);
        this.selectedCustomerInvoice$ = this.select((state) => state.selectedCustomerInvoice);
        this.loadingCustomerInvoices$ = this.select((state) => state.loadingCustomerInvoices);
        this.customerInvoicesLoaded$ = this.select((state) => state.customerInvoicesLoaded);
        this.loadingCustomerInvoice$ = this.select((state) => state.loadingCustomerInvoice);
        this.customerSubscriptions$ = this.select((state) => state.customerSubscriptions);
        this.loadingCustomerSubscriptions$ = this.select((state) => state.loadingCustomerSubscriptions);
        this.enabledSubscriptions$ = this.select((state) => state.enabledSubscriptions);
        this.loadingEnabledSubscriptions$ = this.select((state) => state.loadingEnabledSubscriptions);
        this.tenantProducts$ = this.select((state) => state.tenantProducts);
        this.tenantProductToProduct$ = this.select((state) => mergeProductPrices(state.tenantProducts.map((tp) => mapTenantProductToProduct(tp))));
        this.loadingTenantProducts$ = this.select((state) => state.loadingTenantProducts);
        this.onCustomerInvoices = this.updater((state, customerInvoices) => {
            return { ...state, customerInvoices, loadingCustomerInvoices: false };
        });
        this.onSelectedCustomerInvoice = this.updater((state, selectedCustomerInvoice) => {
            return { ...state, selectedCustomerInvoice, loadingCustomerInvoice: false };
        });
        this.onLoadingCustomerInvoices = this.updater((state, loadingCustomerInvoices) => {
            return { ...state, loadingCustomerInvoices };
        });
        this.onCustomerInvoicesLoaded = this.updater((state, customerInvoicesLoaded) => {
            return { ...state, customerInvoicesLoaded };
        });
        this.onCustomerSubscriptions = this.updater((state, customerSubscriptions) => {
            return { ...state, customerSubscriptions, loadingCustomerSubscriptions: false };
        });
        this.onEnabledSubscriptions = this.updater((state, enabledSubscriptions) => {
            return { ...state, enabledSubscriptions, loadingEnabledSubscriptions: false };
        });
        this.onTenantProducts = this.updater((state, tenantProducts) => {
            return { ...state, tenantProducts, loadingTenantProducts: false };
        });
        this.onLoadingTenantProducts = this.updater((state, loadingTenantProducts) => {
            return { ...state, loadingTenantProducts };
        });
        this.getEnabledSubscriptions = this.effect((_$) => {
            return _$.pipe(tap(() => this.patchState({ loadingEnabledSubscriptions: true })), switchMap(() => this.#platformAdminSubscriptionsService.getEnabledSubscriptions()), tap((enabledSubscriptions) => this.onEnabledSubscriptions(enabledSubscriptions)));
        });
        this.getPlatformAdminCustomerSubscriptions = this.effect((customerKey$) => {
            return customerKey$.pipe(tap(() => this.patchState({ loadingCustomerSubscriptions: true })), switchMap((customerKey) => this.#platformAdminSubscriptionsService.getCustomerSubscriptions(customerKey)), tap((customerSubscriptions) => this.onCustomerSubscriptions(customerSubscriptions)));
        });
        this.getCustomerSubscriptions = this.effect((customerKey$) => {
            return customerKey$.pipe(tap(() => this.patchState({ loadingCustomerSubscriptions: true })), switchMap((customerKey) => this.#platformAdminCustomerPaymentService.getCustomerSubscriptions(customerKey)), tap((customerSubscriptions) => this.onCustomerSubscriptions(customerSubscriptions)));
        });
        this.addCustomerSubscription = this.effect((data$) => {
            return data$.pipe(switchMap(({ customerKey, data, onComplete, onError }) => this.#platformAdminSubscriptionsService.addCustomerSubscription(customerKey, data).pipe(tapResponse((_) => {
                onComplete();
                return this.getCustomerSubscriptions(customerKey);
            }, (e) => {
                if (onError)
                    onError(e);
                return this.#errorHandler.handleError(e, { alertUser: true, title: 'Error adding subscription' });
            }))));
        });
        this.cancelCustomerSubscription = this.effect((value$) => {
            return value$.pipe(switchMap((value) => this.#platformAdminSubscriptionsService.cancelSubscription(value.subscriptionId).pipe(tapResponse({
                next: () => {
                    if (value.onComplete)
                        value.onComplete();
                    return this.getCustomerSubscriptions(value.customerKey);
                },
                error: (e) => {
                    if (value.onError)
                        value.onError(e);
                    this.#errorHandler.handleError(e, { alertUser: true, title: 'Error cancelling subscription' });
                },
            }))));
        });
        this.createPaymentIntent = this.effect((data$) => {
            return data$.pipe(switchMap(({ customerKey, data }) => this.#platformAdminCustomerPaymentService.createPaymentIntent(customerKey, data)));
        });
        this.getCustomerInvoices = this.effect((customerKey$) => {
            return customerKey$.pipe(tap(() => this.patchState({ loadingCustomerInvoices: true })), switchMap((customerKey) => this.#platformAdminCustomerPaymentService.getCustomerInvoices(customerKey)), tap((customerInvoices) => this.onCustomerInvoices(customerInvoices.invoices)));
        });
        this.getCustomerSingleInvoice = this.effect((_$) => {
            return _$.pipe(tap(() => this.patchState({ loadingCustomerInvoice: true })), switchMap(({ customerKey, invoiceId }) => this.#platformAdminCustomerPaymentService.getCustomerSingleInvoice(customerKey, invoiceId)), tap((selectedCustomerInvoice) => this.onSelectedCustomerInvoice(selectedCustomerInvoice)));
        });
        this.updateCustomerInvoice = this.effect((_$) => {
            return _$.pipe(switchMap(({ customerKey, invoiceId }) => this.#platformAdminCustomerPaymentService.updateCustomerInvoice(customerKey, invoiceId).pipe(tapResponse({
                next: () => this.getCustomerInvoices(customerKey),
                error: (e) => this.#errorHandler.handleError(e, { alertUser: true, title: 'Error updating invoice' }),
            }))));
        });
        this.getPlatformAdminTenantProducts = this.effect((tenantKey$) => {
            return tenantKey$.pipe(tap(() => this.onLoadingTenantProducts(true)), switchMap((tenantKey) => this.#platformAdminCustomerPaymentService.getPlatformAdminTenantProducts(tenantKey).pipe(tapResponse({
                next: (tenantProducts) => {
                    this.onTenantProducts(tenantProducts);
                },
                error: (e) => this.#errorHandler.handleError(e, { alertUser: true, title: 'Error getting tenant products' }),
            }))));
        });
        this.getTenantProducts = this.effect((_$) => {
            return _$.pipe(tap(() => this.onLoadingTenantProducts(true)), switchMap((_) => this.#platformAdminCustomerPaymentService.getTenantProducts().pipe(tapResponse({
                next: (tenantProducts) => {
                    this.onTenantProducts(tenantProducts);
                },
                error: (e) => this.#errorHandler.handleError(e, { alertUser: true, title: 'Error getting tenant products' }),
            }))));
        });
        this.addProductToTenant = this.effect((data$) => {
            return data$.pipe(switchMap(({ tenantId, sku, onComplete, onError }) => this.#platformAdminCustomerPaymentService.addProductToProduct({ tenantId, sku }).pipe(tapResponse((_) => {
                return onComplete();
            }, (e) => {
                if (onError)
                    onError(e);
                return this.#errorHandler.handleError(e, { alertUser: true, title: 'Error adding product to tenant' });
            }))));
        });
        this.addTenantCustomerSubscription = this.effect((data$) => {
            return data$.pipe(switchMap(({ customerKey, data, onComplete, onError }) => this.#platformAdminCustomerPaymentService.addTenantCustomerSubscription(customerKey, data).pipe(tapResponse((_) => {
                onComplete();
                return this.getCustomerSubscriptions(customerKey);
            }, (e) => {
                if (onError)
                    onError(e);
                return this.#errorHandler.handleError(e, { alertUser: true, title: 'Error adding subscription' });
            }))));
        });
        this.cancelTenantCustomerSubscription = this.effect((value$) => {
            return value$.pipe(switchMap((value) => this.#platformAdminCustomerPaymentService.cancelTenantCustomerSubscription(value.subscriptionId).pipe(tapResponse({
                next: () => {
                    if (value.onComplete)
                        value.onComplete();
                    return this.getCustomerSubscriptions(value.customerKey);
                },
                error: (e) => {
                    if (value.onError)
                        value.onError(e);
                    this.#errorHandler.handleError(e, { alertUser: true, title: 'Error cancelling subscription' });
                },
            }))));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminCustomerPaymentsStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminCustomerPaymentsStore, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminCustomerPaymentsStore, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });
const mapTenantProductToProduct = (tenantProduct) => {
    return {
        id: tenantProduct.productId,
        enabled: true,
        name: tenantProduct.productName,
        description: tenantProduct.productName,
        permissions: tenantProduct.permissions,
        prices: [
            {
                id: tenantProduct.priceId,
                productId: tenantProduct.productId,
                amount: tenantProduct.amount,
                currencyCode: tenantProduct.currencyCode,
                enabled: true,
                quantityTransform: tenantProduct.quantityTransform,
                recurring: tenantProduct.recurring,
                sku: tenantProduct.id,
            },
        ],
        paymentIntegration: tenantProduct.paymentIntegration,
    };
};
const groupProductByProductId = (products) => {
    return products.reduce((acc, product) => {
        if (!acc[product.id])
            acc[product.id] = [];
        acc[product.id].push(product);
        return acc;
    }, {});
};
const mergeProductPrices = (products) => {
    return Object.values(groupProductByProductId(products)).map((products) => {
        const prices = products.reduce((acc, product) => acc.concat(product.prices), []);
        return { ...products[0], prices };
    });
};

const pathFn$1 = (productId, priceId = '') => `${COLLECTIONS.PRODUCT}${productId}${COLLECTIONS.PRICE}${priceId}`;
class ProductPriceRepository extends SubcollectionRepository {
    constructor() {
        super(...arguments);
        this.buildPath = pathFn$1;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductPriceRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductPriceRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductPriceRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class ProductPriceService {
    constructor() {
        this.productPriceRepository = inject(ProductPriceRepository);
    }
    getAllProductPrices(productId) {
        return this.productPriceRepository.getAll(productId);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductPriceService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductPriceService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductPriceService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const pathFn = (subscriptionId = '') => `${COLLECTIONS.SUBSCRIPTION}${subscriptionId}`;
class ProductSubscriptionRepository extends SubcollectionRepository {
    constructor() {
        super(...arguments);
        this.buildPath = pathFn;
    }
    getAllForUserValueChanges(customerId) {
        const queryBuilder = new FirestoreQueryBuilder().where('customerKey', '==', customerId).build();
        return this.queryValueChanges(queryBuilder);
    }
    getActiveForUserValueChanges(customerId) {
        const queryBuilder = new FirestoreQueryBuilder()
            .where('customerKey', '==', customerId)
            .where('status', '!=', ProductSubscriptionStatus.Cancelled)
            .build();
        return super._queryValueChanges(this.buildPath(), queryBuilder);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductSubscriptionRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductSubscriptionRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductSubscriptionRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const PRODUCT_SUBSCRIPTION_PATH = 'product/subscriptions';
class ProductSubscriptionService {
    constructor(productSubscriptionRepository, libConfig) {
        this.productSubscriptionRepository = productSubscriptionRepository;
        this.libConfig = libConfig;
        this.logger = inject(E11Logger);
        this.api = new RestApiClient({ token: libConfig.config.token$.pipe(filter(isNotNil)), baseUrl: `${libConfig.config.paymentsBaseURL}` });
    }
    getCustomerSubscription(customerId) {
        return this.productSubscriptionRepository.getAllForUserValueChanges(customerId);
    }
    getCustomerActiveSubscription(customerId) {
        return this.productSubscriptionRepository.getActiveForUserValueChanges(customerId);
    }
    getActiveSubscription() {
        return this.api.get(PRODUCT_SUBSCRIPTION_PATH).pipe(map((response) => response.data));
    }
    updateSubscriptionPaymentMethod(subscriptionId, defaultPaymentMethod) {
        return this.api.put(`${PRODUCT_SUBSCRIPTION_PATH}/${subscriptionId}`, { defaultPaymentMethod });
    }
    updateSubscriptionStatus(subscriptionId, cancelAtPeriodEnd = true) {
        return this.api.put(`${PRODUCT_SUBSCRIPTION_PATH}/${subscriptionId}`, { cancelAtPeriodEnd });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductSubscriptionService, deps: [{ token: ProductSubscriptionRepository }, { token: BasePaymentConfigurationProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductSubscriptionService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductSubscriptionService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: ProductSubscriptionRepository }, { type: BasePaymentConfigurationProvider }] });

class ProductRepository extends CollectionRepository {
    constructor() {
        super(...arguments);
        this.COLLECTION = COLLECTIONS.PRODUCT;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const PRODUCT_PATH = 'product/products';
class ProductService {
    constructor(libConfig, logger, productRepository) {
        this.libConfig = libConfig;
        this.logger = logger;
        this.productRepository = productRepository;
        this.api = new RestApiClient({ token: libConfig.config.token$.pipe(filter(isNotNil)), baseUrl: `${libConfig.config.paymentsBaseURL}` });
    }
    getAllProductsValueChanges() {
        return this.productRepository.getAllValueChanges();
    }
    getAllProductsWithPrices(limit = 10) {
        return this.api.get(PRODUCT_PATH).pipe(map((response) => response.data));
    }
    getAllRecurringProducts() {
        return this.api.get(`${PRODUCT_PATH}/recurring`).pipe(map((response) => response.data));
    }
    getAllEnabledProducts() {
        return this.api.get(`${PRODUCT_PATH}/enabled`).pipe(map((response) => response.data));
    }
    createProduct(product) {
        return this.api.post(PRODUCT_PATH, product);
    }
    getSingleProduct(productId) {
        return this.productRepository.get(productId);
    }
    updateProduct(product, id) {
        return this.api.put(`${PRODUCT_PATH}/${id}`, product);
    }
    addProductPrice(productPrice, productId) {
        return this.api.post(`${PRODUCT_PATH}/${productId}/prices`, productPrice);
    }
    updateProductPrice(price, id) {
        return this.api.put(`${PRODUCT_PATH}/${id}/prices`, price);
    }
    deleteProductPrice(productId, priceId) {
        return this.api.delete(`${PRODUCT_PATH}/${productId}/prices/${priceId}`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductService, deps: [{ token: BasePaymentConfigurationProvider }, { token: i2.E11Logger }, { token: ProductRepository }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: BasePaymentConfigurationProvider }, { type: i2.E11Logger }, { type: ProductRepository }] });

const initialState = {
    products: [],
    singleProduct: undefined,
    recurringProducts: [],
    customerSubscriptions: [],
    activeCustomerSubscriptions: [],
    loadingProducts: false,
    subscriptionsLoading: false,
    subscriptionsLoaded: false,
};
class ProductStore extends ComponentStore {
    constructor() {
        super(initialState);
        this.productService = inject(ProductService);
        this.errorHandler = inject(E11ErrorHandlerService);
        this.notificationService = inject(E11NotificationsService);
        this.productSubscriptionService = inject(ProductSubscriptionService);
        this.libConfig = inject(BasePaymentConfigurationProvider);
        this.productPriceService = inject(ProductPriceService);
        this.customerKey$ = this.libConfig.customerKey$;
        this.allProducts$ = this.select((state) => state.products);
        this.singleProduct$ = this.select((state) => state.singleProduct);
        this.recurringProducts$ = this.select((state) => state.recurringProducts);
        this.loadingProducts$ = this.select((state) => state.loadingProducts);
        this.customerSubscriptions$ = this.select((state) => state.customerSubscriptions);
        this.activeCustomerSubscriptions$ = this.select((state) => state.activeCustomerSubscriptions);
        this.subscriptionsLoading$ = this.select((state) => state.subscriptionsLoading);
        this.subscriptionsLoaded$ = this.select((state) => state.subscriptionsLoaded);
        this.loadingProducts = this.updater((state, loadingProducts) => ({
            ...state,
            loadingProducts,
        }));
        this.onProducts = this.updater((state, products) => ({
            ...state,
            products,
        }));
        this.onRecurringProducts = this.updater((state, recurringProducts) => ({
            ...state,
            recurringProducts,
        }));
        this.subscriptionsLoading = this.updater((state, subscriptionsLoading) => ({
            ...state,
            subscriptionsLoading: subscriptionsLoading,
        }));
        this.subscriptionsLoaded = this.updater((state, subscriptionsLoaded) => ({
            ...state,
            subscriptionsLoaded: subscriptionsLoaded,
        }));
        this.onCustomerSubscriptions = this.updater((state, subscriptions) => ({
            ...state,
            customerSubscriptions: subscriptions,
        }));
        this.onActiveCustomerSubscriptions = this.updater((state, activeSubscriptions) => ({
            ...state,
            activeCustomerSubscriptions: activeSubscriptions,
        }));
        this.onSingleProduct = this.updater((state, singleProduct) => ({
            ...state,
            singleProduct: singleProduct,
        }));
        this.onListenAllProducts = this.effect((_$) => {
            return _$.pipe(tap(() => this.loadingProducts(true)), switchMap(() => this.productService.getAllProductsValueChanges().pipe(tapResponse((products) => this.onProducts(products), (e) => this.errorHandler.handleError(e)), finalize(() => this.loadingProducts(false)))));
        });
        this.onGetAllProductsWithPrice = this.effect((_$) => {
            return _$.pipe(tap(() => this.loadingProducts(true)), switchMap(() => this.productService.getAllProductsWithPrices().pipe(tapResponse((products) => this.onProducts(products), (e) => this.errorHandler.handleError(e)), finalize(() => this.loadingProducts(false)))));
        });
        this.onGetAllRecurringProducts = this.effect((_$) => {
            return _$.pipe(tap(() => this.loadingProducts(true)), switchMap(() => this.productService.getAllRecurringProducts().pipe(tapResponse((products) => this.onRecurringProducts(products), (e) => this.errorHandler.handleError(e)), finalize(() => this.loadingProducts(false)))));
        });
        this.getAllEnabledProducts = this.effect((_$) => {
            return _$.pipe(tap(() => this.loadingProducts(true)), switchMap(() => this.productService.getAllEnabledProducts().pipe(tapResponse((products) => this.onRecurringProducts(products), (e) => this.errorHandler.handleError(e)), finalize(() => this.loadingProducts(false)))));
        });
        this.createProduct = this.effect((product$) => {
            return product$.pipe(switchMap((product) => this.productService.createProduct(product).pipe(tapResponse((_) => {
                this.notificationService.popNotificationMessage(NOTIFICATION_MESSAGES.productAdded());
                product.onComplete();
                return this.onGetAllProductsWithPrice();
            }, (e) => {
                if (product.onError)
                    product.onError(e);
                return this.errorHandler.handleError(e);
            }))));
        });
        this.updateProduct = this.effect((product$) => {
            return product$.pipe(switchMap((product) => this.productService.updateProduct(product, product.id).pipe(tapResponse((_) => {
                this.notificationService.popNotificationMessage(NOTIFICATION_MESSAGES.productUpdated());
                product.onComplete();
                return this.onGetAllProductsWithPrice();
            }, (e) => {
                if (product.onError)
                    product.onError(e);
                return this.errorHandler.handleError(e);
            }))));
        });
        this.addProductPrice = this.effect((productPrice$) => {
            return productPrice$.pipe(switchMap((productPrice) => this.productService.addProductPrice(productPrice.price, productPrice.price.productId).pipe(tapResponse((_) => {
                // this.notificationService.popNotificationMessage(NOTIFICATION_MESSAGES.productAdded())
                return productPrice.onComplete();
            }, (e) => {
                if (productPrice.onError)
                    productPrice.onError(e);
                return this.errorHandler.handleError(e, { alertUser: true });
            }))));
        });
        this.getSingleProduct = this.effect((productId$) => {
            return productId$.pipe(switchMap((productId) => this.productService.getSingleProduct(productId).pipe(tapResponse((product) => this.onSingleProduct(product), (e) => this.errorHandler.handleError(e)))));
        });
        this.getSingleProductWithPrice = this.effect((productId$) => {
            return productId$.pipe(switchMap((productId) => combineLatest([this.productService.getSingleProduct(productId), this.productPriceService.getAllProductPrices(productId)]).pipe(tapResponse(([product, prices]) => {
                this.onSingleProduct(product ? Object.assign(product, { prices }) : undefined);
            }, (e) => this.errorHandler.handleError(e)))));
        });
        this.deleteProduct = this.effect((deleteProduct$) => {
            return deleteProduct$.pipe(switchMap((deleteProduct) => this.productService.deleteProductPrice(deleteProduct.productId, deleteProduct.priceId).pipe(tapResponse((_) => {
                this.notificationService.popNotificationMessage(NOTIFICATION_MESSAGES.productRemoved());
                return this.onGetAllProductsWithPrice();
            }, (e) => this.errorHandler.handleError(e)))));
        });
        this.onListenCustomerSubscriptions = this.effect((_$) => {
            return this.customerKey$.pipe(tap(() => this.subscriptionsLoading(true)), filter(isNotNil), switchMap((customerKey) => this.productSubscriptionService.getCustomerSubscription(customerKey).pipe(tapResponse((subscriptions) => this.onCustomerSubscriptions(subscriptions), (e) => this.errorHandler.handleError(e)), finalize(() => {
                this.subscriptionsLoading(false);
                this.subscriptionsLoaded(true);
            }))));
        });
        this.onListenCustomerActiveSubscriptions = this.effect((_$) => {
            return this.customerKey$.pipe(tap(() => this.subscriptionsLoading(true)), filter(isNotNil), switchMap((customerKey) => this.productSubscriptionService.getCustomerActiveSubscription(customerKey).pipe(tapResponse((subscriptions) => this.onActiveCustomerSubscriptions(subscriptions), (e) => this.errorHandler.handleError(e)), finalize(() => {
                this.subscriptionsLoading(false);
                this.subscriptionsLoaded(true);
            }))));
        });
        this.onListenActiveSubscription = this.effect((_$) => {
            return _$.pipe(switchMap(() => this.productSubscriptionService.getActiveSubscription().pipe(tapResponse((subscriptions) => {
                return this.onActiveCustomerSubscriptions(subscriptions);
            }, (e) => this.errorHandler.handleError(e)))));
        });
        this.cancelSubscription = this.effect((subscriptionId$) => {
            return subscriptionId$.pipe(switchMap((subscriptionId) => this.productSubscriptionService.updateSubscriptionStatus(subscriptionId).pipe(tapResponse((_) => {
                this.notificationService.popNotificationMessage(NOTIFICATION_MESSAGES.cancelSubscription());
                return this.onListenActiveSubscription();
            }, (e) => this.errorHandler.handleError(e)))));
        });
        this.reEnableSubscription = this.effect((subscriptionId$) => {
            return subscriptionId$.pipe(switchMap((subscriptionId) => this.productSubscriptionService.updateSubscriptionStatus(subscriptionId, false).pipe(tapResponse((_) => {
                this.notificationService.popNotificationMessage(NOTIFICATION_MESSAGES.reEnableSubscription());
                return this.onListenActiveSubscription();
            }, (e) => this.errorHandler.handleError(e)))));
        });
        this.updatePaymentMethodOfSubscription = this.effect((subscriptionDetail$) => {
            return subscriptionDetail$.pipe(switchMap(({ subscriptionId, defaultPaymentMethod }) => this.productSubscriptionService.updateSubscriptionPaymentMethod(subscriptionId, defaultPaymentMethod).pipe(tapResponse((_) => {
                this.notificationService.popNotificationMessage(NOTIFICATION_MESSAGES.updatePaymentMethodOfSubscription());
                return this.onListenActiveSubscription();
            }, (e) => this.errorHandler.handleError(e)))));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductStore, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductStore, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });
const NOTIFICATION_MESSAGES = {
    productAdded: () => ({
        title: 'New product added',
        message: 'New product has been added successfully',
        type: 'success',
    }),
    productUpdated: () => ({
        title: 'Product updated',
        message: 'The product has been updated successfully',
        type: 'success',
    }),
    productRemoved: () => ({
        title: 'Product removed',
        message: 'Product has been removed successfully',
        type: 'success',
    }),
    cancelSubscription: () => ({
        title: 'Subscription cancelled',
        message: 'Subscription has been cancelled successfully',
        type: 'success',
    }),
    reEnableSubscription: () => ({
        title: 'Subscription re-enabled',
        message: 'Subscription has been re-enabled successfully',
        type: 'success',
    }),
    updatePaymentMethodOfSubscription: () => ({
        title: 'Update payment method',
        message: 'Payment method has been updated successfully',
        type: 'success',
    }),
};

class PlatformAdminCustomerProductListHeaderComponent {
    constructor() {
        this.showButton = true;
        this.addClicked = new EventEmitter();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminCustomerProductListHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: PlatformAdminCustomerProductListHeaderComponent, selector: "platform-admin-customer-products-list-header", inputs: { headerText: "headerText", buttonText: "buttonText", showButton: "showButton" }, outputs: { addClicked: "addClicked" }, ngImport: i0, template: `
    <div class="e11-flex e11-w-full e11-mb-6 e11-gap-2 e11-flex-col lg:e11-flex-row">
      <div class="e11-grow">
        <h3>{{ headerText | translate }}</h3>
      </div>
      <div class="e11-flex lg:e11-justify-end ">
        <e11-button
          *ngIf="showButton"
          color="primary-accent"
          [size]="'sm'"
          [icon]="'add'"
          [iconPosition]="'left'"
          [iconSize]="'md-18'"
          value="{{ buttonText | translate }}"
          (click)="addClicked.emit()"
        >
        </e11-button>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$3.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminCustomerProductListHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'platform-admin-customer-products-list-header',
                    template: `
    <div class="e11-flex e11-w-full e11-mb-6 e11-gap-2 e11-flex-col lg:e11-flex-row">
      <div class="e11-grow">
        <h3>{{ headerText | translate }}</h3>
      </div>
      <div class="e11-flex lg:e11-justify-end ">
        <e11-button
          *ngIf="showButton"
          color="primary-accent"
          [size]="'sm'"
          [icon]="'add'"
          [iconPosition]="'left'"
          [iconSize]="'md-18'"
          value="{{ buttonText | translate }}"
          (click)="addClicked.emit()"
        >
        </e11-button>
      </div>
    </div>
  `,
                }]
        }], propDecorators: { headerText: [{
                type: Input,
                args: [{ required: true }]
            }], buttonText: [{
                type: Input,
                args: [{ required: true }]
            }], showButton: [{
                type: Input
            }], addClicked: [{
                type: Output
            }] } });

const customerSubscriptionsTableOptions = {
    keyField: 'id',
    selectable: false,
    enableDynamicFields: false,
    enableFilter: false,
    enableSorting: false,
    paginationSettings: {
        enabled: true,
        serverSide: false,
        pageLinks: 5,
        rowCount: 10,
        rowsPerPageOptions: [25, 50, 100],
    },
    columns: [
        {
            key: 'productName',
            title: 'Product Name',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
        {
            key: 'sku',
            title: 'SKU',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
        {
            key: 'autoRenew',
            title: 'Auto Renew',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
        {
            key: 'status',
            title: 'Status',
            type: DataType.CharField,
            visible: true,
            optional: false,
            enableFilter: true,
        },
        {
            key: 'subscriptionAction',
            title: 'Action',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
    ],
};

class SdkSubscriptionListComponent {
    constructor() {
        this.initialGridOptions = customerSubscriptionsTableOptions;
        this.loadingCustomerSubscription = false;
        this.customerSubscriptions = [];
        this.isPlatformAdmin = false;
        this.cancelSubscription = new EventEmitter();
    }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkSubscriptionListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkSubscriptionListComponent, selector: "sdk-subscription-list", inputs: { loadingCustomerSubscription: "loadingCustomerSubscription", customerSubscriptions: "customerSubscriptions", isPlatformAdmin: "isPlatformAdmin" }, outputs: { cancelSubscription: "cancelSubscription" }, ngImport: i0, template: ` <e11-datatable [initialGridOptions]="initialGridOptions" [loading]="loadingCustomerSubscription" [records]="customerSubscriptions">
    <ng-template e11DatatableTemplate="productName" let-row>
      <div class="e11-flex e11-items-center">
        <span> {{ row.productName }} </span>
        <e11-tooltip [tooltipText]="'This is a system product.' + (!isPlatformAdmin ? ' This subscription cannot be cancelled.' : '') | translate">
          <span *ngIf="row.isSystemProduct" class="e11-text-skin-primary-accent e11-cursor-default material-icons e11-ml-2 md-18">settings</span>
        </e11-tooltip>
      </div>
    </ng-template>
    <ng-template e11DatatableTemplate="sku" let-row>
      {{ row.priceInfo?.sku ?? 'N/A' }}
    </ng-template>
    <ng-template e11DatatableTemplate="autoRenew" let-row>
      {{ row.autoRenew ? 'Yes' : 'No' }}
    </ng-template>
    <ng-template e11DatatableTemplate="subscriptionAction" let-row>
      <a
        role="button"
        *ngIf="!row.isSystemProduct || isPlatformAdmin"
        class=" e11-text-skin-primary-accent"
        [ngClass]="{ 'e11-cursor-not-allowed': false }"
        e11ConfirmationBox
        [confirmationBoxContent]="confirmationBoxText"
        [componentInput]=" {
          confirmText: 'Cancel Subscription' | translate,
          cancelText: 'Cancel' | translate,
          confirmValueWithInputBox: false,
        }"
        (confirmed)="cancelSubscription.emit(row)"
        >{{ 'Cancel Subscription' | translate }}
      </a>
      <ng-template #confirmationBoxText>
        <p>{{ 'Are you sure you want to cancel this subscription?' | translate }}</p>
        <p class="e11-text-skin-error" *ngIf="row.isSystemProduct">
          {{
            "WARNING: This is a system product! Removing this subscription will result in serious disruptions for this customer's users" | translate
          }}
        </p>
      </ng-template>
    </ng-template>
  </e11-datatable>`, isInline: true, styles: [""], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$1.E11DataTableComponent, selector: "e11-datatable", inputs: ["initialGridOptions", "records", "selectedRecords", "isAllSelected", "loading", "striped"], outputs: ["selectedRecordsChange", "isAllSelectedChange", "bulkOperationsChecked"] }, { kind: "directive", type: i2$1.E11DatatableTemplate, selector: "[e11DatatableTemplate]", inputs: ["type", "e11DatatableTemplate"] }, { kind: "directive", type: i3$1.E11ConfirmationBoxDirective, selector: "[e11ConfirmationBox]", inputs: ["confirmationBoxContent", "componentInput"], outputs: ["confirmed", "canceled"] }, { kind: "component", type: i4$1.E11TooltipComponent, selector: "e11-tooltip", inputs: ["tooltipText", "tooltipClassOverrides", "hideTooltip", "color", "tooltipContent", "position", "interactive", "maxWidth", "delay"], outputs: ["tooltipClick"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkSubscriptionListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-subscription-list', template: ` <e11-datatable [initialGridOptions]="initialGridOptions" [loading]="loadingCustomerSubscription" [records]="customerSubscriptions">
    <ng-template e11DatatableTemplate="productName" let-row>
      <div class="e11-flex e11-items-center">
        <span> {{ row.productName }} </span>
        <e11-tooltip [tooltipText]="'This is a system product.' + (!isPlatformAdmin ? ' This subscription cannot be cancelled.' : '') | translate">
          <span *ngIf="row.isSystemProduct" class="e11-text-skin-primary-accent e11-cursor-default material-icons e11-ml-2 md-18">settings</span>
        </e11-tooltip>
      </div>
    </ng-template>
    <ng-template e11DatatableTemplate="sku" let-row>
      {{ row.priceInfo?.sku ?? 'N/A' }}
    </ng-template>
    <ng-template e11DatatableTemplate="autoRenew" let-row>
      {{ row.autoRenew ? 'Yes' : 'No' }}
    </ng-template>
    <ng-template e11DatatableTemplate="subscriptionAction" let-row>
      <a
        role="button"
        *ngIf="!row.isSystemProduct || isPlatformAdmin"
        class=" e11-text-skin-primary-accent"
        [ngClass]="{ 'e11-cursor-not-allowed': false }"
        e11ConfirmationBox
        [confirmationBoxContent]="confirmationBoxText"
        [componentInput]=" {
          confirmText: 'Cancel Subscription' | translate,
          cancelText: 'Cancel' | translate,
          confirmValueWithInputBox: false,
        }"
        (confirmed)="cancelSubscription.emit(row)"
        >{{ 'Cancel Subscription' | translate }}
      </a>
      <ng-template #confirmationBoxText>
        <p>{{ 'Are you sure you want to cancel this subscription?' | translate }}</p>
        <p class="e11-text-skin-error" *ngIf="row.isSystemProduct">
          {{
            "WARNING: This is a system product! Removing this subscription will result in serious disruptions for this customer's users" | translate
          }}
        </p>
      </ng-template>
    </ng-template>
  </e11-datatable>` }]
        }], propDecorators: { loadingCustomerSubscription: [{
                type: Input,
                args: [{ required: true }]
            }], customerSubscriptions: [{
                type: Input,
                args: [{ required: true }]
            }], isPlatformAdmin: [{
                type: Input
            }], cancelSubscription: [{
                type: Output
            }] } });

function generateDynamicFormObjectForProducts({ productOptions }) {
    return e11FormFor()([
        e11FormLayoutFullWidth([
            e11FieldInputSelect('productId', 'Product Group', productOptions, undefined, {
                validatorOrOpts: [Validators.required],
            }),
        ]),
    ]);
}
function generateDynamicFormObject({ prices }) {
    return e11FormFor()([
        e11FormLayoutFullWidth([
            e11FieldInputSelect('priceId', 'Product', prices ?? [], undefined, {
                validatorOrOpts: [Validators.required],
                isDisabled: !prices?.length,
            }),
        ]),
        e11FormLayoutInlineGrid([
            e11FieldInputSelect('status', 'status', productSubscriptionStatusOptions, undefined, { validatorOrOpts: [Validators.required] }),
            e11FieldInputCheckBox('autoRenew', 'Auto Renew', [{ name: 'Auto Renew', value: 'autoRenew' }], undefined, {
                validatorOrOpts: [Validators.required],
            }),
        ]),
    ]);
}
// @TODO Logic to divide the amount by 100 should be moved to a directive or down in the selector
const productPriceOptions = (price) => ({
    name: `${price?.sku ?? ''} (${(price?.amount || 0) / AMOUNT_MULTIPLIER}${price?.currencyCode})`,
    value: price.id,
});

class SdkSubscribeToProductComponent {
    constructor() {
        this.subscribeToProduct = new EventEmitter();
        this.fieldGroups = generateDynamicFormObject({});
        this.productPricesCache = {};
    }
    set recurringProducts(products) {
        this.updateProductOptions(products);
    }
    ngOnInit() {
        if (!this._customerKey) {
            throw e11Error(customerKeyNotPassed$1);
        }
    }
    updateProductOptions(products) {
        this.productPricesCache = products.reduce((acc, { id, prices }) => ({ ...acc, [id]: prices ?? [] }), {});
        this.productFieldGroups = generateDynamicFormObjectForProducts({
            productOptions: products.map((product) => ({ name: product.name, value: product.id })),
        });
    }
    onProductSelectionChange(productId) {
        if (productId && this.productPricesCache[productId]) {
            this.selectedProductId = productId;
            this.fieldGroups = generateDynamicFormObject({
                prices: this.productPricesCache[productId].map(productPriceOptions),
            });
        }
    }
    addSubscription(subscription) {
        if (!this.selectedProductId) {
            throw e11Error(productNotSelected);
        }
        this.subscribeToProduct.emit({
            ...subscription,
            productId: this.selectedProductId,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkSubscribeToProductComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkSubscribeToProductComponent, selector: "sdk-subscribe-to-product", inputs: { _customerKey: "_customerKey", recurringProducts: "recurringProducts" }, outputs: { subscribeToProduct: "subscribeToProduct" }, viewQueries: [{ propertyName: "productSubscriptionDetailFormBuilder", first: true, predicate: ["productSubscriptionDetailFormBuilder"], descendants: true }, { propertyName: "productSubscriptionFormBuilder", first: true, predicate: ["productSubscriptionFormBuilder"], descendants: true }], ngImport: i0, template: `<e11-dynamic-form-builder
      #productSubscriptionFormBuilder
      *ngIf="productFieldGroups"
      [fieldGroups]="productFieldGroups"
      [hideSubmitMenu]="true"
      (valueChanges)="onProductSelectionChange($event.productId)"
    ></e11-dynamic-form-builder>

    <e11-dynamic-form-builder
      #productSubscriptionDetailFormBuilder
      *ngIf="fieldGroups"
      [fieldGroups]="fieldGroups"
      (onSubmit)="addSubscription($event)"
    ></e11-dynamic-form-builder>`, isInline: true, styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4$2.E11DynamicFormBuilderComponent, selector: "e11-dynamic-form-builder", inputs: ["initialValue", "hideSubmitMenu", "loading", "disabled", "disabledText", "submitButtonConfig", "cancelButtonConfig", "deleteButtonConfig", "cancelLinkConfig", "showCancelLink", "showCancelButton", "showDeleteButton", "submitButtonTitle", "cancelLinkTitle", "cancelButtonTitle", "deleteButtonTitle", "buttonColorSubmit", "buttonColorCancel", "buttonColorDelete", "fieldGroups", "errorMessageOverrides"], outputs: ["valueChanges", "onSubmit", "onCancel", "onDelete"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkSubscribeToProductComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-subscribe-to-product', template: `<e11-dynamic-form-builder
      #productSubscriptionFormBuilder
      *ngIf="productFieldGroups"
      [fieldGroups]="productFieldGroups"
      [hideSubmitMenu]="true"
      (valueChanges)="onProductSelectionChange($event.productId)"
    ></e11-dynamic-form-builder>

    <e11-dynamic-form-builder
      #productSubscriptionDetailFormBuilder
      *ngIf="fieldGroups"
      [fieldGroups]="fieldGroups"
      (onSubmit)="addSubscription($event)"
    ></e11-dynamic-form-builder>` }]
        }], propDecorators: { productSubscriptionDetailFormBuilder: [{
                type: ViewChild,
                args: ['productSubscriptionDetailFormBuilder']
            }], productSubscriptionFormBuilder: [{
                type: ViewChild,
                args: ['productSubscriptionFormBuilder']
            }], _customerKey: [{
                type: Input
            }], recurringProducts: [{
                type: Input
            }], subscribeToProduct: [{
                type: Output
            }] } });
const customerKeyNotPassed$1 = {
    type: 'payment-customer-key-not-passed',
    title: 'Customer key not passed',
};
const productNotSelected = {
    type: 'product-not-passed',
    title: 'Product must be selected to subscribe',
};

class PlatformAdminCustomerProductsComponent {
    constructor() {
        this.platformAdminCustomerPaymentsStore = inject(PlatformAdminCustomerPaymentsStore);
        this.productStore = inject(ProductStore);
        this.e11ErrorService = inject(E11ErrorHandlerService);
        this.customerSubscriptions$ = this.platformAdminCustomerPaymentsStore.customerSubscriptions$;
        this.loadingCustomerSubscription$ = this.platformAdminCustomerPaymentsStore.loadingCustomerSubscriptions$;
        this.recurringProducts$ = this.productStore.recurringProducts$;
    }
    set selectedCustomerKey(value) {
        this.platformAdminCustomerPaymentsStore.getPlatformAdminCustomerSubscriptions(value);
        this._customerKey = value;
    }
    ngOnInit() {
        this.productStore.getAllEnabledProducts();
    }
    addSubscription(subscription) {
        if (!this._customerKey)
            throw e11Error(customerKeyNotPassed);
        this.platformAdminCustomerPaymentsStore.addCustomerSubscription({
            customerKey: this._customerKey,
            data: subscription,
            onComplete: () => {
                this.modalAddSubscription.close();
            },
        });
    }
    cancelSubscription(subscription) {
        if (!this._customerKey)
            throw e11Error(customerKeyNotPassed);
        this.platformAdminCustomerPaymentsStore.cancelCustomerSubscription({
            subscriptionId: subscription.id,
            customerKey: this._customerKey,
            onComplete: () => { },
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminCustomerProductsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: PlatformAdminCustomerProductsComponent, selector: "platform-admin-customer-products", inputs: { selectedCustomerKey: "selectedCustomerKey" }, viewQueries: [{ propertyName: "modalAddSubscription", first: true, predicate: ["modalAddSubscription"], descendants: true }], ngImport: i0, template: `
    <ng-container *ngrxLet="recurringProducts$ as recurringProducts">
      <platform-admin-customer-products-list-header
        [buttonText]="'Subscribe to Product'"
        [headerText]="'Subscriptions'"
        (addClicked)="modalAddSubscription.open()"
      ></platform-admin-customer-products-list-header>
      <sdk-subscription-list
        [isPlatformAdmin]="true"
        [customerSubscriptions]="(customerSubscriptions$ | async) || []"
        [loadingCustomerSubscription]="(loadingCustomerSubscription$ | async) || false"
        (cancelSubscription)="cancelSubscription($event)"
      ></sdk-subscription-list>
      <e11-modal
        #modalAddSubscription
        width="lg"
        [bodyHeight]="'auto'"
        title="{{ 'Subscribe to product' | translate }}"
        [showHeader]="true"
        [showFooter]="false"
      >
        <ng-template e11Template="Body" class="e11-mb-8">
          <sdk-subscribe-to-product
            [recurringProducts]="recurringProducts"
            [_customerKey]="_customerKey"
            (subscribeToProduct)="addSubscription($event)"
          ></sdk-subscribe-to-product>
        </ng-template>
      </e11-modal>
    </ng-container>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "directive", type: i2$2.E11Template, selector: "[e11Template]", inputs: ["type", "e11Template"] }, { kind: "component", type: i3$2.E11ModalComponent, selector: "e11-modal", inputs: ["title", "width", "bodyHeight", "titleAlign", "hideCloseButton", "closer", "bodyClassOverrides", "bodyClass", "modalClassOverrides", "modalClass", "contentClassOverrides", "contentClass", "headerClassOverrides", "headerClass", "footerClassOverrides", "footerClass", "showFooter", "showHeader", "darkMode", "closeOnEscape", "closeOnClickOutside", "headerCTAs", "hasBackdrop", "backdropStyle"], outputs: ["modalOpened", "onModalOpened", "modalClosed", "onModalClosed", "headerCTAClicked", "onHeaderCTAClicked"] }, { kind: "component", type: PlatformAdminCustomerProductListHeaderComponent, selector: "platform-admin-customer-products-list-header", inputs: ["headerText", "buttonText", "showButton"], outputs: ["addClicked"] }, { kind: "component", type: SdkSubscriptionListComponent, selector: "sdk-subscription-list", inputs: ["loadingCustomerSubscription", "customerSubscriptions", "isPlatformAdmin"], outputs: ["cancelSubscription"] }, { kind: "component", type: SdkSubscribeToProductComponent, selector: "sdk-subscribe-to-product", inputs: ["_customerKey", "recurringProducts"], outputs: ["subscribeToProduct"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminCustomerProductsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'platform-admin-customer-products',
                    template: `
    <ng-container *ngrxLet="recurringProducts$ as recurringProducts">
      <platform-admin-customer-products-list-header
        [buttonText]="'Subscribe to Product'"
        [headerText]="'Subscriptions'"
        (addClicked)="modalAddSubscription.open()"
      ></platform-admin-customer-products-list-header>
      <sdk-subscription-list
        [isPlatformAdmin]="true"
        [customerSubscriptions]="(customerSubscriptions$ | async) || []"
        [loadingCustomerSubscription]="(loadingCustomerSubscription$ | async) || false"
        (cancelSubscription)="cancelSubscription($event)"
      ></sdk-subscription-list>
      <e11-modal
        #modalAddSubscription
        width="lg"
        [bodyHeight]="'auto'"
        title="{{ 'Subscribe to product' | translate }}"
        [showHeader]="true"
        [showFooter]="false"
      >
        <ng-template e11Template="Body" class="e11-mb-8">
          <sdk-subscribe-to-product
            [recurringProducts]="recurringProducts"
            [_customerKey]="_customerKey"
            (subscribeToProduct)="addSubscription($event)"
          ></sdk-subscribe-to-product>
        </ng-template>
      </e11-modal>
    </ng-container>
  `,
                }]
        }], propDecorators: { modalAddSubscription: [{
                type: ViewChild,
                args: ['modalAddSubscription']
            }], selectedCustomerKey: [{
                type: Input,
                args: [{ required: true }]
            }] } });
const customerKeyNotPassed = {
    type: 'payment-customer-key-not-passed',
    title: 'Customer key not passed',
};

class PlatformAdminCustomerInvoiceComponent {
    // platformAdminCustomerPaymentsStore = inject(PlatformAdminCustomerPaymentsStore)
    // billingInvoices$ = this.platformAdminCustomerPaymentsStore.customerInvoices$
    set selectedCustomerKey(value) {
        this._selectedCustomerKey = value;
        // this.platformAdminCustomerPaymentsStore.getCustomerInvoices(value)
    }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminCustomerInvoiceComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: PlatformAdminCustomerInvoiceComponent, selector: "platform-admin-customer-invoices", inputs: { selectedCustomerKey: "selectedCustomerKey" }, ngImport: i0, template: `
    <platform-admin-customer-products [selectedCustomerKey]="_selectedCustomerKey"></platform-admin-customer-products>

    <!-- temporarily comment out until the billing history is implemented -->
    <!-- <platform-admin-billing-history [selectedCustomerKey]="_selectedCustomerKey"></platform-admin-billing-history> -->
  `, isInline: true, dependencies: [{ kind: "component", type: PlatformAdminCustomerProductsComponent, selector: "platform-admin-customer-products", inputs: ["selectedCustomerKey"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminCustomerInvoiceComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'platform-admin-customer-invoices',
                    template: `
    <platform-admin-customer-products [selectedCustomerKey]="_selectedCustomerKey"></platform-admin-customer-products>

    <!-- temporarily comment out until the billing history is implemented -->
    <!-- <platform-admin-billing-history [selectedCustomerKey]="_selectedCustomerKey"></platform-admin-billing-history> -->
  `,
                }]
        }], propDecorators: { selectedCustomerKey: [{
                type: Input,
                args: [{ required: true }]
            }] } });

class PlatformAdminBillingHistoryHeaderComponent {
    constructor() {
        this.addClicked = new EventEmitter();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminBillingHistoryHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: PlatformAdminBillingHistoryHeaderComponent, selector: "platform-admin-billing-history-header", inputs: { headerText: "headerText" }, outputs: { addClicked: "addClicked" }, ngImport: i0, template: `
    <div class="e11-flex e11-w-full e11-mb-6 e11-gap-2 e11-flex-col lg:e11-flex-row">
      <div class="e11-grow">
        <h3>{{ headerText | translate }}</h3>
      </div>
      <div class="e11-flex lg:e11-justify-end "></div>
    </div>
  `, isInline: true, dependencies: [{ kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminBillingHistoryHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'platform-admin-billing-history-header',
                    template: `
    <div class="e11-flex e11-w-full e11-mb-6 e11-gap-2 e11-flex-col lg:e11-flex-row">
      <div class="e11-grow">
        <h3>{{ headerText | translate }}</h3>
      </div>
      <div class="e11-flex lg:e11-justify-end "></div>
    </div>
  `,
                }]
        }], propDecorators: { headerText: [{
                type: Input,
                args: [{ required: true }]
            }], addClicked: [{
                type: Output
            }] } });

const platformAdminBillingTableOptions = {
    keyField: 'id',
    selectable: false,
    enableDynamicFields: false,
    enableFilter: false,
    enableSorting: false,
    paginationSettings: {
        enabled: true,
        serverSide: false,
        pageLinks: 5,
        rowCount: 10,
        rowsPerPageOptions: [25, 50, 100],
    },
    columns: [
        {
            key: 'date',
            title: 'Due Date',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
        {
            key: 'description',
            title: 'Description',
            type: DataType.CharField,
            visible: true,
            optional: false,
        },
        {
            key: 'amount',
            title: 'Amount',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
        {
            key: 'invoiceStatus',
            title: 'Status',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
            enableFilter: true,
        },
        {
            key: 'invoiceAction',
            title: 'Action',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
    ],
};

class PlatformAdminMarkInvoicePaidComponent {
    constructor() {
        // @TODO Logic to divide the amount by 100 should be moved to a directive or down in the selector
        this.AMOUNT_MULTIPLIER = AMOUNT_MULTIPLIER;
    }
    set selectedInvoice(value) {
        this._selectedInvoice = value;
    }
    get isPayable() {
        return this._selectedInvoice.invoiceStatus && ['open', 'overdue'].includes(this._selectedInvoice.invoiceStatus);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminMarkInvoicePaidComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: PlatformAdminMarkInvoicePaidComponent, selector: "platform-admin-mark-invoice-paid", inputs: { selectedInvoice: "selectedInvoice" }, ngImport: i0, template: ` <a
      role="button"
      class=" e11-text-skin-primary-accent"
      [ngClass]="{ 'e11-cursor-not-allowed': isPayable }"
      [ariaDisabled]="isPayable"
      (click)="!isPayable && modalMarkPaid.open()"
      >{{ 'Mark as paid' | translate }}
    </a>
    <e11-modal #modalMarkPaid width="lg" [bodyHeight]="'auto'" title="{{ 'Edit product' | translate }}" [showHeader]="true" [showFooter]="false">
      <ng-template e11Template="Body" class="e11-mb-8"> </ng-template>
    </e11-modal>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2$2.E11Template, selector: "[e11Template]", inputs: ["type", "e11Template"] }, { kind: "component", type: i3$2.E11ModalComponent, selector: "e11-modal", inputs: ["title", "width", "bodyHeight", "titleAlign", "hideCloseButton", "closer", "bodyClassOverrides", "bodyClass", "modalClassOverrides", "modalClass", "contentClassOverrides", "contentClass", "headerClassOverrides", "headerClass", "footerClassOverrides", "footerClass", "showFooter", "showHeader", "darkMode", "closeOnEscape", "closeOnClickOutside", "headerCTAs", "hasBackdrop", "backdropStyle"], outputs: ["modalOpened", "onModalOpened", "modalClosed", "onModalClosed", "headerCTAClicked", "onHeaderCTAClicked"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminMarkInvoicePaidComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'platform-admin-mark-invoice-paid',
                    template: ` <a
      role="button"
      class=" e11-text-skin-primary-accent"
      [ngClass]="{ 'e11-cursor-not-allowed': isPayable }"
      [ariaDisabled]="isPayable"
      (click)="!isPayable && modalMarkPaid.open()"
      >{{ 'Mark as paid' | translate }}
    </a>
    <e11-modal #modalMarkPaid width="lg" [bodyHeight]="'auto'" title="{{ 'Edit product' | translate }}" [showHeader]="true" [showFooter]="false">
      <ng-template e11Template="Body" class="e11-mb-8"> </ng-template>
    </e11-modal>`,
                }]
        }], propDecorators: { selectedInvoice: [{
                type: Input,
                args: [{ required: true }]
            }] } });

class PlatformAdminBillingHistoryComponent {
    set selectedCustomerKey(value) {
        this.platformAdminCustomerPaymentsStore.getCustomerInvoices(value);
    }
    constructor() {
        // @TODO Logic to divide the amount by 100 should be moved to a directive or down in the selector
        this.AMOUNT_MULTIPLIER = AMOUNT_MULTIPLIER;
        this.platformAdminCustomerPaymentsStore = inject(PlatformAdminCustomerPaymentsStore);
        this.initialGridOptions = platformAdminBillingTableOptions;
        this.billingInvoices$ = this.platformAdminCustomerPaymentsStore.customerInvoices$;
        this.loadingCustomerInvoices$ = this.platformAdminCustomerPaymentsStore.loadingCustomerInvoices$;
    }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminBillingHistoryComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: PlatformAdminBillingHistoryComponent, selector: "platform-admin-billing-history", inputs: { selectedCustomerKey: "selectedCustomerKey" }, ngImport: i0, template: ` <platform-admin-billing-history-header [headerText]="'Invoices'"></platform-admin-billing-history-header>
    <e11-datatable
      [initialGridOptions]="initialGridOptions"
      [loading]="(loadingCustomerInvoices$ | async) || false"
      [records]="(billingInvoices$ | async) || []"
    >
      <ng-template e11DatatableTemplate="date" let-row>
        {{ row.dueDate | date: 'MMMM dd, yyyy' }}
      </ng-template>
      <ng-template e11DatatableTemplate="invoiceStatus" let-row>
        <div>
          <e11-badge [inline]="true" [value]="row.invoiceStatus ?? ''" [color]="'warning'" [size]="'sm'"> </e11-badge>
        </div>
      </ng-template>
      <ng-template e11DatatableTemplate="amount" let-row>
        {{ (row.totalDue + row.tax) / AMOUNT_MULTIPLIER | currency: row.currency }} (<span
          class="e11-underline e11-font-bold e11-cursor-pointer e11-text-skin-primary"
        >
          {{ row.orders.length }} </span
        >)
      </ng-template>
      <ng-template e11DatatableTemplate="invoiceAction" let-row>
        <platform-admin-mark-invoice-paid [selectedInvoice]="row"></platform-admin-mark-invoice-paid>
      </ng-template>
    </e11-datatable>`, isInline: true, dependencies: [{ kind: "component", type: i2$1.E11DataTableComponent, selector: "e11-datatable", inputs: ["initialGridOptions", "records", "selectedRecords", "isAllSelected", "loading", "striped"], outputs: ["selectedRecordsChange", "isAllSelectedChange", "bulkOperationsChecked"] }, { kind: "directive", type: i2$1.E11DatatableTemplate, selector: "[e11DatatableTemplate]", inputs: ["type", "e11DatatableTemplate"] }, { kind: "component", type: i2$4.E11BadgeComponent, selector: "e11-badge", inputs: ["color", "shape", "size", "inTag", "classOverrides", "class", "posAdjustY", "posAdjustX", "containerClassOverrides", "containerClass", "inline", "titleText", "titleTextPosition", "value", "limit"] }, { kind: "component", type: PlatformAdminMarkInvoicePaidComponent, selector: "platform-admin-mark-invoice-paid", inputs: ["selectedInvoice"] }, { kind: "component", type: PlatformAdminBillingHistoryHeaderComponent, selector: "platform-admin-billing-history-header", inputs: ["headerText"], outputs: ["addClicked"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1.CurrencyPipe, name: "currency" }, { kind: "pipe", type: i1.DatePipe, name: "date" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PlatformAdminBillingHistoryComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'platform-admin-billing-history',
                    template: ` <platform-admin-billing-history-header [headerText]="'Invoices'"></platform-admin-billing-history-header>
    <e11-datatable
      [initialGridOptions]="initialGridOptions"
      [loading]="(loadingCustomerInvoices$ | async) || false"
      [records]="(billingInvoices$ | async) || []"
    >
      <ng-template e11DatatableTemplate="date" let-row>
        {{ row.dueDate | date: 'MMMM dd, yyyy' }}
      </ng-template>
      <ng-template e11DatatableTemplate="invoiceStatus" let-row>
        <div>
          <e11-badge [inline]="true" [value]="row.invoiceStatus ?? ''" [color]="'warning'" [size]="'sm'"> </e11-badge>
        </div>
      </ng-template>
      <ng-template e11DatatableTemplate="amount" let-row>
        {{ (row.totalDue + row.tax) / AMOUNT_MULTIPLIER | currency: row.currency }} (<span
          class="e11-underline e11-font-bold e11-cursor-pointer e11-text-skin-primary"
        >
          {{ row.orders.length }} </span
        >)
      </ng-template>
      <ng-template e11DatatableTemplate="invoiceAction" let-row>
        <platform-admin-mark-invoice-paid [selectedInvoice]="row"></platform-admin-mark-invoice-paid>
      </ng-template>
    </e11-datatable>`,
                }]
        }], ctorParameters: () => [], propDecorators: { selectedCustomerKey: [{
                type: Input
            }] } });

class CustomerClientSubscriptionsComponent {
    constructor() {
        this.productStore = inject(ProductStore);
        this.platformAdminCustomerPaymentsStore = inject(PlatformAdminCustomerPaymentsStore);
        this.customerSubscriptions$ = this.platformAdminCustomerPaymentsStore.customerSubscriptions$;
        this.loadingCustomerSubscription$ = this.platformAdminCustomerPaymentsStore.loadingCustomerSubscriptions$;
        this.tenantProducts$ = this.platformAdminCustomerPaymentsStore.tenantProductToProduct$;
        this.loadingTenantProducts$ = this.platformAdminCustomerPaymentsStore.loadingTenantProducts$;
    }
    set config(value) {
        const { customerKey, bootstrapTenantKey } = value;
        if (!customerKey || !bootstrapTenantKey)
            return;
        this._customerKey = customerKey;
        this._bootstrapTenantKey = bootstrapTenantKey;
        this.getCustomerSubscriptions(customerKey);
        this.getAvailableCurrentTenantProducts();
    }
    ngOnInit() { }
    getCustomerSubscriptions(customerKey) {
        this.platformAdminCustomerPaymentsStore.getCustomerSubscriptions(customerKey);
    }
    getAvailableCurrentTenantProducts() {
        this.platformAdminCustomerPaymentsStore.getTenantProducts();
    }
    cancelSubscription(subscription) {
        if (!this._customerKey)
            return;
        this.platformAdminCustomerPaymentsStore.cancelTenantCustomerSubscription({
            subscriptionId: subscription.id,
            customerKey: this._customerKey,
            onComplete: () => { },
        });
    }
    addSubscription(subscription) {
        if (!this._customerKey)
            return;
        this.platformAdminCustomerPaymentsStore.addTenantCustomerSubscription({
            customerKey: this._customerKey,
            data: subscription,
            onComplete: () => {
                this.modalAddSubscription.close();
            },
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: CustomerClientSubscriptionsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.9", type: CustomerClientSubscriptionsComponent, selector: "customer-client-subscriptions", inputs: { config: "config" }, viewQueries: [{ propertyName: "modalAddSubscription", first: true, predicate: ["modalAddSubscription"], descendants: true }], ngImport: i0, template: `<ng-container *ngrxLet="tenantProducts$ as tenantProducts">
    @if (_bootstrapTenantKey && _customerKey) {
    <platform-admin-customer-products-list-header
      [buttonText]="'Subscribe to Product'"
      [headerText]="'Subscriptions'"
      (addClicked)="modalAddSubscription.open()"
    ></platform-admin-customer-products-list-header>
    <sdk-subscription-list
      [customerSubscriptions]="(customerSubscriptions$ | async) || []"
      [loadingCustomerSubscription]="(loadingCustomerSubscription$ | async) || false"
      (cancelSubscription)="cancelSubscription($event)"
    ></sdk-subscription-list
    >}@else {
    <e11-empty-state
      [icon]="''"
      [border]="'solid'"
      [title]="'Subscription not available for this client'"
      [subtitle]="'It appears that there is no account provisioned for this client'"
      [clickable]="false"
    ></e11-empty-state>
    }

    <e11-modal
      #modalAddSubscription
      width="lg"
      [bodyHeight]="'auto'"
      title="{{ 'Subscribe to product' | translate }}"
      [showHeader]="true"
      [showFooter]="false"
    >
      <ng-template e11Template="Body" class="e11-mb-8">
        <sdk-subscribe-to-product
          [recurringProducts]="tenantProducts"
          [_customerKey]="_customerKey"
          (subscribeToProduct)="addSubscription($event)"
        ></sdk-subscribe-to-product>
      </ng-template>
    </e11-modal>
  </ng-container>`, isInline: true, styles: [""], dependencies: [{ kind: "directive", type: i1$1.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "directive", type: i2$2.E11Template, selector: "[e11Template]", inputs: ["type", "e11Template"] }, { kind: "component", type: i3$2.E11ModalComponent, selector: "e11-modal", inputs: ["title", "width", "bodyHeight", "titleAlign", "hideCloseButton", "closer", "bodyClassOverrides", "bodyClass", "modalClassOverrides", "modalClass", "contentClassOverrides", "contentClass", "headerClassOverrides", "headerClass", "footerClassOverrides", "footerClass", "showFooter", "showHeader", "darkMode", "closeOnEscape", "closeOnClickOutside", "headerCTAs", "hasBackdrop", "backdropStyle"], outputs: ["modalOpened", "onModalOpened", "modalClosed", "onModalClosed", "headerCTAClicked", "onHeaderCTAClicked"] }, { kind: "component", type: i4$3.E11EmptyStateComponent, selector: "e11-empty-state", inputs: ["icon", "iconType", "iconSize", "customImageUrl", "customImageAltText", "showButton", "borderColor", "buttonValue", "buttonColor", "buttonDisabled", "buttonIcon", "border", "title", "subtitle", "clickable", "disabled", "disabledText", "containerClass", "contentClass", "containerClassOverrides", "contentClassOverrides", "htmlSubtitle"], outputs: ["buttonClicked"] }, { kind: "component", type: PlatformAdminCustomerProductListHeaderComponent, selector: "platform-admin-customer-products-list-header", inputs: ["headerText", "buttonText", "showButton"], outputs: ["addClicked"] }, { kind: "component", type: SdkSubscriptionListComponent, selector: "sdk-subscription-list", inputs: ["loadingCustomerSubscription", "customerSubscriptions", "isPlatformAdmin"], outputs: ["cancelSubscription"] }, { kind: "component", type: SdkSubscribeToProductComponent, selector: "sdk-subscribe-to-product", inputs: ["_customerKey", "recurringProducts"], outputs: ["subscribeToProduct"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: CustomerClientSubscriptionsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'customer-client-subscriptions', template: `<ng-container *ngrxLet="tenantProducts$ as tenantProducts">
    @if (_bootstrapTenantKey && _customerKey) {
    <platform-admin-customer-products-list-header
      [buttonText]="'Subscribe to Product'"
      [headerText]="'Subscriptions'"
      (addClicked)="modalAddSubscription.open()"
    ></platform-admin-customer-products-list-header>
    <sdk-subscription-list
      [customerSubscriptions]="(customerSubscriptions$ | async) || []"
      [loadingCustomerSubscription]="(loadingCustomerSubscription$ | async) || false"
      (cancelSubscription)="cancelSubscription($event)"
    ></sdk-subscription-list
    >}@else {
    <e11-empty-state
      [icon]="''"
      [border]="'solid'"
      [title]="'Subscription not available for this client'"
      [subtitle]="'It appears that there is no account provisioned for this client'"
      [clickable]="false"
    ></e11-empty-state>
    }

    <e11-modal
      #modalAddSubscription
      width="lg"
      [bodyHeight]="'auto'"
      title="{{ 'Subscribe to product' | translate }}"
      [showHeader]="true"
      [showFooter]="false"
    >
      <ng-template e11Template="Body" class="e11-mb-8">
        <sdk-subscribe-to-product
          [recurringProducts]="tenantProducts"
          [_customerKey]="_customerKey"
          (subscribeToProduct)="addSubscription($event)"
        ></sdk-subscribe-to-product>
      </ng-template>
    </e11-modal>
  </ng-container>` }]
        }], propDecorators: { modalAddSubscription: [{
                type: ViewChild,
                args: ['modalAddSubscription']
            }], config: [{
                type: Input
            }] } });

const EXPORT_CARD_SVG_COMPONENTS = [
    AmexSVGComponent,
    DiscoverSVGComponent,
    JcbSVGComponent,
    MasterCardSVGComponent,
    UnionPaySVGComponent,
    VisaSVGComponent,
    CreditCardIconComponent,
];

const IMPORT_EXPORT_COMPONENTS$2 = [
    ...EXPORT_CARD_SVG_COMPONENTS,
    // SdkStripePaymentElementComponent
];
class SharedModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SharedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: SharedModule, declarations: [AmexSVGComponent, DiscoverSVGComponent, JcbSVGComponent, MasterCardSVGComponent, UnionPaySVGComponent, VisaSVGComponent, CreditCardIconComponent], imports: [TranslateModule, CommonModule, E11InputErrorsModule], exports: [AmexSVGComponent, DiscoverSVGComponent, JcbSVGComponent, MasterCardSVGComponent, UnionPaySVGComponent, VisaSVGComponent, CreditCardIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SharedModule, imports: [TranslateModule, CommonModule, E11InputErrorsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SharedModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [TranslateModule, CommonModule, E11InputErrorsModule],
                    exports: [...IMPORT_EXPORT_COMPONENTS$2],
                    declarations: [...IMPORT_EXPORT_COMPONENTS$2],
                    providers: [],
                }]
        }] });

const productPermissions = [];
const productForm = () => e11FormFor()([
    e11FormLayoutInlineGrid([
        e11FieldInputText('name', 'Product Name', undefined, { validatorOrOpts: [Validators.required] }),
        e11FieldInputSelect('paymentIntegration', 'Payment Provider', selectablePaymentIntegration, undefined, {
            validatorOrOpts: [Validators.required],
        }),
        e11FieldInputCheckBox('enabled', 'Is product enabled?', [{ name: 'enabled', value: 'enabled' }], true),
    ]),
    e11FormLayoutInlineGrid([e11FieldInputText('description', 'Description', undefined, { validatorOrOpts: [Validators.required] })]),
]);
const productPermissionForm = ({ permissionOptions }) => e11FormFor()([
    e11FormLayoutFullWidth([
        e11FieldInputCheckBox('permissions', 'Permissions', permissionOptions, undefined, {
            extraInputOption: { containerClass: 'e11-flex e11-gap-4 e11-flex-wrap', fieldClass: 'e11-w-1/5' },
        }),
    ]),
]);
const templateKeys$1 = { billingPeriod: 'billingPeriod' };
const productPriceForm = () => e11FormFor()([
    e11FormLayoutInlineGrid([
        e11FieldInputNumber('amount', 'Amount', undefined, { validatorOrOpts: [Validators.required] }),
        e11FieldInputAutocomplete('currencyCode', 'Currency code', COMMON_CURRENCY_CODES, DEFAULT_CURRENCY_CODE, {
            validatorOrOpts: [Validators.required],
        }),
        e11FieldInputText('sku', 'SKU', undefined, { validatorOrOpts: [Validators.required, Validators.pattern(SKU_REGEX)] }),
        e11FieldInputCheckBox('enabled', 'Is price active?', [{ name: 'enabled', value: 'enabled' }], true),
    ]),
    e11FormLayoutInlineGrid([
        e11FieldInputSelect('interval', 'Billing period', selectableRecurringPriceIntervals, RecurringPriceInterval.Month, {
            validatorOrOpts: [Validators.required],
        }),
        e11FieldInputNumber('intervalCount', 'Interval count', 1),
    ], { templateSpec: { templateKey: templateKeys$1.billingPeriod } }),
]);

const templateKeys = { billingPeriod: 'billingPeriod' };
class SdkProductAddPricesSubFormComponent {
    constructor() {
        this.templateKeys = templateKeys;
        this.priceFieldGroups = [];
    }
    createProductPriceForm() {
        return productPriceForm();
    }
    addProductPriceFormGroup() {
        this.priceFieldGroups.push({ fieldGroups: this.createProductPriceForm(), expanded: true });
    }
    removePriceFormGroup(index) {
        this.priceFieldGroups.splice(index, 1);
    }
    onSave() {
        const formValuesArray = this.productPriceFormBuilders.map((formBuilder, index) => {
            formBuilder.onSubmitClick();
            if (formBuilder.form.invalid) {
                this.priceFieldGroups[index].expanded = true;
                return null;
            }
            return this.transformPriceObject(formBuilder.form.value, index);
        });
        if (formValuesArray.every((formValue) => formValue !== null)) {
            // All forms are valid, return the array of form values
            return formValuesArray;
        }
        return null;
    }
    transformPriceObject(priceForm, index) {
        const correctedPriceObject = pick(priceForm, 'amount', 'currencyCode', 'enabled');
        return {
            ...correctedPriceObject,
            // @TODO Logic to divide the amount by 100 should be moved to a directive or down in the selector
            amount: +correctedPriceObject.amount * AMOUNT_MULTIPLIER,
            id: uuid(),
            recurring: {
                interval: priceForm.interval,
                intervalCount: +(priceForm.intervalCount || 0),
            },
            quantityTransform: {
                divideBy: DEFAULT_DIVIDE_BY,
                round: QuantityTransformRound.Up,
            },
            sku: priceForm.sku,
        };
    }
    reset() {
        this.priceFieldGroups = [
            {
                fieldGroups: this.createProductPriceForm(),
                expanded: true,
            },
        ];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductAddPricesSubFormComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkProductAddPricesSubFormComponent, selector: "sdk-payments-add-prices-sub-form", viewQueries: [{ propertyName: "productPriceFormBuilders", predicate: ["productPriceFormBuilder"], descendants: true }], ngImport: i0, template: `
    <div class="">
      <div class="e11-flex e11-justify-between">
        <h3>{{ 'Add Price information' | translate }}</h3>

        <e11-button [size]="'sm'" (click)="addProductPriceFormGroup()" value="{{ 'Add new price' | translate }}" [color]="'complimentary'">
        </e11-button>
      </div>
      <div role="list" class="e11-divide-y e11-divide-gray-200 ">
        <div class="e11-py-4"></div>
        <ng-container *ngFor="let config of priceFieldGroups; let i = index">
          <div class="e11-group e11-flex e11-flex-col e11-gap-2 e11-py-4" [tabindex]="i + 1">
            <div (click)="config.expanded = !config.expanded" class="e11-flex e11-w-full e11-cursor-pointer e11-items-center e11-justify-between">
              <p *ngIf="config.expanded">{{ 'Pricing details' | translate }}</p>
              <span *ngIf="!config.expanded" class="e11-text-skin-light">
                {{
                  productPriceFormBuilders?.get(i)?.form?.value?.amount || 0
                    | currency: productPriceFormBuilders?.get(i)?.form?.value?.currencyCode:true
                }}
                {{ productPriceFormBuilders?.get(i)?.form?.value?.currencyCode }} {{ 'per unit' | translate }} /
                {{ productPriceFormBuilders?.get(i)?.form?.value?.interval }}
              </span>
              <div class="e11-flex">
                <span class="material-icons-outlined e11-text-skin-dark/50 hover:e11-text-skin-dark" (click)="removePriceFormGroup(i)"
                  >delete_forever
                </span>
                <span class="material-icons-outlined"> {{ config.expanded ? 'expand_less' : 'expand_more' }} </span>
              </div>
            </div>
            <div
              class=" e11-h-auto  e11-items-center  e11-transition-all "
              [ngClass]="{
                'e11-visible e11-max-h-screen e11-opacity-100 e11-duration-1000': config.expanded,
                'e11-invisible e11-max-h-0 e11-opacity-0': !config.expanded
              }"
            >
              <e11-dynamic-form-builder #productPriceFormBuilder [hideSubmitMenu]="true" [fieldGroups]="config.fieldGroups">
                <ng-template e11Template="{{ templateKeys.billingPeriod }}">
                  <h4 class="e11-font-bold">{{ 'Billing period' | translate }}</h4>
                </ng-template>
              </e11-dynamic-form-builder>
            </div>
          </div>
        </ng-container>
        <div class="e11-py-4"></div>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$3.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "directive", type: i2$2.E11Template, selector: "[e11Template]", inputs: ["type", "e11Template"] }, { kind: "component", type: i4$2.E11DynamicFormBuilderComponent, selector: "e11-dynamic-form-builder", inputs: ["initialValue", "hideSubmitMenu", "loading", "disabled", "disabledText", "submitButtonConfig", "cancelButtonConfig", "deleteButtonConfig", "cancelLinkConfig", "showCancelLink", "showCancelButton", "showDeleteButton", "submitButtonTitle", "cancelLinkTitle", "cancelButtonTitle", "deleteButtonTitle", "buttonColorSubmit", "buttonColorCancel", "buttonColorDelete", "fieldGroups", "errorMessageOverrides"], outputs: ["valueChanges", "onSubmit", "onCancel", "onDelete"] }, { kind: "pipe", type: i1.CurrencyPipe, name: "currency" }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductAddPricesSubFormComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-payments-add-prices-sub-form',
                    template: `
    <div class="">
      <div class="e11-flex e11-justify-between">
        <h3>{{ 'Add Price information' | translate }}</h3>

        <e11-button [size]="'sm'" (click)="addProductPriceFormGroup()" value="{{ 'Add new price' | translate }}" [color]="'complimentary'">
        </e11-button>
      </div>
      <div role="list" class="e11-divide-y e11-divide-gray-200 ">
        <div class="e11-py-4"></div>
        <ng-container *ngFor="let config of priceFieldGroups; let i = index">
          <div class="e11-group e11-flex e11-flex-col e11-gap-2 e11-py-4" [tabindex]="i + 1">
            <div (click)="config.expanded = !config.expanded" class="e11-flex e11-w-full e11-cursor-pointer e11-items-center e11-justify-between">
              <p *ngIf="config.expanded">{{ 'Pricing details' | translate }}</p>
              <span *ngIf="!config.expanded" class="e11-text-skin-light">
                {{
                  productPriceFormBuilders?.get(i)?.form?.value?.amount || 0
                    | currency: productPriceFormBuilders?.get(i)?.form?.value?.currencyCode:true
                }}
                {{ productPriceFormBuilders?.get(i)?.form?.value?.currencyCode }} {{ 'per unit' | translate }} /
                {{ productPriceFormBuilders?.get(i)?.form?.value?.interval }}
              </span>
              <div class="e11-flex">
                <span class="material-icons-outlined e11-text-skin-dark/50 hover:e11-text-skin-dark" (click)="removePriceFormGroup(i)"
                  >delete_forever
                </span>
                <span class="material-icons-outlined"> {{ config.expanded ? 'expand_less' : 'expand_more' }} </span>
              </div>
            </div>
            <div
              class=" e11-h-auto  e11-items-center  e11-transition-all "
              [ngClass]="{
                'e11-visible e11-max-h-screen e11-opacity-100 e11-duration-1000': config.expanded,
                'e11-invisible e11-max-h-0 e11-opacity-0': !config.expanded
              }"
            >
              <e11-dynamic-form-builder #productPriceFormBuilder [hideSubmitMenu]="true" [fieldGroups]="config.fieldGroups">
                <ng-template e11Template="{{ templateKeys.billingPeriod }}">
                  <h4 class="e11-font-bold">{{ 'Billing period' | translate }}</h4>
                </ng-template>
              </e11-dynamic-form-builder>
            </div>
          </div>
        </ng-container>
        <div class="e11-py-4"></div>
      </div>
    </div>
  `,
                }]
        }], propDecorators: { productPriceFormBuilders: [{
                type: ViewChildren,
                args: ['productPriceFormBuilder']
            }] } });

class SdkProductAddPermissionsSubFormComponent {
    set initialPermissions(permissions) {
        this._initialPermissions = permissions;
        if (permissions)
            this.updatePermissionFieldGroups(permissions);
    }
    constructor(libConfig) {
        this.libConfig = libConfig;
        this.destroy$ = new Subject();
        this.permissibleRolePermissions = {};
        this.permissionFieldGroups = [];
    }
    async ngOnInit() {
        this.libConfig.config.productPermissions$.pipe(filter(isNotNil), takeUntil(this.destroy$)).subscribe((permissibleRolePermissions) => {
            this.permissibleRolePermissions = permissibleRolePermissions;
        });
        this.createPermissionPriceForm();
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    createPermissionPriceForm() {
        Object.entries(this.permissibleRolePermissions).forEach(([key, value]) => {
            const flatPermission = {};
            if (this.initialPermissions)
                this.initialPermissions[key].forEach((permission) => (flatPermission[permission] = true));
            const rolePermissionList = toSelectOptions(value);
            if (rolePermissionList.length === 0)
                return;
            this.permissionFieldGroups.push({
                fieldGroups: productPermissionForm({ permissionOptions: rolePermissionList }),
                expanded: Object.values(flatPermission).some((val) => val),
                allSelected: isEmpty(xor(value, Object.keys(flatPermission))),
                role: key,
                initialValue: flatPermission,
            });
        });
    }
    updatePermissionFieldGroups(permissions) {
        Object.entries(this.permissibleRolePermissions).forEach(([key, value]) => {
            const flatPermission = {};
            if (!permissions[key])
                return;
            permissions[key].forEach((permission) => (flatPermission[permission] = true));
            this.permissionFieldGroups = this.permissionFieldGroups.map((el) => el.role === key
                ? {
                    ...el,
                    initialValue: flatPermission,
                    expanded: Object.values(flatPermission).some((val) => val),
                    allSelected: isEmpty(xor(value, Object.keys(flatPermission))),
                }
                : el);
        });
    }
    updateForm(event, index) {
        const checkbox = this.productPermissionFormCheckboxes.get(index);
        if (!checkbox)
            return;
        const allChecked = Object.values(event).every((val) => val === true);
        if (checkbox._checked !== allChecked)
            checkbox.checked = allChecked;
    }
    stopClick(event) {
        event.stopPropagation();
    }
    selectAll(event, index) {
        event.stopPropagation();
        const roleForm = this.productPermissionFormBuilders.get(index);
        Object.keys(roleForm.form.controls).forEach((control) => {
            const formControl = roleForm?.form.get(control);
            if (!formControl)
                return;
            formControl.setValue(event.srcElement.checked);
        });
    }
    onSave() {
        const permissionsRecord = {};
        this.productPermissionFormBuilders.forEach((formBuilder, index) => {
            formBuilder.onSubmitClick();
            if (formBuilder.form.invalid) {
                this.permissionFieldGroups[index].expanded = true;
                return;
            }
            const selectedPermissions = Object.keys(formBuilder.form.value).filter((key) => formBuilder.form.value[key]);
            permissionsRecord[this.permissionFieldGroups[index].role] = selectedPermissions;
        });
        return permissionsRecord;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductAddPermissionsSubFormComponent, deps: [{ token: BasePaymentConfigurationProvider }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkProductAddPermissionsSubFormComponent, selector: "sdk-payments-add-permissions-sub-form", inputs: { initialPermissions: "initialPermissions" }, viewQueries: [{ propertyName: "productPermissionFormBuilders", predicate: ["productPermissionFormBuilder"], descendants: true }, { propertyName: "productPermissionFormCheckboxes", predicate: ["productPermissionFormCheckbox"], descendants: true }], ngImport: i0, template: `
    <div>
      <h3>{{ 'Product Permissions' | translate }}</h3>
      <div role="list" class="e11-divide-y e11-divide-gray-200 ">
        <div class="e11-py-4"></div>
        <ng-container *ngFor="let config of permissionFieldGroups; let i = index">
          <div class="e11-py-4">
            <div (click)="config.expanded = !config.expanded" class="e11-flex e11-items-center e11-justify-between">
              <p [ngClass]="{ 'e11-mb-0': !config.expanded }">{{ config.role | translate }}</p>
              <div class="e11-flex e11-items-center">
                <div (click)="stopClick($event)" class="e11-mr-2 e11-flex e11-items-center">
                  <e11-input-checkbox
                    #productPermissionFormCheckbox
                    (change)="selectAll($event, i)"
                    [size]="'sm'"
                    labelClassOverrides="e11-text-skin-base"
                    [label]="'Select all' | translate"
                    [checked]="config.allSelected ?? false"
                  ></e11-input-checkbox>
                </div>
                <span class="e11-cursor-pointer material-icons-outlined"> {{ config.expanded ? 'expand_less' : 'expand_more' }} </span>
              </div>
            </div>
            <div
              class="e11-h-auto e11-transition-all"
              [ngClass]="{
                'e11-visible e11-max-h-screen e11-opacity-100 e11-duration-500': config.expanded,
                'e11-invisible e11-max-h-0 e11-opacity-0': !config.expanded
              }"
            >
              <e11-dynamic-form-builder
                #productPermissionFormBuilder
                [hideSubmitMenu]="true"
                [fieldGroups]="config.fieldGroups"
                [initialValue]="config.initialValue"
                (valueChanges)="updateForm($event, i)"
              ></e11-dynamic-form-builder>
            </div>
          </div>
        </ng-container>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: i3$3.E11CheckboxComponent, selector: "e11-input-checkbox", inputs: ["name", "size", "color", "fontColor", "ring", "inputClass", "containerClass", "label", "display", "description", "descriptionClassOverrides", "checked", "checkboxPosition", "disabled", "disabledText", "containerClassOverrides", "labelClassOverrides", "loading", "e11CheckboxProps"] }, { kind: "component", type: i4$2.E11DynamicFormBuilderComponent, selector: "e11-dynamic-form-builder", inputs: ["initialValue", "hideSubmitMenu", "loading", "disabled", "disabledText", "submitButtonConfig", "cancelButtonConfig", "deleteButtonConfig", "cancelLinkConfig", "showCancelLink", "showCancelButton", "showDeleteButton", "submitButtonTitle", "cancelLinkTitle", "cancelButtonTitle", "deleteButtonTitle", "buttonColorSubmit", "buttonColorCancel", "buttonColorDelete", "fieldGroups", "errorMessageOverrides"], outputs: ["valueChanges", "onSubmit", "onCancel", "onDelete"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductAddPermissionsSubFormComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-payments-add-permissions-sub-form',
                    template: `
    <div>
      <h3>{{ 'Product Permissions' | translate }}</h3>
      <div role="list" class="e11-divide-y e11-divide-gray-200 ">
        <div class="e11-py-4"></div>
        <ng-container *ngFor="let config of permissionFieldGroups; let i = index">
          <div class="e11-py-4">
            <div (click)="config.expanded = !config.expanded" class="e11-flex e11-items-center e11-justify-between">
              <p [ngClass]="{ 'e11-mb-0': !config.expanded }">{{ config.role | translate }}</p>
              <div class="e11-flex e11-items-center">
                <div (click)="stopClick($event)" class="e11-mr-2 e11-flex e11-items-center">
                  <e11-input-checkbox
                    #productPermissionFormCheckbox
                    (change)="selectAll($event, i)"
                    [size]="'sm'"
                    labelClassOverrides="e11-text-skin-base"
                    [label]="'Select all' | translate"
                    [checked]="config.allSelected ?? false"
                  ></e11-input-checkbox>
                </div>
                <span class="e11-cursor-pointer material-icons-outlined"> {{ config.expanded ? 'expand_less' : 'expand_more' }} </span>
              </div>
            </div>
            <div
              class="e11-h-auto e11-transition-all"
              [ngClass]="{
                'e11-visible e11-max-h-screen e11-opacity-100 e11-duration-500': config.expanded,
                'e11-invisible e11-max-h-0 e11-opacity-0': !config.expanded
              }"
            >
              <e11-dynamic-form-builder
                #productPermissionFormBuilder
                [hideSubmitMenu]="true"
                [fieldGroups]="config.fieldGroups"
                [initialValue]="config.initialValue"
                (valueChanges)="updateForm($event, i)"
              ></e11-dynamic-form-builder>
            </div>
          </div>
        </ng-container>
      </div>
    </div>
  `,
                }]
        }], ctorParameters: () => [{ type: BasePaymentConfigurationProvider }], propDecorators: { productPermissionFormBuilders: [{
                type: ViewChildren,
                args: ['productPermissionFormBuilder']
            }], productPermissionFormCheckboxes: [{
                type: ViewChildren,
                args: ['productPermissionFormCheckbox']
            }], initialPermissions: [{
                type: Input
            }] } });

class SdkProductAddProductsComponent {
    constructor(productStore, libConfig) {
        this.productStore = productStore;
        this.libConfig = libConfig;
        this.productAdded = new EventEmitter();
        this.fieldGroups = productForm();
    }
    ngOnInit() { }
    onSave() {
        const prices = this.priceSubForm.onSave();
        const permissions = this.permissionSubForm.onSave();
        this.productFormBuilder.onSubmitClick();
        if (prices?.length === 0)
            this.additionalErrorMessage = 'A product price is required to save a new product';
        if (Object.keys(permissions).length === 0)
            this.additionalErrorMessage = 'At least one permission is required to save a new product';
        if (this.productFormBuilder.form.invalid || prices === null || Object.keys(permissions).length === 0)
            return;
        this.createProduct(this.productFormBuilder.form.value, prices, permissions);
    }
    createProduct(productFormValue, prices, permissions) {
        const product = productFormValue;
        this.productStore.createProduct({
            ...product,
            prices: prices,
            permissions: permissions,
            onComplete: () => {
                this.productAdded.emit();
            },
            onError: (error) => {
                // handle errors
            },
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductAddProductsComponent, deps: [{ token: ProductStore }, { token: BasePaymentConfigurationProvider }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkProductAddProductsComponent, selector: "sdk-payments-add-product", outputs: { productAdded: "productAdded" }, viewQueries: [{ propertyName: "productFormBuilder", first: true, predicate: ["productFormBuilder"], descendants: true }, { propertyName: "permissionSubForm", first: true, predicate: ["permissionSubForm"], descendants: true }, { propertyName: "priceSubForm", first: true, predicate: ["priceSubForm"], descendants: true }], ngImport: i0, template: `
    <h3 class="e11-mb-4">{{ 'Product Information' | translate }}</h3>
    <div class="e11-flex e11-gap-6 e11-flex-col">
      <e11-dynamic-form-builder #productFormBuilder [fieldGroups]="fieldGroups" [hideSubmitMenu]="true"></e11-dynamic-form-builder>
      <sdk-payments-add-permissions-sub-form #permissionSubForm></sdk-payments-add-permissions-sub-form>
      <sdk-payments-add-prices-sub-form #priceSubForm></sdk-payments-add-prices-sub-form>
      <p *ngIf="additionalErrorMessage" class="e11-text-skin-error">{{ additionalErrorMessage }}</p>
      <div class="e11-py-4 e11-flex e11-w-full e11-flex-row-reverse">
        <e11-button (click)="onSave()" value="{{ 'Save' | translate }}" [color]="'primary-accent'"> </e11-button>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$3.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i4$2.E11DynamicFormBuilderComponent, selector: "e11-dynamic-form-builder", inputs: ["initialValue", "hideSubmitMenu", "loading", "disabled", "disabledText", "submitButtonConfig", "cancelButtonConfig", "deleteButtonConfig", "cancelLinkConfig", "showCancelLink", "showCancelButton", "showDeleteButton", "submitButtonTitle", "cancelLinkTitle", "cancelButtonTitle", "deleteButtonTitle", "buttonColorSubmit", "buttonColorCancel", "buttonColorDelete", "fieldGroups", "errorMessageOverrides"], outputs: ["valueChanges", "onSubmit", "onCancel", "onDelete"] }, { kind: "component", type: SdkProductAddPricesSubFormComponent, selector: "sdk-payments-add-prices-sub-form" }, { kind: "component", type: SdkProductAddPermissionsSubFormComponent, selector: "sdk-payments-add-permissions-sub-form", inputs: ["initialPermissions"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductAddProductsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-payments-add-product',
                    template: `
    <h3 class="e11-mb-4">{{ 'Product Information' | translate }}</h3>
    <div class="e11-flex e11-gap-6 e11-flex-col">
      <e11-dynamic-form-builder #productFormBuilder [fieldGroups]="fieldGroups" [hideSubmitMenu]="true"></e11-dynamic-form-builder>
      <sdk-payments-add-permissions-sub-form #permissionSubForm></sdk-payments-add-permissions-sub-form>
      <sdk-payments-add-prices-sub-form #priceSubForm></sdk-payments-add-prices-sub-form>
      <p *ngIf="additionalErrorMessage" class="e11-text-skin-error">{{ additionalErrorMessage }}</p>
      <div class="e11-py-4 e11-flex e11-w-full e11-flex-row-reverse">
        <e11-button (click)="onSave()" value="{{ 'Save' | translate }}" [color]="'primary-accent'"> </e11-button>
      </div>
    </div>
  `,
                }]
        }], ctorParameters: () => [{ type: ProductStore }, { type: BasePaymentConfigurationProvider }], propDecorators: { productFormBuilder: [{
                type: ViewChild,
                args: ['productFormBuilder']
            }], permissionSubForm: [{
                type: ViewChild,
                args: ['permissionSubForm']
            }], priceSubForm: [{
                type: ViewChild,
                args: ['priceSubForm']
            }], productAdded: [{
                type: Output
            }] } });

const productsTableOptions = {
    keyField: 'id',
    selectable: false,
    enableDynamicFields: false,
    enableFilter: false,
    enableSorting: false,
    paginationSettings: {
        enabled: true,
        serverSide: false,
        pageLinks: 5,
        rowCount: 10,
        rowsPerPageOptions: [25, 50, 100],
    },
    columns: [
        {
            key: 'name',
            title: 'Name',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
        {
            key: 'description',
            title: 'Description',
            type: DataType.CharField,
            visible: true,
            optional: false,
        },
        {
            key: 'enabled',
            title: 'Is Enabled?',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
        {
            key: 'action',
            title: 'Action',
            type: DataType.TemplateField,
            visible: true,
            enableFilter: false,
            enableSort: false,
        },
    ],
};

const productPricesTableOptions = {
    keyField: 'id',
    selectable: false,
    enableDynamicFields: false,
    enableFilter: false,
    enableSorting: false,
    paginationSettings: {
        enabled: true,
        serverSide: false,
        pageLinks: 5,
        rowCount: 10,
        rowsPerPageOptions: [25, 50, 100],
    },
    columns: [
        {
            key: 'sku',
            title: 'SKU',
            type: DataType.CharField,
            visible: true,
            optional: false,
        },
        {
            key: 'amount',
            title: 'Amount',
            type: DataType.CharField,
            visible: true,
            optional: false,
        },
        {
            key: 'enabled',
            title: 'Is Enabled?',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
        {
            key: 'action',
            title: 'Action',
            type: DataType.TemplateField,
            visible: true,
            enableFilter: false,
            enableSort: false,
        },
    ],
};

class SdkEditProductPriceComponent {
    constructor() {
        this.prices = [];
        // @TODO Logic to divide the amount by 100 should be moved to a directive or down in the selector
        this.AMOUNT_MULTIPLIER = AMOUNT_MULTIPLIER;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkEditProductPriceComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.9", type: SdkEditProductPriceComponent, selector: "sdk-edit-product-price", inputs: { prices: "prices" }, ngImport: i0, template: `<div class="">
    <div class="e11-mt-4 e11-flow-root">
      <div class="-e11-mx-4 -e11-my-2 e11-overflow-x-auto sm:-e11-mx-6 lg:-e11-mx-8">
        <div class="e11-inline-block e11-min-w-full e11-py-2 e11-align-middle sm:e11-px-6 lg:e11-px-8">
          <table class="e11-min-w-full e11-divide-y e11-divide-gray-300">
            <thead>
              <tr>
                <th
                  scope="col"
                  class="e11-whitespace-nowrap e11-py-3.5 e11-pl-4 e11-pr-3 e11-text-left e11-text-sm e11-font-semibold e11-text-gray-900 sm:e11-pl-0"
                >
                  SKU
                </th>
                <th scope="col" class="e11-whitespace-nowrap e11-px-2 e11-py-3.5 e11-text-left e11-text-sm e11-font-semibold e11-text-gray-900">
                  Amount
                </th>
                <th scope="col" class="e11-whitespace-nowrap e11-px-2 e11-py-3.5 e11-text-left e11-text-sm e11-font-semibold e11-text-gray-900">
                  Billing Period
                </th>
                <th scope="col" class="e11-whitespace-nowrap e11-px-2 e11-py-3.5 e11-text-left e11-text-sm e11-font-semibold e11-text-gray-900">
                  Is Enabled
                </th>
              </tr>
            </thead>
            <tbody class="e11-divide-y e11-divide-gray-200 e11-bg-white">
              @for (price of prices; track $index) {
              <tr>
                <td class="e11-whitespace-nowrap e11-py-2 e11-pl-4 e11-pr-3 e11-text-sm e11-text-gray-500 sm:e11-pl-0">{{ price.sku }}</td>
                <td class="e11-whitespace-nowrap e11-px-2 e11-py-2 e11-text-sm e11-font-medium e11-text-gray-900">
                  {{ (price.amount ?? 0) / AMOUNT_MULTIPLIER | currency: price.currencyCode:true }}
                </td>
                <td class="e11-whitespace-nowrap e11-px-2 e11-py-2 e11-text-sm e11-text-gray-900">
                  {{ price.recurring?.intervalCount + '/' + price.recurring?.interval }}
                </td>
                <td class="e11-whitespace-nowrap e11-px-2 e11-py-2 e11-text-sm e11-text-gray-500">
                  {{ price.enabled ? 'Yes' : 'No' }}
                </td>
              </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>`, isInline: true, dependencies: [{ kind: "pipe", type: i1.CurrencyPipe, name: "currency" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkEditProductPriceComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-edit-product-price',
                    template: `<div class="">
    <div class="e11-mt-4 e11-flow-root">
      <div class="-e11-mx-4 -e11-my-2 e11-overflow-x-auto sm:-e11-mx-6 lg:-e11-mx-8">
        <div class="e11-inline-block e11-min-w-full e11-py-2 e11-align-middle sm:e11-px-6 lg:e11-px-8">
          <table class="e11-min-w-full e11-divide-y e11-divide-gray-300">
            <thead>
              <tr>
                <th
                  scope="col"
                  class="e11-whitespace-nowrap e11-py-3.5 e11-pl-4 e11-pr-3 e11-text-left e11-text-sm e11-font-semibold e11-text-gray-900 sm:e11-pl-0"
                >
                  SKU
                </th>
                <th scope="col" class="e11-whitespace-nowrap e11-px-2 e11-py-3.5 e11-text-left e11-text-sm e11-font-semibold e11-text-gray-900">
                  Amount
                </th>
                <th scope="col" class="e11-whitespace-nowrap e11-px-2 e11-py-3.5 e11-text-left e11-text-sm e11-font-semibold e11-text-gray-900">
                  Billing Period
                </th>
                <th scope="col" class="e11-whitespace-nowrap e11-px-2 e11-py-3.5 e11-text-left e11-text-sm e11-font-semibold e11-text-gray-900">
                  Is Enabled
                </th>
              </tr>
            </thead>
            <tbody class="e11-divide-y e11-divide-gray-200 e11-bg-white">
              @for (price of prices; track $index) {
              <tr>
                <td class="e11-whitespace-nowrap e11-py-2 e11-pl-4 e11-pr-3 e11-text-sm e11-text-gray-500 sm:e11-pl-0">{{ price.sku }}</td>
                <td class="e11-whitespace-nowrap e11-px-2 e11-py-2 e11-text-sm e11-font-medium e11-text-gray-900">
                  {{ (price.amount ?? 0) / AMOUNT_MULTIPLIER | currency: price.currencyCode:true }}
                </td>
                <td class="e11-whitespace-nowrap e11-px-2 e11-py-2 e11-text-sm e11-text-gray-900">
                  {{ price.recurring?.intervalCount + '/' + price.recurring?.interval }}
                </td>
                <td class="e11-whitespace-nowrap e11-px-2 e11-py-2 e11-text-sm e11-text-gray-500">
                  {{ price.enabled ? 'Yes' : 'No' }}
                </td>
              </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>`,
                }]
        }], propDecorators: { prices: [{
                type: Input,
                args: [{ required: true }]
            }] } });

class SdkPaymentProductDetailComponent {
    constructor() {
        this.#productStore = inject(ProductStore);
        this.#notificationService = inject(E11NotificationsService);
        this.fieldGroups = productForm();
        this.productPricesTableOptions = productPricesTableOptions;
        this.selectedProduct$ = this.#productStore.singleProduct$.pipe(tap((product) => {
            this.initialProduct = product;
        }));
    }
    #productStore;
    #notificationService;
    set productId(value) {
        this._productId = value;
        this.#productStore.getSingleProduct(value);
        this.#productStore.getSingleProductWithPrice(value);
    }
    updateProduct() {
        const permissions = this.permissionSubForm.onSave();
        this.productFormBuilder.onSubmitClick();
        if (this.productFormBuilder.form.invalid || !this._productId)
            return;
        const product = { ...this.productFormBuilder.form.value, permissions: permissions };
        this.#productStore.updateProduct({
            ...product,
            id: this._productId,
            onComplete: () => {
                // close
            },
        });
    }
    addNewPrice() {
        const prices = this.priceSubForm.onSave();
        if (prices === null || !this._productId)
            return;
        const addProductPrices = prices.map((price) => new Promise((resolve, reject) => this.#productStore.addProductPrice({ price: { ...price, productId: this._productId }, onComplete: resolve, onError: reject })));
        Promise.all(addProductPrices).then(() => {
            this.#notificationService.popNotificationMessage(NOTIFICATION_MESSAGES.productAdded());
            this.productId = this._productId;
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkPaymentProductDetailComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkPaymentProductDetailComponent, selector: "sdk-payment-product-details", inputs: { productId: "productId" }, viewQueries: [{ propertyName: "productFormBuilder", first: true, predicate: ["productFormBuilder"], descendants: true }, { propertyName: "priceSubForm", first: true, predicate: ["priceSubForm"], descendants: true }, { propertyName: "permissionSubForm", first: true, predicate: ["permissionSubForm"], descendants: true }], ngImport: i0, template: `<ng-container *ngrxLet="selectedProduct$ as selectedProduct">
    <div class="e11-flex e11-gap-6 e11-flex-col">
      <e11-dynamic-form-builder
        #productFormBuilder
        [initialValue]="initialProduct"
        [fieldGroups]="fieldGroups"
        [hideSubmitMenu]="true"
      ></e11-dynamic-form-builder>
      <sdk-payments-add-permissions-sub-form
        #permissionSubForm
        [initialPermissions]="selectedProduct?.permissions"
      ></sdk-payments-add-permissions-sub-form>
      <div class="e11-flex e11-w-full e11-flex-row-reverse">
        <e11-button (click)="updateProduct()" [size]="'sm'" value="{{ 'Update Product' | translate }}" [color]="'primary-accent'"> </e11-button>
      </div>
      <sdk-edit-product-price [prices]="selectedProduct?.prices ?? []"></sdk-edit-product-price>
      <sdk-payments-add-prices-sub-form #priceSubForm></sdk-payments-add-prices-sub-form>
      <div *ngIf="priceSubForm.priceFieldGroups.length" class="e11-flex e11-w-full e11-flex-row-reverse">
        <e11-button (click)="addNewPrice()" [size]="'sm'" value="{{ 'Save Price' | translate }}" [color]="'primary-accent'"> </e11-button>
      </div>
    </div>
  </ng-container>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$3.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i4$2.E11DynamicFormBuilderComponent, selector: "e11-dynamic-form-builder", inputs: ["initialValue", "hideSubmitMenu", "loading", "disabled", "disabledText", "submitButtonConfig", "cancelButtonConfig", "deleteButtonConfig", "cancelLinkConfig", "showCancelLink", "showCancelButton", "showDeleteButton", "submitButtonTitle", "cancelLinkTitle", "cancelButtonTitle", "deleteButtonTitle", "buttonColorSubmit", "buttonColorCancel", "buttonColorDelete", "fieldGroups", "errorMessageOverrides"], outputs: ["valueChanges", "onSubmit", "onCancel", "onDelete"] }, { kind: "directive", type: i1$1.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "component", type: SdkProductAddPricesSubFormComponent, selector: "sdk-payments-add-prices-sub-form" }, { kind: "component", type: SdkProductAddPermissionsSubFormComponent, selector: "sdk-payments-add-permissions-sub-form", inputs: ["initialPermissions"] }, { kind: "component", type: SdkEditProductPriceComponent, selector: "sdk-edit-product-price", inputs: ["prices"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkPaymentProductDetailComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-payment-product-details',
                    template: `<ng-container *ngrxLet="selectedProduct$ as selectedProduct">
    <div class="e11-flex e11-gap-6 e11-flex-col">
      <e11-dynamic-form-builder
        #productFormBuilder
        [initialValue]="initialProduct"
        [fieldGroups]="fieldGroups"
        [hideSubmitMenu]="true"
      ></e11-dynamic-form-builder>
      <sdk-payments-add-permissions-sub-form
        #permissionSubForm
        [initialPermissions]="selectedProduct?.permissions"
      ></sdk-payments-add-permissions-sub-form>
      <div class="e11-flex e11-w-full e11-flex-row-reverse">
        <e11-button (click)="updateProduct()" [size]="'sm'" value="{{ 'Update Product' | translate }}" [color]="'primary-accent'"> </e11-button>
      </div>
      <sdk-edit-product-price [prices]="selectedProduct?.prices ?? []"></sdk-edit-product-price>
      <sdk-payments-add-prices-sub-form #priceSubForm></sdk-payments-add-prices-sub-form>
      <div *ngIf="priceSubForm.priceFieldGroups.length" class="e11-flex e11-w-full e11-flex-row-reverse">
        <e11-button (click)="addNewPrice()" [size]="'sm'" value="{{ 'Save Price' | translate }}" [color]="'primary-accent'"> </e11-button>
      </div>
    </div>
  </ng-container>`,
                }]
        }], propDecorators: { productFormBuilder: [{
                type: ViewChild,
                args: ['productFormBuilder']
            }], priceSubForm: [{
                type: ViewChild,
                args: ['priceSubForm']
            }], permissionSubForm: [{
                type: ViewChild,
                args: ['permissionSubForm']
            }], productId: [{
                type: Input
            }] } });

class SdkProductListComponent {
    constructor(productStore, paymentStore) {
        this.productStore = productStore;
        this.paymentStore = paymentStore;
        this.allProducts$ = this.productStore.allProducts$;
        this.loadingProducts$ = this.productStore.loadingProducts$;
        this.initialGridOptions = productsTableOptions;
    }
    ngOnInit() {
        this.productStore.onGetAllProductsWithPrice();
    }
    onRemove(product) {
        // TODO: Use direct product endpoint to delete product
        // Currently removing the prices until backend is ready
        product?.prices?.forEach((price) => {
            this.productStore.deleteProduct({ productId: product.id, priceId: price.id });
        });
    }
    onAddProduct() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductListComponent, deps: [{ token: ProductStore }, { token: PaymentStore }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkProductListComponent, selector: "sdk-payment-list-products", ngImport: i0, template: "<div class=\"e11-flex e11-flex-col e11-gap-6 e11-overflow-auto\">\n  <div class=\"e11-flex e11-flex-col md:e11-flex-row e11-justify-between\">\n    <h3 class=\"e11-mb-2\">{{ \"Products List\" | translate }}</h3>\n    <e11-button [color]=\"'primary'\" [size]=\"'sm'\" [value]=\"'Add Product' | translate\" (click)=\"modalNewProduct.open()\"></e11-button>\n  </div>\n  <e11-datatable [initialGridOptions]=\"initialGridOptions\" [loading]=\"(loadingProducts$ | async) || false\" [records]=\"(allProducts$ | async) || []\">\n    <ng-template e11DatatableTemplate=\"name\" let-row>\n      <div class=\"e11-flex e11-items-center\">\n        <span> {{ row.name }} </span>\n        <e11-tooltip\n          [tooltipText]=\"\n            'This is a system product, which is automatically subscribed to by default. Tenants cannot unsubscribe their clients from system products.'\n              | translate\n          \"\n        >\n          <span *ngIf=\"row.isSystemProduct\" class=\"e11-text-skin-primary-accent e11-cursor-default material-icons e11-ml-2 md-18\">settings</span>\n        </e11-tooltip>\n      </div>\n    </ng-template>\n    <ng-template e11DatatableTemplate=\"enabled\" let-row>\n      <span> {{ row.enabled ? \"Yes\" : (\"No\" | translate) }} </span>\n    </ng-template>\n    <ng-template e11DatatableTemplate=\"numberOfPrices\" let-row>\n      <span> {{ row.prices.length }} </span>\n    </ng-template>\n    <ng-template e11DatatableTemplate=\"action\" let-row>\n      <ng-template #confirmationBoxText>\n        <p>{{ \"Are you sure you want to delete this product?\" | translate }}</p>\n        <p>{{ \"Deleting this product will also remove this price lists\" | translate }}:</p>\n        <ul>\n          <li *ngFor=\"let price of row.prices\">\n            {{ price.id }} - <span class=\"e11-font-bold\"> {{ price.currencyCode }}{{ price.amount }} </span>\n          </li>\n        </ul>\n      </ng-template>\n      <div class=\"e11-flex e11-gap-1\">\n        <e11-button\n          [color]=\"'neutral'\"\n          [size]=\"'sm'\"\n          [icon]=\"'visibility'\"\n          [value]=\"''\"\n          [iconPosition]=\"'left'\"\n          [iconSize]=\"'md-14'\"\n          (click)=\"selectedProductId = row.id; modalEditProduct.open()\"\n        >\n        </e11-button>\n        <!-- Until we get product deletion-->\n        <!-- <e11-button\n          [color]=\"'error'\"\n          [size]=\"'sm'\"\n          [icon]=\"'delete'\"\n          [value]=\"''\"\n          [iconPosition]=\"'left'\"\n          [iconSize]=\"'md-14'\"\n          e11ConfirmationBox\n          [confirmationBoxContent]=\"confirmationBoxText\"\n          [componentInput]=\" {\n        confirmText: 'Delete' | translate,\n        cancelText: 'Cancel' | translate,\n        confirmValueWithInputBox: false,\n      }\"\n          (confirmed)=\"onRemove(row)\"\n        >\n        </e11-button> -->\n      </div>\n    </ng-template>\n  </e11-datatable>\n</div>\n\n<e11-modal #modalNewProduct width=\"lg\" [bodyHeight]=\"'large'\" title=\"{{ 'Add new product' | translate }}\" [showHeader]=\"true\" [showFooter]=\"false\">\n  <ng-template e11Template=\"Body\" class=\"e11-mb-8\">\n    <sdk-payments-add-product (productAdded)=\"modalNewProduct.close()\"></sdk-payments-add-product>\n  </ng-template>\n</e11-modal>\n\n<e11-modal #modalEditProduct width=\"lg\" [bodyHeight]=\"'auto'\" title=\"{{ 'Edit product' | translate }}\" [showHeader]=\"true\" [showFooter]=\"false\">\n  <ng-template e11Template=\"Body\" class=\"e11-mb-8\">\n    <sdk-payment-product-details *ngIf=\"selectedProductId\" [productId]=\"selectedProductId\"></sdk-payment-product-details>\n  </ng-template>\n</e11-modal>\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$3.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i3$2.E11ModalComponent, selector: "e11-modal", inputs: ["title", "width", "bodyHeight", "titleAlign", "hideCloseButton", "closer", "bodyClassOverrides", "bodyClass", "modalClassOverrides", "modalClass", "contentClassOverrides", "contentClass", "headerClassOverrides", "headerClass", "footerClassOverrides", "footerClass", "showFooter", "showHeader", "darkMode", "closeOnEscape", "closeOnClickOutside", "headerCTAs", "hasBackdrop", "backdropStyle"], outputs: ["modalOpened", "onModalOpened", "modalClosed", "onModalClosed", "headerCTAClicked", "onHeaderCTAClicked"] }, { kind: "directive", type: i2$2.E11Template, selector: "[e11Template]", inputs: ["type", "e11Template"] }, { kind: "component", type: i2$1.E11DataTableComponent, selector: "e11-datatable", inputs: ["initialGridOptions", "records", "selectedRecords", "isAllSelected", "loading", "striped"], outputs: ["selectedRecordsChange", "isAllSelectedChange", "bulkOperationsChecked"] }, { kind: "directive", type: i2$1.E11DatatableTemplate, selector: "[e11DatatableTemplate]", inputs: ["type", "e11DatatableTemplate"] }, { kind: "component", type: i4$1.E11TooltipComponent, selector: "e11-tooltip", inputs: ["tooltipText", "tooltipClassOverrides", "hideTooltip", "color", "tooltipContent", "position", "interactive", "maxWidth", "delay"], outputs: ["tooltipClick"] }, { kind: "component", type: SdkProductAddProductsComponent, selector: "sdk-payments-add-product", outputs: ["productAdded"] }, { kind: "component", type: SdkPaymentProductDetailComponent, selector: "sdk-payment-product-details", inputs: ["productId"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-payment-list-products', template: "<div class=\"e11-flex e11-flex-col e11-gap-6 e11-overflow-auto\">\n  <div class=\"e11-flex e11-flex-col md:e11-flex-row e11-justify-between\">\n    <h3 class=\"e11-mb-2\">{{ \"Products List\" | translate }}</h3>\n    <e11-button [color]=\"'primary'\" [size]=\"'sm'\" [value]=\"'Add Product' | translate\" (click)=\"modalNewProduct.open()\"></e11-button>\n  </div>\n  <e11-datatable [initialGridOptions]=\"initialGridOptions\" [loading]=\"(loadingProducts$ | async) || false\" [records]=\"(allProducts$ | async) || []\">\n    <ng-template e11DatatableTemplate=\"name\" let-row>\n      <div class=\"e11-flex e11-items-center\">\n        <span> {{ row.name }} </span>\n        <e11-tooltip\n          [tooltipText]=\"\n            'This is a system product, which is automatically subscribed to by default. Tenants cannot unsubscribe their clients from system products.'\n              | translate\n          \"\n        >\n          <span *ngIf=\"row.isSystemProduct\" class=\"e11-text-skin-primary-accent e11-cursor-default material-icons e11-ml-2 md-18\">settings</span>\n        </e11-tooltip>\n      </div>\n    </ng-template>\n    <ng-template e11DatatableTemplate=\"enabled\" let-row>\n      <span> {{ row.enabled ? \"Yes\" : (\"No\" | translate) }} </span>\n    </ng-template>\n    <ng-template e11DatatableTemplate=\"numberOfPrices\" let-row>\n      <span> {{ row.prices.length }} </span>\n    </ng-template>\n    <ng-template e11DatatableTemplate=\"action\" let-row>\n      <ng-template #confirmationBoxText>\n        <p>{{ \"Are you sure you want to delete this product?\" | translate }}</p>\n        <p>{{ \"Deleting this product will also remove this price lists\" | translate }}:</p>\n        <ul>\n          <li *ngFor=\"let price of row.prices\">\n            {{ price.id }} - <span class=\"e11-font-bold\"> {{ price.currencyCode }}{{ price.amount }} </span>\n          </li>\n        </ul>\n      </ng-template>\n      <div class=\"e11-flex e11-gap-1\">\n        <e11-button\n          [color]=\"'neutral'\"\n          [size]=\"'sm'\"\n          [icon]=\"'visibility'\"\n          [value]=\"''\"\n          [iconPosition]=\"'left'\"\n          [iconSize]=\"'md-14'\"\n          (click)=\"selectedProductId = row.id; modalEditProduct.open()\"\n        >\n        </e11-button>\n        <!-- Until we get product deletion-->\n        <!-- <e11-button\n          [color]=\"'error'\"\n          [size]=\"'sm'\"\n          [icon]=\"'delete'\"\n          [value]=\"''\"\n          [iconPosition]=\"'left'\"\n          [iconSize]=\"'md-14'\"\n          e11ConfirmationBox\n          [confirmationBoxContent]=\"confirmationBoxText\"\n          [componentInput]=\" {\n        confirmText: 'Delete' | translate,\n        cancelText: 'Cancel' | translate,\n        confirmValueWithInputBox: false,\n      }\"\n          (confirmed)=\"onRemove(row)\"\n        >\n        </e11-button> -->\n      </div>\n    </ng-template>\n  </e11-datatable>\n</div>\n\n<e11-modal #modalNewProduct width=\"lg\" [bodyHeight]=\"'large'\" title=\"{{ 'Add new product' | translate }}\" [showHeader]=\"true\" [showFooter]=\"false\">\n  <ng-template e11Template=\"Body\" class=\"e11-mb-8\">\n    <sdk-payments-add-product (productAdded)=\"modalNewProduct.close()\"></sdk-payments-add-product>\n  </ng-template>\n</e11-modal>\n\n<e11-modal #modalEditProduct width=\"lg\" [bodyHeight]=\"'auto'\" title=\"{{ 'Edit product' | translate }}\" [showHeader]=\"true\" [showFooter]=\"false\">\n  <ng-template e11Template=\"Body\" class=\"e11-mb-8\">\n    <sdk-payment-product-details *ngIf=\"selectedProductId\" [productId]=\"selectedProductId\"></sdk-payment-product-details>\n  </ng-template>\n</e11-modal>\n" }]
        }], ctorParameters: () => [{ type: ProductStore }, { type: PaymentStore }] });

class SdkListProductAdminComponent {
    #libConfig = inject(BasePaymentConfigurationProvider);
    set customerKey(value) {
        this.#libConfig.updateClientKey(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkListProductAdminComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkListProductAdminComponent, selector: "sdk-list-product-admin", inputs: { customerKey: "customerKey" }, ngImport: i0, template: `<sdk-payment-list-products></sdk-payment-list-products>`, isInline: true, dependencies: [{ kind: "component", type: SdkProductListComponent, selector: "sdk-payment-list-products" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkListProductAdminComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-list-product-admin',
                    template: `<sdk-payment-list-products></sdk-payment-list-products>`,
                }]
        }], propDecorators: { customerKey: [{
                type: Input,
                args: [{ required: true }]
            }] } });

class SdkPaymentSingleSubscriptionPriceComponent {
    constructor() {
        this.divideBy = DEFAULT_DIVIDE_BY;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkPaymentSingleSubscriptionPriceComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkPaymentSingleSubscriptionPriceComponent, selector: "sdk-product-single-subscription-price", inputs: { planInfo: "planInfo" }, ngImport: i0, template: `
    <div *ngIf="planInfo.currency && divideBy" class="e11-mt-6 e11-text-sm e11-font-light e11-text-skin-black">
      {{ planInfo.amount / divideBy | currency: planInfo.currency.toUpperCase() }}
      / {{ planInfo.interval }}
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.CurrencyPipe, name: "currency" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkPaymentSingleSubscriptionPriceComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-product-single-subscription-price',
                    template: `
    <div *ngIf="planInfo.currency && divideBy" class="e11-mt-6 e11-text-sm e11-font-light e11-text-skin-black">
      {{ planInfo.amount / divideBy | currency: planInfo.currency.toUpperCase() }}
      / {{ planInfo.interval }}
    </div>
  `,
                }]
        }], propDecorators: { planInfo: [{
                type: Input,
                args: [{ required: true }]
            }] } });

class SdkListRecurringProductsComponent {
    constructor(productStore) {
        this.productStore = productStore;
        this.recurringProducts$ = this.productStore.recurringProducts$.pipe(map((products) => products.filter((product) => !product.isSystemProduct)), tap((products) => {
            if (products && products.length && products[0].prices && products[0].prices.length) {
                this.changePlan(products[0].id, products[0].prices[0].id);
            }
        }));
        this.intentInfoChange = new EventEmitter();
    }
    ngOnInit() {
        this.productStore.onGetAllRecurringProducts();
    }
    changePlan(productId, priceId) {
        this.intentInfo = { productId, priceId };
        this.intentInfoChange.emit(this.intentInfo);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkListRecurringProductsComponent, deps: [{ token: ProductStore }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkListRecurringProductsComponent, selector: "sdk-list-recurring-products", outputs: { intentInfoChange: "intentInfoChange" }, ngImport: i0, template: `
    <fieldset *ngrxLet="recurringProducts$ as recurringProducts">
      <h3>{{ 'Subscription' | translate }}</h3>
      <div class="e11-mt-4 e11-grid e11-grid-cols-1 e11-gap-y-6 sm:e11-grid-cols-2 sm:e11-gap-x-4">
        <ng-container *ngFor="let recurringProduct of recurringProducts">
          <label
            *ngFor="let recurringProductPrice of recurringProduct.prices"
            (click)="changePlan(recurringProduct.id, recurringProductPrice.id!)"
            class="e11-relative e11-flex e11-cursor-pointer e11-rounded-lg e11-border e11-bg-skin-white e11-p-4 e11-shadow-sm focus:e11-outline-none "
            [ngClass]="{
                  'e11-ring-2 e11-ring-skin-primary e11-border-transparent': intentInfo?.productId === recurringProduct.id && intentInfo?.priceId === recurringProductPrice.id,
                  'e11-border-gray-300': intentInfo?.productId != recurringProduct.id,
                }"
          >
            <span class="e11-flex e11-flex-1">
              <span class="e11-flex e11-flex-col">
                <span class="e11-block e11-text-sm e11-font-medium e11-text-skin-dark e11-capitalize">{{ recurringProduct.name }}</span>
                <span class="e11-mt-1 e11-flex e11-items-center e11-text-sm e11-text-skin-gray"> {{ recurringProduct.description }}</span>
                <sdk-product-single-subscription-price
                  [planInfo]="{
                    amount: recurringProductPrice.amount!,
                    currency: recurringProductPrice.currencyCode!,
                    interval: recurringProductPrice.recurring?.interval || ''
                  }"
                ></sdk-product-single-subscription-price>
              </span>
            </span>
            <span
              class="material-icons e11-h-5 e11-w-5 e11-text-skin-primary"
              [ngClass]="{ 'e11-hidden': intentInfo?.productId != recurringProduct.id }"
              >check_circle</span
            >
          </label>
        </ng-container>
      </div>
    </fieldset>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "component", type: SdkPaymentSingleSubscriptionPriceComponent, selector: "sdk-product-single-subscription-price", inputs: ["planInfo"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkListRecurringProductsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-list-recurring-products',
                    template: `
    <fieldset *ngrxLet="recurringProducts$ as recurringProducts">
      <h3>{{ 'Subscription' | translate }}</h3>
      <div class="e11-mt-4 e11-grid e11-grid-cols-1 e11-gap-y-6 sm:e11-grid-cols-2 sm:e11-gap-x-4">
        <ng-container *ngFor="let recurringProduct of recurringProducts">
          <label
            *ngFor="let recurringProductPrice of recurringProduct.prices"
            (click)="changePlan(recurringProduct.id, recurringProductPrice.id!)"
            class="e11-relative e11-flex e11-cursor-pointer e11-rounded-lg e11-border e11-bg-skin-white e11-p-4 e11-shadow-sm focus:e11-outline-none "
            [ngClass]="{
                  'e11-ring-2 e11-ring-skin-primary e11-border-transparent': intentInfo?.productId === recurringProduct.id && intentInfo?.priceId === recurringProductPrice.id,
                  'e11-border-gray-300': intentInfo?.productId != recurringProduct.id,
                }"
          >
            <span class="e11-flex e11-flex-1">
              <span class="e11-flex e11-flex-col">
                <span class="e11-block e11-text-sm e11-font-medium e11-text-skin-dark e11-capitalize">{{ recurringProduct.name }}</span>
                <span class="e11-mt-1 e11-flex e11-items-center e11-text-sm e11-text-skin-gray"> {{ recurringProduct.description }}</span>
                <sdk-product-single-subscription-price
                  [planInfo]="{
                    amount: recurringProductPrice.amount!,
                    currency: recurringProductPrice.currencyCode!,
                    interval: recurringProductPrice.recurring?.interval || ''
                  }"
                ></sdk-product-single-subscription-price>
              </span>
            </span>
            <span
              class="material-icons e11-h-5 e11-w-5 e11-text-skin-primary"
              [ngClass]="{ 'e11-hidden': intentInfo?.productId != recurringProduct.id }"
              >check_circle</span
            >
          </label>
        </ng-container>
      </div>
    </fieldset>
  `,
                }]
        }], ctorParameters: () => [{ type: ProductStore }], propDecorators: { intentInfoChange: [{
                type: Output
            }] } });

class SdkProductCancelSubscriptionButtonComponent {
    constructor() {
        this.cancelSubscription = new EventEmitter();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductCancelSubscriptionButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkProductCancelSubscriptionButtonComponent, selector: "sdk-product-cancel-subscription-button", inputs: { subscriptionId: "subscriptionId" }, outputs: { cancelSubscription: "cancelSubscription" }, ngImport: i0, template: `
    <div class="e11-flex e11-items-center e11-gap-2">
      <e11-button
        [size]="'sm'"
        value="{{ 'Cancel Subscription' | translate }}"
        [color]="'red'"
        e11ConfirmationBox
        [confirmationBoxContent]="'Are you sure you want to cancel your subscription?'"
        [componentInput]=" {
                        confirmText: 'Cancel Subscription' | translate,
                        cancelText: 'Cancel' | translate,
                      }"
        (confirmed)="this.cancelSubscription.emit(subscriptionId)"
      >
      </e11-button>
    </div>
  `, isInline: true, dependencies: [{ kind: "component", type: i2$3.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "directive", type: i3$1.E11ConfirmationBoxDirective, selector: "[e11ConfirmationBox]", inputs: ["confirmationBoxContent", "componentInput"], outputs: ["confirmed", "canceled"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductCancelSubscriptionButtonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-product-cancel-subscription-button',
                    template: `
    <div class="e11-flex e11-items-center e11-gap-2">
      <e11-button
        [size]="'sm'"
        value="{{ 'Cancel Subscription' | translate }}"
        [color]="'red'"
        e11ConfirmationBox
        [confirmationBoxContent]="'Are you sure you want to cancel your subscription?'"
        [componentInput]=" {
                        confirmText: 'Cancel Subscription' | translate,
                        cancelText: 'Cancel' | translate,
                      }"
        (confirmed)="this.cancelSubscription.emit(subscriptionId)"
      >
      </e11-button>
    </div>
  `,
                }]
        }], propDecorators: { cancelSubscription: [{
                type: Output
            }], subscriptionId: [{
                type: Input,
                args: [{ required: true }]
            }] } });

class SdkProductReNewSubscriptionButtonComponent {
    constructor() {
        this.reEnableSubscription = new EventEmitter();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductReNewSubscriptionButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkProductReNewSubscriptionButtonComponent, selector: "sdk-product-re-enable-subscription-button", inputs: { subscriptionId: "subscriptionId" }, outputs: { reEnableSubscription: "reEnableSubscription" }, ngImport: i0, template: `
    <div class="e11-flex e11-items-center e11-gap-2">
      <span class="e11-text-skin-red"> {{ 'Subscription is canceled ' | translate }} </span>
      <e11-button [size]="'sm'" value="{{ 'Re-enable' | translate }}" [color]="'primary-accent'" (click)="reEnableSubscription.emit(subscriptionId)">
      </e11-button>
    </div>
  `, isInline: true, dependencies: [{ kind: "component", type: i2$3.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductReNewSubscriptionButtonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-product-re-enable-subscription-button',
                    template: `
    <div class="e11-flex e11-items-center e11-gap-2">
      <span class="e11-text-skin-red"> {{ 'Subscription is canceled ' | translate }} </span>
      <e11-button [size]="'sm'" value="{{ 'Re-enable' | translate }}" [color]="'primary-accent'" (click)="reEnableSubscription.emit(subscriptionId)">
      </e11-button>
    </div>
  `,
                }]
        }], propDecorators: { reEnableSubscription: [{
                type: Output
            }], subscriptionId: [{
                type: Input,
                args: [{ required: true }]
            }] } });

class SdkProductSubscriptionTitleComponent {
    constructor() {
        this.planTitle = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductSubscriptionTitleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkProductSubscriptionTitleComponent, selector: "sdk-product-subscription-title", inputs: { subscriptionInfo: "subscriptionInfo", planTitle: "planTitle" }, ngImport: i0, template: `
    <div class="e11-flex e11-flex-col">
      <span class="e11-block e11-text-sm e11-font-medium e11-text-skin-dark e11-capitalize">
        {{ planTitle }}
      </span>
      <span *ngIf="subscriptionInfo" class="e11-mt-1 e11-flex e11-items-center e11-text-sm e11-text-skin-grey">
        {{ subscriptionInfo.current_period_end * 1000 | date: 'until  MMMM d, y' }}
      </span>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.DatePipe, name: "date" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkProductSubscriptionTitleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-product-subscription-title',
                    template: `
    <div class="e11-flex e11-flex-col">
      <span class="e11-block e11-text-sm e11-font-medium e11-text-skin-dark e11-capitalize">
        {{ planTitle }}
      </span>
      <span *ngIf="subscriptionInfo" class="e11-mt-1 e11-flex e11-items-center e11-text-sm e11-text-skin-grey">
        {{ subscriptionInfo.current_period_end * 1000 | date: 'until  MMMM d, y' }}
      </span>
    </div>
  `,
                }]
        }], propDecorators: { subscriptionInfo: [{
                type: Input
            }], planTitle: [{
                type: Input
            }] } });

const tenantProductsTableOptions = {
    keyField: 'id',
    selectable: false,
    enableDynamicFields: false,
    enableFilter: false,
    enableSorting: false,
    paginationSettings: {
        enabled: true,
        serverSide: false,
        pageLinks: 5,
        rowCount: 10,
        rowsPerPageOptions: [25, 50, 100],
    },
    columns: [
        {
            key: 'id',
            title: 'SKU',
            type: DataType.CharField,
            visible: true,
            optional: false,
        },
        {
            key: 'productName',
            title: 'Name',
            type: DataType.CharField,
            visible: true,
            optional: false,
        },
        {
            key: 'amount',
            title: 'Amount',
            type: DataType.TemplateField,
            visible: true,
            optional: false,
        },
        {
            key: 'action',
            title: 'Action',
            type: DataType.TemplateField,
            visible: true,
            enableFilter: false,
            enableSort: false,
        },
    ],
};

class SdkTenantProductsManagerComponent {
    constructor() {
        this.platformAdminCustomerPaymentsStore = inject(PlatformAdminCustomerPaymentsStore);
        this.productStore = inject(ProductStore);
        this.AMOUNT_MULTIPLIER = AMOUNT_MULTIPLIER;
        this.recurringProducts$ = this.productStore.recurringProducts$.pipe(map$1((products) => products.filter((product) => !product.isSystemProduct)), tap$1(this.formatTenantProductOptions.bind(this)));
        this.tenantProducts$ = this.platformAdminCustomerPaymentsStore.tenantProducts$;
        this.loadingTenantProducts$ = this.platformAdminCustomerPaymentsStore.loadingTenantProducts$;
        this.initialGridOptions = tenantProductsTableOptions;
        this.addProductMode = false;
        this.fieldGroups = this.priceForm();
    }
    set tenantId(value) {
        this._tenantId = value;
        this.platformAdminCustomerPaymentsStore.getPlatformAdminTenantProducts(value);
    }
    ngOnInit() {
        this.productStore.getAllEnabledProducts();
    }
    formatTenantProductOptions(products) {
        this.formatProductOptions(products);
        this.productPrices = products.reduce((acc, { id, prices }) => ({ ...acc, [id]: prices ?? [] }), {});
    }
    formatProductOptions(products) {
        this.productFieldGroups = generateDynamicFormObjectForProducts({
            productOptions: products.map((product) => ({ name: product.name, value: product.id })),
        });
    }
    priceForm(prices) {
        return e11FormFor()([
            e11FormLayoutFullWidth([
                e11FieldInputSelect('priceId', 'Product', prices ?? [], undefined, {
                    validatorOrOpts: [Validators.required],
                    isDisabled: !prices?.length,
                }),
            ]),
        ]);
    }
    addSubscriptionFormValueChanges(productId) {
        if (!this.productSubscriptionFormBuilder || !this.productPrices || !productId)
            return;
        this.selectedProductId = productId;
        this.fieldGroups = this.priceForm(this.productPrices[productId].map(productPriceOptions));
    }
    addSubscription(product) {
        if (!this._tenantId || !this.productPrices || !this.selectedProductId)
            throw e11Error(tenantKeyNotPassed);
        const sku = this.productPrices[this.selectedProductId].find((price) => price.id === product.priceId)?.sku;
        if (!sku)
            throw e11Error(tenantSKUNotPassed);
        this.platformAdminCustomerPaymentsStore.addProductToTenant({
            sku: sku,
            tenantId: this._tenantId,
            onComplete: () => {
                this.tenantId = this._tenantId;
                this.addProductMode = false;
            },
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkTenantProductsManagerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.9", type: SdkTenantProductsManagerComponent, selector: "tenant-products-manager", inputs: { tenantId: "tenantId" }, viewQueries: [{ propertyName: "productSubscriptionDetailFormBuilder", first: true, predicate: ["productSubscriptionDetailFormBuilder"], descendants: true }, { propertyName: "productSubscriptionFormBuilder", first: true, predicate: ["productSubscriptionFormBuilder"], descendants: true }], ngImport: i0, template: `
    <div *ngrxLet="recurringProducts$ as recurringProducts" class="e11-flex e11-flex-col e11-gap-6 e11-overflow-auto">
      <div class="e11-flex e11-flex-col md:e11-flex-row e11-justify-between">
        <div class="e11-flex e11-items-center">
          <h3 class="e11-mr-2">{{ addProductMode ? 'Add product to tenant' : ('Available Products List' | translate) }}</h3>
          <e11-tooltip
            [tooltipText]="
              'Products added to this tenant will be available for Tenant Owners to subscribe their clients to with their Client Management access.'
                | translate
            "
          >
            <span class="material-icons-outlined md-18 e11-cursor-default">info</span>
          </e11-tooltip>
        </div>
        <e11-button [color]="'primary'" [size]="'sm'" [value]="'Add Product' | translate" (click)="addProductMode = true"></e11-button>
      </div>
      @if(addProductMode) {
      <div>
        <e11-dynamic-form-builder
          #productSubscriptionFormBuilder
          *ngIf="productFieldGroups"
          [fieldGroups]="productFieldGroups"
          [hideSubmitMenu]="true"
          (valueChanges)="addSubscriptionFormValueChanges($event.productId)"
        ></e11-dynamic-form-builder>
        <e11-dynamic-form-builder
          #productSubscriptionDetailFormBuilder
          *ngIf="fieldGroups"
          [fieldGroups]="fieldGroups"
          [showCancelButton]="true"
          (onCancel)="addProductMode = false"
          (onSubmit)="addSubscription($event)"
        ></e11-dynamic-form-builder>
      </div>
      }@else {
      <ng-container *ngrxLet="tenantProducts$ as tenantProducts">
        <e11-datatable [initialGridOptions]="initialGridOptions" [loading]="(loadingTenantProducts$ | async) || false" [records]="tenantProducts">
          <ng-template e11DatatableTemplate="amount" let-row>
            {{ row.amount / AMOUNT_MULTIPLIER | currency: row.currency }}
          </ng-template>
          <ng-template e11DatatableTemplate="action" let-row>
            <e11-button [value]="'Remove product' | translate" [color]="'complimentary'" [size]="'sm'"></e11-button>
          </ng-template>
        </e11-datatable>
      </ng-container>
      }
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$3.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i2$1.E11DataTableComponent, selector: "e11-datatable", inputs: ["initialGridOptions", "records", "selectedRecords", "isAllSelected", "loading", "striped"], outputs: ["selectedRecordsChange", "isAllSelectedChange", "bulkOperationsChecked"] }, { kind: "directive", type: i2$1.E11DatatableTemplate, selector: "[e11DatatableTemplate]", inputs: ["type", "e11DatatableTemplate"] }, { kind: "component", type: i4$2.E11DynamicFormBuilderComponent, selector: "e11-dynamic-form-builder", inputs: ["initialValue", "hideSubmitMenu", "loading", "disabled", "disabledText", "submitButtonConfig", "cancelButtonConfig", "deleteButtonConfig", "cancelLinkConfig", "showCancelLink", "showCancelButton", "showDeleteButton", "submitButtonTitle", "cancelLinkTitle", "cancelButtonTitle", "deleteButtonTitle", "buttonColorSubmit", "buttonColorCancel", "buttonColorDelete", "fieldGroups", "errorMessageOverrides"], outputs: ["valueChanges", "onSubmit", "onCancel", "onDelete"] }, { kind: "component", type: i4$1.E11TooltipComponent, selector: "e11-tooltip", inputs: ["tooltipText", "tooltipClassOverrides", "hideTooltip", "color", "tooltipContent", "position", "interactive", "maxWidth", "delay"], outputs: ["tooltipClick"] }, { kind: "directive", type: i1$1.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1.CurrencyPipe, name: "currency" }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkTenantProductsManagerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tenant-products-manager',
                    template: `
    <div *ngrxLet="recurringProducts$ as recurringProducts" class="e11-flex e11-flex-col e11-gap-6 e11-overflow-auto">
      <div class="e11-flex e11-flex-col md:e11-flex-row e11-justify-between">
        <div class="e11-flex e11-items-center">
          <h3 class="e11-mr-2">{{ addProductMode ? 'Add product to tenant' : ('Available Products List' | translate) }}</h3>
          <e11-tooltip
            [tooltipText]="
              'Products added to this tenant will be available for Tenant Owners to subscribe their clients to with their Client Management access.'
                | translate
            "
          >
            <span class="material-icons-outlined md-18 e11-cursor-default">info</span>
          </e11-tooltip>
        </div>
        <e11-button [color]="'primary'" [size]="'sm'" [value]="'Add Product' | translate" (click)="addProductMode = true"></e11-button>
      </div>
      @if(addProductMode) {
      <div>
        <e11-dynamic-form-builder
          #productSubscriptionFormBuilder
          *ngIf="productFieldGroups"
          [fieldGroups]="productFieldGroups"
          [hideSubmitMenu]="true"
          (valueChanges)="addSubscriptionFormValueChanges($event.productId)"
        ></e11-dynamic-form-builder>
        <e11-dynamic-form-builder
          #productSubscriptionDetailFormBuilder
          *ngIf="fieldGroups"
          [fieldGroups]="fieldGroups"
          [showCancelButton]="true"
          (onCancel)="addProductMode = false"
          (onSubmit)="addSubscription($event)"
        ></e11-dynamic-form-builder>
      </div>
      }@else {
      <ng-container *ngrxLet="tenantProducts$ as tenantProducts">
        <e11-datatable [initialGridOptions]="initialGridOptions" [loading]="(loadingTenantProducts$ | async) || false" [records]="tenantProducts">
          <ng-template e11DatatableTemplate="amount" let-row>
            {{ row.amount / AMOUNT_MULTIPLIER | currency: row.currency }}
          </ng-template>
          <ng-template e11DatatableTemplate="action" let-row>
            <e11-button [value]="'Remove product' | translate" [color]="'complimentary'" [size]="'sm'"></e11-button>
          </ng-template>
        </e11-datatable>
      </ng-container>
      }
    </div>
  `,
                }]
        }], propDecorators: { productSubscriptionDetailFormBuilder: [{
                type: ViewChild,
                args: ['productSubscriptionDetailFormBuilder']
            }], productSubscriptionFormBuilder: [{
                type: ViewChild,
                args: ['productSubscriptionFormBuilder']
            }], tenantId: [{
                type: Input,
                args: [{ required: true }]
            }] } });
const tenantKeyNotPassed = {
    type: 'payment-tenant-key-not-passed',
    title: 'Tenant key not passed',
};
const tenantSKUNotPassed = {
    type: 'payment-sku-not-passed',
    title: 'SKU not passed',
};

const IMPORT_EXPORT_COMPONENTS$1 = [
    SdkProductAddProductsComponent,
    SdkProductAddPricesSubFormComponent,
    SdkProductAddPermissionsSubFormComponent,
    SdkProductListComponent,
    SdkListRecurringProductsComponent,
    // SdkPaymentSingleActiveSubscriptionComponent,
    SdkPaymentSingleSubscriptionPriceComponent,
    SdkProductCancelSubscriptionButtonComponent,
    SdkProductReNewSubscriptionButtonComponent,
    SdkProductSubscriptionTitleComponent,
    SdkListProductAdminComponent,
    SdkPaymentProductDetailComponent,
    SdkEditProductPriceComponent,
    SdkTenantProductsManagerComponent,
    // CreditCardIconComponent,
];
class ProductModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: ProductModule, declarations: [SdkProductAddProductsComponent,
            SdkProductAddPricesSubFormComponent,
            SdkProductAddPermissionsSubFormComponent,
            SdkProductListComponent,
            SdkListRecurringProductsComponent,
            // SdkPaymentSingleActiveSubscriptionComponent,
            SdkPaymentSingleSubscriptionPriceComponent,
            SdkProductCancelSubscriptionButtonComponent,
            SdkProductReNewSubscriptionButtonComponent,
            SdkProductSubscriptionTitleComponent,
            SdkListProductAdminComponent,
            SdkPaymentProductDetailComponent,
            SdkEditProductPriceComponent,
            SdkTenantProductsManagerComponent], imports: [CommonModule,
            E11ButtonModule,
            E11InputCheckboxModule,
            E11ConfirmationModule,
            E11ModalModule,
            E11DataTableModule,
            E11DynamicFormModule,
            E11TooltipModule,
            SharedModule,
            TranslateModule,
            LetDirective,
            PushPipe,
            E11InputErrorsModule], exports: [SdkProductAddProductsComponent,
            SdkProductAddPricesSubFormComponent,
            SdkProductAddPermissionsSubFormComponent,
            SdkProductListComponent,
            SdkListRecurringProductsComponent,
            // SdkPaymentSingleActiveSubscriptionComponent,
            SdkPaymentSingleSubscriptionPriceComponent,
            SdkProductCancelSubscriptionButtonComponent,
            SdkProductReNewSubscriptionButtonComponent,
            SdkProductSubscriptionTitleComponent,
            SdkListProductAdminComponent,
            SdkPaymentProductDetailComponent,
            SdkEditProductPriceComponent,
            SdkTenantProductsManagerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductModule, imports: [CommonModule,
            E11ButtonModule,
            E11InputCheckboxModule,
            E11ConfirmationModule,
            E11ModalModule,
            E11DataTableModule,
            E11DynamicFormModule,
            E11TooltipModule,
            SharedModule,
            TranslateModule,
            E11InputErrorsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ProductModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        E11ButtonModule,
                        E11InputCheckboxModule,
                        E11ConfirmationModule,
                        E11ModalModule,
                        E11DataTableModule,
                        E11DynamicFormModule,
                        E11TooltipModule,
                        SharedModule,
                        TranslateModule,
                        LetDirective,
                        PushPipe,
                        E11InputErrorsModule,
                    ],
                    declarations: [...IMPORT_EXPORT_COMPONENTS$1],
                    exports: [...IMPORT_EXPORT_COMPONENTS$1],
                }]
        }] });

class SdkSubscribeRecurringProductComponent {
    constructor(paymentStore, productStore) {
        this.paymentStore = paymentStore;
        this.productStore = productStore;
        this.stripePublishableKey = inject(stripePublishableKey);
        this.submitButtonTitle = 'Subscribe';
        this.onComplete = this.paymentStore.confirmPaymentFinalStatus$;
        this.getPaymentIntentLoading$ = this.paymentStore.getPaymentIntentLoading$;
        this.addNew = false;
        this.loadingSubscriptions$ = this.productStore.loadingProducts$;
        this.paymentMethods$ = this.paymentStore.getPaymentMethods$.pipe(tap((paymentMethods) => {
            const defaultPaymentMethod = paymentMethods.find((paymentMethod) => paymentMethod.isDefault);
            if (!defaultPaymentMethod) {
                this.addNew = true;
                return;
            }
            this.addNew = false;
            this.changePaymentMethod(defaultPaymentMethod);
        }));
    }
    ngOnInit() { }
    changePlan(productId, priceId) {
        this.intentInfo = { productId, priceId };
        this.paymentStore.getPaymentCustomer();
    }
    changePaymentMethod(paymentMethod) {
        if (this.intentInfo) {
            this.intentInfo['paymentMethod'] = paymentMethod.id;
            this.addNew = false;
        }
    }
    startSubscription() {
        if (!this.intentInfo) {
            return;
        }
        if (!this.addNew) {
            // Create payment intent and confirm payment with selected payment method
            this.paymentStore.createPaymentIntent({ intentObject: this.intentInfo });
            return;
        }
        // Create payment intent and confirm payment with card element
        const stripeFormAddressValue = this.stripePaymentElement.validateAndGateAll();
        if (!stripeFormAddressValue)
            return;
        this.paymentStore.createPaymentIntent({
            intentObject: this.intentInfo,
            confirmPaymentData: {
                payment_method: {
                    card: this.stripePaymentElement.card.element,
                    billing_details: {
                        name: this.stripePaymentElement.stripeAddressForm.value.name,
                        address: transformStripeAddressToBillingAddress(stripeFormAddressValue),
                    },
                },
            },
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkSubscribeRecurringProductComponent, deps: [{ token: PaymentStore }, { token: ProductStore }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkSubscribeRecurringProductComponent, selector: "sdk-payment-subscribe-recurring-product", inputs: { submitButtonTitle: "submitButtonTitle" }, outputs: { onComplete: "onComplete" }, viewQueries: [{ propertyName: "stripePaymentElement", first: true, predicate: SdkStripePaymentElementComponent, descendants: true }], ngImport: i0, template: `
    <div>
      <sdk-list-recurring-products (intentInfoChange)="changePlan($event.productId, $event.priceId)"></sdk-list-recurring-products>
      <div *ngIf="stripePublishableKey" class="e11-py-4">
        <h3>{{ 'Payment methods' | translate }}</h3>
        <ul *ngrxLet="paymentMethods$ as paymentMethods" class="e11-py-4 e11-flex e11-flex-col e11-gap-2">
          <li
            *ngFor="let paymentMethod of paymentMethods"
            class="e11-flex e11-justify-between  e11-border e11-p-4 e11-rounded-md e11-cursor-pointer"
            [ngClass]="{
                  'e11-ring-2 e11-ring-skin-primary e11-border-transparent': intentInfo?.paymentMethod === paymentMethod.id && !addNew,
                }"
            (click)="changePaymentMethod(paymentMethod)"
          >
            <div class="e11-w-full lg:e11-w-1/3">
              <div class="e11-flex e11-gap-2 e11-items-center">
                <credit-card-icon class="e11-w-8" [cardType]="paymentMethod.card?.brand"></credit-card-icon>
                <div>
                  <span class="e11-capitalize"> {{ paymentMethod.card?.brand }} </span>
                  ending in {{ paymentMethod.card?.last4 }}
                </div>
              </div>
            </div>
            <div class="e11-w-full lg:e11-w-1/3">
              {{ 'Expiring' | translate }} {{ paymentMethod.card?.exp_month }}/{{ paymentMethod.card?.exp_year }}
            </div>
            <div class="e11-w-full lg:e11-w-1/3 e11-self-end">
              <span
                class="material-icons e11-h-5 e11-w-5 e11-text-skin-primary"
                [ngClass]="{ 'e11-hidden': intentInfo?.paymentMethod !== paymentMethod.id || addNew }"
                >check_circle</span
              >
            </div>
          </li>
          <li *ngIf="!addNew" class="e11-flex e11-justify-between  e11-border e11-p-4 e11-rounded-md e11-cursor-pointer" (click)="addNew = true">
            <div class="e11-text-skin-primary-accent ">
              {{ 'Add a new payment method' | translate }}
            </div>
          </li>
        </ul>
        <div *ngIf="addNew">
          <h3 class="e11-mb-4">{{ 'Add a new payment method' | translate }}</h3>
          <sdk-stripe-payment-element #stripePaymentElement></sdk-stripe-payment-element>
        </div>
        <div class="e11-flex e11-justify-end" *ngrxLet="getPaymentIntentLoading$ as getPaymentIntentLoading">
          <ng-container *ngrxLet="loadingSubscriptions$ as loadingSubscriptions">
            <e11-button
              (click)="startSubscription()"
              [disabled]="getPaymentIntentLoading || loadingSubscriptions"
              value="{{ getPaymentIntentLoading ? 'Loading...' : (submitButtonTitle | translate) }}"
              [color]="'primary-accent'"
            >
            </e11-button>
          </ng-container>
        </div>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CreditCardIconComponent, selector: "credit-card-icon", inputs: ["cardType"] }, { kind: "component", type: i2$3.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: SdkListRecurringProductsComponent, selector: "sdk-list-recurring-products", outputs: ["intentInfoChange"] }, { kind: "component", type: SdkStripePaymentElementComponent, selector: "sdk-stripe-payment-element" }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkSubscribeRecurringProductComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-payment-subscribe-recurring-product',
                    template: `
    <div>
      <sdk-list-recurring-products (intentInfoChange)="changePlan($event.productId, $event.priceId)"></sdk-list-recurring-products>
      <div *ngIf="stripePublishableKey" class="e11-py-4">
        <h3>{{ 'Payment methods' | translate }}</h3>
        <ul *ngrxLet="paymentMethods$ as paymentMethods" class="e11-py-4 e11-flex e11-flex-col e11-gap-2">
          <li
            *ngFor="let paymentMethod of paymentMethods"
            class="e11-flex e11-justify-between  e11-border e11-p-4 e11-rounded-md e11-cursor-pointer"
            [ngClass]="{
                  'e11-ring-2 e11-ring-skin-primary e11-border-transparent': intentInfo?.paymentMethod === paymentMethod.id && !addNew,
                }"
            (click)="changePaymentMethod(paymentMethod)"
          >
            <div class="e11-w-full lg:e11-w-1/3">
              <div class="e11-flex e11-gap-2 e11-items-center">
                <credit-card-icon class="e11-w-8" [cardType]="paymentMethod.card?.brand"></credit-card-icon>
                <div>
                  <span class="e11-capitalize"> {{ paymentMethod.card?.brand }} </span>
                  ending in {{ paymentMethod.card?.last4 }}
                </div>
              </div>
            </div>
            <div class="e11-w-full lg:e11-w-1/3">
              {{ 'Expiring' | translate }} {{ paymentMethod.card?.exp_month }}/{{ paymentMethod.card?.exp_year }}
            </div>
            <div class="e11-w-full lg:e11-w-1/3 e11-self-end">
              <span
                class="material-icons e11-h-5 e11-w-5 e11-text-skin-primary"
                [ngClass]="{ 'e11-hidden': intentInfo?.paymentMethod !== paymentMethod.id || addNew }"
                >check_circle</span
              >
            </div>
          </li>
          <li *ngIf="!addNew" class="e11-flex e11-justify-between  e11-border e11-p-4 e11-rounded-md e11-cursor-pointer" (click)="addNew = true">
            <div class="e11-text-skin-primary-accent ">
              {{ 'Add a new payment method' | translate }}
            </div>
          </li>
        </ul>
        <div *ngIf="addNew">
          <h3 class="e11-mb-4">{{ 'Add a new payment method' | translate }}</h3>
          <sdk-stripe-payment-element #stripePaymentElement></sdk-stripe-payment-element>
        </div>
        <div class="e11-flex e11-justify-end" *ngrxLet="getPaymentIntentLoading$ as getPaymentIntentLoading">
          <ng-container *ngrxLet="loadingSubscriptions$ as loadingSubscriptions">
            <e11-button
              (click)="startSubscription()"
              [disabled]="getPaymentIntentLoading || loadingSubscriptions"
              value="{{ getPaymentIntentLoading ? 'Loading...' : (submitButtonTitle | translate) }}"
              [color]="'primary-accent'"
            >
            </e11-button>
          </ng-container>
        </div>
      </div>
    </div>
  `,
                }]
        }], ctorParameters: () => [{ type: PaymentStore }, { type: ProductStore }], propDecorators: { stripePaymentElement: [{
                type: ViewChild,
                args: [SdkStripePaymentElementComponent]
            }], submitButtonTitle: [{
                type: Input
            }], onComplete: [{
                type: Output
            }] } });

class PaymentMethodsSelectorComponent {
    constructor() {
        this.paymentStore = inject(PaymentStore);
        this.onPaymentMethodChange = new EventEmitter();
        this.paymentMethods$ = this.paymentStore.getPaymentMethods$.pipe(tap$1((paymentMethods) => {
            const defaultPaymentMethod = paymentMethods.find((paymentMethod) => paymentMethod.isDefault);
            if (!defaultPaymentMethod)
                return;
            this.selectedPaymentMethod = defaultPaymentMethod;
            this.onPaymentMethodChange.emit(defaultPaymentMethod);
        }));
    }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PaymentMethodsSelectorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: PaymentMethodsSelectorComponent, selector: "payment-methods-selector", outputs: { onPaymentMethodChange: "onPaymentMethodChange" }, ngImport: i0, template: `
    <ul *ngrxLet="paymentMethods$ as paymentMethods" class="e11-py-4 e11-flex e11-flex-col e11-gap-2">
      <li
        *ngFor="let paymentMethod of paymentMethods"
        class="e11-flex e11-justify-between  e11-border e11-p-4 e11-rounded-md e11-cursor-pointer"
        [ngClass]="{
                  'e11-ring-2 e11-ring-skin-primary e11-border-transparent': selectedPaymentMethod?.id === paymentMethod.id ,
                }"
        (click)="onPaymentMethodChange.emit(paymentMethod); selectedPaymentMethod = paymentMethod"
      >
        <div class="e11-w-full lg:e11-w-1/3">
          <div class="e11-flex e11-flex-col lg:e11-flex-row e11-gap-2 e11-items-center">
            <credit-card-icon class="e11-w-8" [cardType]="paymentMethod.card?.brand"></credit-card-icon>
            <div>
              <span class="e11-capitalize"> {{ paymentMethod.card?.brand }} </span>
              ending in {{ paymentMethod.card?.last4 }}
            </div>
          </div>
        </div>
        <div class="e11-w-full lg:e11-w-1/3">{{ 'Expiring' | translate }} {{ paymentMethod.card?.exp_month }}/{{ paymentMethod.card?.exp_year }}</div>
        <div class="e11-w-full lg:e11-w-1/3 e11-self-end">
          <span
            class="material-icons e11-h-5 e11-w-5 e11-text-skin-primary"
            [ngClass]="{ 'e11-hidden': selectedPaymentMethod?.id !== paymentMethod.id }"
            >check_circle</span
          >
        </div>
      </li>
    </ul>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: CreditCardIconComponent, selector: "credit-card-icon", inputs: ["cardType"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PaymentMethodsSelectorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'payment-methods-selector',
                    template: `
    <ul *ngrxLet="paymentMethods$ as paymentMethods" class="e11-py-4 e11-flex e11-flex-col e11-gap-2">
      <li
        *ngFor="let paymentMethod of paymentMethods"
        class="e11-flex e11-justify-between  e11-border e11-p-4 e11-rounded-md e11-cursor-pointer"
        [ngClass]="{
                  'e11-ring-2 e11-ring-skin-primary e11-border-transparent': selectedPaymentMethod?.id === paymentMethod.id ,
                }"
        (click)="onPaymentMethodChange.emit(paymentMethod); selectedPaymentMethod = paymentMethod"
      >
        <div class="e11-w-full lg:e11-w-1/3">
          <div class="e11-flex e11-flex-col lg:e11-flex-row e11-gap-2 e11-items-center">
            <credit-card-icon class="e11-w-8" [cardType]="paymentMethod.card?.brand"></credit-card-icon>
            <div>
              <span class="e11-capitalize"> {{ paymentMethod.card?.brand }} </span>
              ending in {{ paymentMethod.card?.last4 }}
            </div>
          </div>
        </div>
        <div class="e11-w-full lg:e11-w-1/3">{{ 'Expiring' | translate }} {{ paymentMethod.card?.exp_month }}/{{ paymentMethod.card?.exp_year }}</div>
        <div class="e11-w-full lg:e11-w-1/3 e11-self-end">
          <span
            class="material-icons e11-h-5 e11-w-5 e11-text-skin-primary"
            [ngClass]="{ 'e11-hidden': selectedPaymentMethod?.id !== paymentMethod.id }"
            >check_circle</span
          >
        </div>
      </li>
    </ul>
  `,
                }]
        }], ctorParameters: () => [], propDecorators: { onPaymentMethodChange: [{
                type: Output
            }] } });

class SdkPaymentSingleActiveSubscriptionComponent {
    constructor() {
        this.productStore = inject(ProductStore);
        this.active = false;
        this.cancelSubscription = new EventEmitter();
        this.reEnableSubscription = new EventEmitter();
    }
    set activeSubscription(value) {
        this._activeSubscription = value;
    }
    updatePaymentMethod() {
        this.productStore.updatePaymentMethodOfSubscription({
            subscriptionId: this._activeSubscription.id,
            defaultPaymentMethod: this.selectedPaymentMethod.id,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkPaymentSingleActiveSubscriptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkPaymentSingleActiveSubscriptionComponent, selector: "sdk-payment-single-active-subscription", inputs: { active: "active", activeSubscription: "activeSubscription" }, outputs: { cancelSubscription: "cancelSubscription", reEnableSubscription: "reEnableSubscription" }, host: { classAttribute: "e11-relative e11-group" }, ngImport: i0, template: `
    <label
      class="e11-relative e11-flex e11-cursor-pointer e11-rounded-lg e11-border e11-bg-skin-white e11-p-4 e11-shadow-sm focus:e11-outline-none "
      [ngClass]="{
                  'e11-ring-2 e11-ring-skin-primary e11-border-transparent': active,
                  'e11-border-skin-grey': !active,
                }"
    >
      <span class="e11-flex e11-flex-1">
        <span class="e11-flex e11-flex-col e11-gap-4">
          <sdk-product-subscription-title
            [planTitle]="_activeSubscription.productName"
            [subscriptionInfo]="_activeSubscription.stripeInfo"
          ></sdk-product-subscription-title>
          <sdk-product-cancel-subscription-button
            *ngIf="_activeSubscription.autoRenew"
            [subscriptionId]="_activeSubscription.id"
            (cancelSubscription)="this.cancelSubscription.emit($event)"
          ></sdk-product-cancel-subscription-button>
          <sdk-product-re-enable-subscription-button
            *ngIf="!_activeSubscription.autoRenew"
            [subscriptionId]="_activeSubscription.id"
            (reEnableSubscription)="reEnableSubscription.emit($event)"
          ></sdk-product-re-enable-subscription-button>
        </span>
      </span>
      <sdk-product-single-subscription-price
        *ngIf="_activeSubscription.stripeInfo && _activeSubscription.stripeInfo.plan"
        [planInfo]="_activeSubscription.stripeInfo.plan"
      ></sdk-product-single-subscription-price>
    </label>
    <div class="e11-hidden group-hover:e11-block e11-absolute e11-right-0 e11-top-0 e11-p-4 e11-cursor-pointer" (click)="modalUpdatePrice.open()">
      <span class="material-icons-outlined  e11-text-skin-dark/50 hover:e11-text-skin-dark">edit </span>
    </div>
    <e11-modal
      #modalUpdatePrice
      modalWidth="sm"
      [bodyHeight]="'auto'"
      title="{{ 'Update payment method' | translate }}"
      [showHeader]="true"
      [showFooter]="false"
    >
      <ng-template e11Template="Body" class="e11-mb-8">
        <payment-methods-selector (onPaymentMethodChange)="selectedPaymentMethod = $event"></payment-methods-selector>
        <div class="e11-flex e11-justify-end">
          <e11-button [value]="'Update'" [color]="'primary-accent'" (click)="updatePaymentMethod(); modalUpdatePrice.close()"> </e11-button>
        </div>
      </ng-template>
    </e11-modal>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.E11Template, selector: "[e11Template]", inputs: ["type", "e11Template"] }, { kind: "component", type: i3$2.E11ModalComponent, selector: "e11-modal", inputs: ["title", "width", "bodyHeight", "titleAlign", "hideCloseButton", "closer", "bodyClassOverrides", "bodyClass", "modalClassOverrides", "modalClass", "contentClassOverrides", "contentClass", "headerClassOverrides", "headerClass", "footerClassOverrides", "footerClass", "showFooter", "showHeader", "darkMode", "closeOnEscape", "closeOnClickOutside", "headerCTAs", "hasBackdrop", "backdropStyle"], outputs: ["modalOpened", "onModalOpened", "modalClosed", "onModalClosed", "headerCTAClicked", "onHeaderCTAClicked"] }, { kind: "component", type: i2$3.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: SdkPaymentSingleSubscriptionPriceComponent, selector: "sdk-product-single-subscription-price", inputs: ["planInfo"] }, { kind: "component", type: SdkProductCancelSubscriptionButtonComponent, selector: "sdk-product-cancel-subscription-button", inputs: ["subscriptionId"], outputs: ["cancelSubscription"] }, { kind: "component", type: SdkProductReNewSubscriptionButtonComponent, selector: "sdk-product-re-enable-subscription-button", inputs: ["subscriptionId"], outputs: ["reEnableSubscription"] }, { kind: "component", type: SdkProductSubscriptionTitleComponent, selector: "sdk-product-subscription-title", inputs: ["subscriptionInfo", "planTitle"] }, { kind: "component", type: PaymentMethodsSelectorComponent, selector: "payment-methods-selector", outputs: ["onPaymentMethodChange"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkPaymentSingleActiveSubscriptionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-payment-single-active-subscription',
                    template: `
    <label
      class="e11-relative e11-flex e11-cursor-pointer e11-rounded-lg e11-border e11-bg-skin-white e11-p-4 e11-shadow-sm focus:e11-outline-none "
      [ngClass]="{
                  'e11-ring-2 e11-ring-skin-primary e11-border-transparent': active,
                  'e11-border-skin-grey': !active,
                }"
    >
      <span class="e11-flex e11-flex-1">
        <span class="e11-flex e11-flex-col e11-gap-4">
          <sdk-product-subscription-title
            [planTitle]="_activeSubscription.productName"
            [subscriptionInfo]="_activeSubscription.stripeInfo"
          ></sdk-product-subscription-title>
          <sdk-product-cancel-subscription-button
            *ngIf="_activeSubscription.autoRenew"
            [subscriptionId]="_activeSubscription.id"
            (cancelSubscription)="this.cancelSubscription.emit($event)"
          ></sdk-product-cancel-subscription-button>
          <sdk-product-re-enable-subscription-button
            *ngIf="!_activeSubscription.autoRenew"
            [subscriptionId]="_activeSubscription.id"
            (reEnableSubscription)="reEnableSubscription.emit($event)"
          ></sdk-product-re-enable-subscription-button>
        </span>
      </span>
      <sdk-product-single-subscription-price
        *ngIf="_activeSubscription.stripeInfo && _activeSubscription.stripeInfo.plan"
        [planInfo]="_activeSubscription.stripeInfo.plan"
      ></sdk-product-single-subscription-price>
    </label>
    <div class="e11-hidden group-hover:e11-block e11-absolute e11-right-0 e11-top-0 e11-p-4 e11-cursor-pointer" (click)="modalUpdatePrice.open()">
      <span class="material-icons-outlined  e11-text-skin-dark/50 hover:e11-text-skin-dark">edit </span>
    </div>
    <e11-modal
      #modalUpdatePrice
      modalWidth="sm"
      [bodyHeight]="'auto'"
      title="{{ 'Update payment method' | translate }}"
      [showHeader]="true"
      [showFooter]="false"
    >
      <ng-template e11Template="Body" class="e11-mb-8">
        <payment-methods-selector (onPaymentMethodChange)="selectedPaymentMethod = $event"></payment-methods-selector>
        <div class="e11-flex e11-justify-end">
          <e11-button [value]="'Update'" [color]="'primary-accent'" (click)="updatePaymentMethod(); modalUpdatePrice.close()"> </e11-button>
        </div>
      </ng-template>
    </e11-modal>
  `,
                    host: {
                        class: 'e11-relative e11-group',
                    },
                }]
        }], propDecorators: { active: [{
                type: Input
            }], activeSubscription: [{
                type: Input
            }], cancelSubscription: [{
                type: Output
            }], reEnableSubscription: [{
                type: Output
            }] } });

class SdkManageSubscriptionComponent {
    constructor(productStore) {
        this.productStore = productStore;
        this.customerActiveSubscriptions$ = this.productStore.activeCustomerSubscriptions$;
    }
    ngOnInit() {
        this.productStore.onListenActiveSubscription();
    }
    cancelSubscription(subscriptionId) {
        this.productStore.cancelSubscription(subscriptionId);
    }
    reEnableSubscription(subscriptionId) {
        this.productStore.reEnableSubscription(subscriptionId);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkManageSubscriptionComponent, deps: [{ token: ProductStore }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SdkManageSubscriptionComponent, selector: "sdk-payment-manage-subscription", ngImport: i0, template: `
    <div *ngrxLet="customerActiveSubscriptions$ as customerActiveSubscriptions">
      <fieldset>
        <h3>{{ 'Plan' | translate }}</h3>
        <div class="e11-mt-4 e11-grid e11-grid-cols-1 e11-gap-y-6 sm:e11-grid-cols-2 sm:e11-gap-x-4">
          <ng-container *ngFor="let customerActiveSubscription of customerActiveSubscriptions; let i = index">
            <sdk-payment-single-active-subscription
              [active]="customerActiveSubscription.autoRenew"
              [activeSubscription]="customerActiveSubscription"
              (cancelSubscription)="cancelSubscription(customerActiveSubscription.id)"
              (reEnableSubscription)="reEnableSubscription(customerActiveSubscription.id)"
            >
            </sdk-payment-single-active-subscription>
          </ng-container>
        </div>
      </fieldset>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: SdkPaymentSingleActiveSubscriptionComponent, selector: "sdk-payment-single-active-subscription", inputs: ["active", "activeSubscription"], outputs: ["cancelSubscription", "reEnableSubscription"] }, { kind: "pipe", type: i5.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SdkManageSubscriptionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-payment-manage-subscription',
                    template: `
    <div *ngrxLet="customerActiveSubscriptions$ as customerActiveSubscriptions">
      <fieldset>
        <h3>{{ 'Plan' | translate }}</h3>
        <div class="e11-mt-4 e11-grid e11-grid-cols-1 e11-gap-y-6 sm:e11-grid-cols-2 sm:e11-gap-x-4">
          <ng-container *ngFor="let customerActiveSubscription of customerActiveSubscriptions; let i = index">
            <sdk-payment-single-active-subscription
              [active]="customerActiveSubscription.autoRenew"
              [activeSubscription]="customerActiveSubscription"
              (cancelSubscription)="cancelSubscription(customerActiveSubscription.id)"
              (reEnableSubscription)="reEnableSubscription(customerActiveSubscription.id)"
            >
            </sdk-payment-single-active-subscription>
          </ng-container>
        </div>
      </fieldset>
    </div>
  `,
                }]
        }], ctorParameters: () => [{ type: ProductStore }] });

const IMPORT_EXPORT_COMPONENTS = [
    SdkBillingHistoryComponent,
    SdkPaymentCardComponent,
    SdkPaymentMethodsComponent,
    SdkBillingComponent,
    SdkNewPaymentCardComponent,
    SdkStripePaymentElementComponent,
    SdkSubscribeRecurringProductComponent,
    PaymentMethodsSelectorComponent,
    SdkManageSubscriptionComponent,
    SdkPaymentSingleActiveSubscriptionComponent,
    PlatformAdminCustomerInvoiceComponent,
    PlatformAdminBillingHistoryComponent,
    PlatformAdminMarkInvoicePaidComponent,
    PlatformAdminCustomerProductsComponent,
    PlatformAdminCustomerProductListHeaderComponent,
    PlatformAdminBillingHistoryHeaderComponent,
    SdkSubscriptionListComponent,
    CustomerClientSubscriptionsComponent,
    SdkSubscribeToProductComponent,
];
class PaymentModule {
    static forRoot(config) {
        const providers = [
            {
                provide: stripePublishableKey,
                useValue: config.stripePublishableKey,
            },
        ];
        if (config.stripePublishableKey) {
            providers.push({
                provide: StripeService,
                useFactory: () => {
                    return injectStripe(config.stripePublishableKey);
                },
            });
        }
        return {
            ngModule: PaymentModule,
            providers: [
                {
                    provide: BasePaymentConfigurationProvider,
                    useClass: config.configProvider,
                },
                ...providers,
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PaymentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: PaymentModule, declarations: [SdkBillingHistoryComponent,
            SdkPaymentCardComponent,
            SdkPaymentMethodsComponent,
            SdkBillingComponent,
            SdkNewPaymentCardComponent,
            SdkStripePaymentElementComponent,
            SdkSubscribeRecurringProductComponent,
            PaymentMethodsSelectorComponent,
            SdkManageSubscriptionComponent,
            SdkPaymentSingleActiveSubscriptionComponent,
            PlatformAdminCustomerInvoiceComponent,
            PlatformAdminBillingHistoryComponent,
            PlatformAdminMarkInvoicePaidComponent,
            PlatformAdminCustomerProductsComponent,
            PlatformAdminCustomerProductListHeaderComponent,
            PlatformAdminBillingHistoryHeaderComponent,
            SdkSubscriptionListComponent,
            CustomerClientSubscriptionsComponent,
            SdkSubscribeToProductComponent], imports: [LetDirective,
            PushPipe,
            WebUtilityModule,
            CommonModule,
            E11DataTableModule,
            E11ConfirmationModule,
            SharedModule,
            E11ModalModule,
            E11DynamicFormModule,
            E11ButtonModule,
            TranslateModule,
            ProductModule,
            E11OverflowModule,
            E11InputErrorsModule,
            E11EmptyStateModule,
            E11DividerModule,
            E11BadgeModule,
            E11TooltipModule, i4.NgxStripeModule], exports: [SdkBillingHistoryComponent,
            SdkPaymentCardComponent,
            SdkPaymentMethodsComponent,
            SdkBillingComponent,
            SdkNewPaymentCardComponent,
            SdkStripePaymentElementComponent,
            SdkSubscribeRecurringProductComponent,
            PaymentMethodsSelectorComponent,
            SdkManageSubscriptionComponent,
            SdkPaymentSingleActiveSubscriptionComponent,
            PlatformAdminCustomerInvoiceComponent,
            PlatformAdminBillingHistoryComponent,
            PlatformAdminMarkInvoicePaidComponent,
            PlatformAdminCustomerProductsComponent,
            PlatformAdminCustomerProductListHeaderComponent,
            PlatformAdminBillingHistoryHeaderComponent,
            SdkSubscriptionListComponent,
            CustomerClientSubscriptionsComponent,
            SdkSubscribeToProductComponent, ProductModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PaymentModule, imports: [WebUtilityModule,
            CommonModule,
            E11DataTableModule,
            E11ConfirmationModule,
            SharedModule,
            E11ModalModule,
            E11DynamicFormModule,
            E11ButtonModule,
            TranslateModule,
            ProductModule,
            E11OverflowModule,
            E11InputErrorsModule,
            E11EmptyStateModule,
            E11DividerModule,
            E11BadgeModule,
            E11TooltipModule,
            NgxStripeModule.forRoot(), ProductModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PaymentModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...IMPORT_EXPORT_COMPONENTS],
                    imports: [
                        LetDirective,
                        PushPipe,
                        WebUtilityModule,
                        CommonModule,
                        E11DataTableModule,
                        E11ConfirmationModule,
                        SharedModule,
                        E11ModalModule,
                        E11DynamicFormModule,
                        E11ButtonModule,
                        TranslateModule,
                        ProductModule,
                        E11OverflowModule,
                        E11InputErrorsModule,
                        E11EmptyStateModule,
                        E11DividerModule,
                        E11BadgeModule,
                        E11TooltipModule,
                        NgxStripeModule.forRoot(),
                    ],
                    exports: [...IMPORT_EXPORT_COMPONENTS, ProductModule],
                }]
        }] });

var ProductPermissions;
(function (ProductPermissions) {
    ProductPermissions["ViewProducts"] = "product/view-products";
    ProductPermissions["ViewEnabledProducts"] = "product/view-enabled-products";
    ProductPermissions["EditProducts"] = "product/edit-product";
    ProductPermissions["EditProductPrice"] = "product/edit-product-price";
    ProductPermissions["ViewBillingDetails"] = "product/view-billing-details";
    ProductPermissions["EditBillingDetails"] = "product/edit-billing-details";
    ProductPermissions["PlatformAdminViewBillingDetails"] = "product/platform-admin-view-billing-details";
    ProductPermissions["PlatformAdminEditBillingDetails"] = "product/platform-admin-edit-billing-details";
    ProductPermissions["PlatformAdminViewSubscriptions"] = "product/platform-admin-view-subscriptions";
    ProductPermissions["PlatformAdminEditSubscriptions"] = "product/platform-admin-edit-subscriptions";
    ProductPermissions["PlatformAdminViewTenantProducts"] = "product/platform-admin-view-tenant-products";
    ProductPermissions["PlatformAdminEditTenantProducts"] = "product/platform-admin-edit-tenant-products";
})(ProductPermissions || (ProductPermissions = {}));

/*
 * Public API Surface of payment
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AMOUNT_MULTIPLIER, AmexSVGComponent, BasePaymentConfigurationProvider, COLLECTIONS, COMMON_CURRENCY_CODES, CreditCardIconComponent, CustomerClientSubscriptionsComponent, DEFAULT_CURRENCY_CODE, DEFAULT_DIVIDE_BY, DiscoverSVGComponent, EXPORT_CARD_SVG_COMPONENTS, JcbSVGComponent, MasterCardSVGComponent, PAYMENT_ERRORS, PaymentIntegration, PaymentMethodsSelectorComponent, PaymentModule, PaymentPermissions, PaymentService, PlatformAdminBillingHistoryComponent, PlatformAdminBillingHistoryHeaderComponent, PlatformAdminCustomerInvoiceComponent, PlatformAdminCustomerProductListHeaderComponent, PlatformAdminCustomerProductsComponent, PlatformAdminMarkInvoicePaidComponent, ProductModule, ProductPermissions, ProductRepository, ProductService, QuantityTransformRound, RecurringPriceInterval, SKU_REGEX, SdkBillingComponent, SdkBillingHistoryComponent, SdkEditProductPriceComponent, SdkListProductAdminComponent, SdkListRecurringProductsComponent, SdkManageSubscriptionComponent, SdkNewPaymentCardComponent, SdkPaymentCardComponent, SdkPaymentMethodsComponent, SdkPaymentProductDetailComponent, SdkPaymentSingleActiveSubscriptionComponent, SdkPaymentSingleSubscriptionPriceComponent, SdkProductAddPermissionsSubFormComponent, SdkProductAddPricesSubFormComponent, SdkProductAddProductsComponent, SdkProductCancelSubscriptionButtonComponent, SdkProductListComponent, SdkProductReNewSubscriptionButtonComponent, SdkProductSubscriptionTitleComponent, SdkStripePaymentElementComponent, SdkSubscribeRecurringProductComponent, SdkSubscribeToProductComponent, SdkSubscriptionListComponent, SdkTenantProductsManagerComponent, UnionPaySVGComponent, VisaSVGComponent, customerSubscriptionsTableOptions, generateDynamicFormObject, generateDynamicFormObjectForProducts, platformAdminBillingTableOptions, productPriceOptions, selectablePaymentIntegration, selectableRecurringPriceIntervals, stripePublishableKey, transformPaymentIntentResponseToBillingHistory, transformStripeAddressToBillingAddress };
